/* PLAYTS Set Configurator [cb:84]
 * --------------------------------------------------------------------------
 * Renders all PLAYTS configurator instances on the page. Each instance:
 *   - Maintains a set state (array of {shape, color})
 *   - Renders the assembled set in a Three.js scene (lazy-loaded)
 *   - Lets users add pieces via a variant-grid modal (all 12 cards, image + price)
 *   - Lets users click pieces in the 3D scene OR the slot pills below to edit
 *   - Sold-out pieces are addable; cart button is disabled while any picked
 *     variant is unavailable, banner explains and slot pills highlight
 *   - Persists state in URL (?set=oval-pink,round-creme,...) for sharing
 *
 * Real Three.js PBR tuning is iterated in a separate scratch page; the
 * defaults here are conservative and known to ship.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
// Post-processing (photorealism pipeline). All optional — wrapped in try/catch
// so a load/init failure falls back to direct rendering.
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';

const SET_DISCOUNT_PCT = 10;

// ── Constants ─────────────────────────────────────────────────────────────────
const COLORS = ['creme', 'light-pink', 'blue', 'red', 'light-green'];
const SHAPES = ['round', 'oval', 'square'];

// Swatch colours for the UI chips. The 3D material uses richer/more saturated
// variants (CERAMIC_HEX) because clearcoat + ACES wash pastels out on screen.
const COLOR_HEX = {
  'creme':       '#ebe4d8',
  'light-pink':  '#f4d3c9',
  'blue':        '#6789c1',
  'red':         '#ee6e3e',
  'light-green': '#d8de9a',
};
const CERAMIC_HEX = {
  'creme':       '#ece1c8',
  'light-pink':  '#f6c0ac',
  'blue':        '#3f6ee0',
  'red':         '#f04d18',
  'light-green': '#c8da6a',
};

// Selectable floors. white = current flat matte; stone + linoleum are procedural
// (canvas-baked, tintable); oak is a lazy-loaded CC0 wood texture set. Per-floor
// texture scale lives in SCENE_PARAMS.floor*Scale (live-tunable in the debug GUI).
const FLOORS = {
  // "Neutral": the grey linoleum, fixed to the grey tint (id stays 'white' so
  // existing share links keep working).
  white:    { kind: 'proc', proc: 'lino', lino: 'grey' },
  stone:    { kind: 'tex', assets: 'floorStone', contrastParam: 'floorStoneContrast',
              grade: { bright: 'floorStoneBright', warm: 'floorStoneWarmth', tint: 'floorStoneTint' },
              gloss: { amt: 'floorStoneGloss', rough: 'floorStoneGlossRough' },
              mat: { envMapIntensity: 0.5, normalScale: 0.8 } },
  linoleum: { kind: 'proc', proc: 'lino' },
  oak:      { kind: 'tex', assets: 'floorOak', contrastParam: 'floorOakContrast',
              grade: { bright: 'floorOakBright', warm: 'floorOakWarmth', tint: 'floorOakTint' },
              gloss: { amt: 'floorOakGloss', rough: 'floorOakGlossRough' },
              mat: { envMapIntensity: 0.6, normalScale: 0.7 } },
  // Real metal: metalness 1 (+ its metalness map), no clearcoat, strong env —
  // a steel surface is essentially all reflection.
  // envMapIntensity 3 + roughness 0.4 tuned live against the scene's dark room:
  // at metalness 1 the floor shows ONLY the environment, so it needs a strong,
  // sharp env or it reads as flat dark grey instead of brushed steel.
  steel:    { kind: 'tex', assets: 'floorSteel', contrastParam: 'floorSteelContrast',
              grade: { bright: 'floorSteelBright', warm: 'floorSteelWarmth', tint: 'floorSteelTint' },
              gloss: { amt: 'floorSteelGloss', rough: 'floorSteelGlossRough' },
              mat: { envParam: 'floorSteelEnv', roughParam: 'floorSteelRough',
                     envMapIntensity: 3.0, normalScale: 0.45, metalness: 1.0, roughness: 0.4 } },
  // Fabric: dielectric + sheen for the soft grazing-angle falloff cloth has.
  cloth:    { kind: 'tex', assets: 'floorCloth', contrastParam: 'floorClothContrast',
              grade: { bright: 'floorClothBright', warm: 'floorClothWarmth', tint: 'floorClothTint' },
              gloss: { amt: 'floorClothGloss', rough: 'floorClothGlossRough' },
              mat: { envParam: 'floorClothEnv', weaveParam: 'floorClothWeave', sheenParam: 'floorClothSheen',
                     envMapIntensity: 0.35, normalScale: 1.5,
                     sheen: 0.6, sheenRoughness: 0.9, sheenColor: '#ffffff' } },
  marblewhite: { kind: 'tex', assets: 'floorMarblewhite', contrastParam: 'floorMarblewhiteContrast',
              grade: { bright: 'floorMarblewhiteBright', warm: 'floorMarblewhiteWarmth', tint: 'floorMarblewhiteTint' },
              gloss: { amt: 'floorMarblewhiteGloss', rough: 'floorMarblewhiteGlossRough' },
              mat: { envMapIntensity: 0.7, normalScale: 0.5, matteBase: true } },
  marblebeige: { kind: 'tex', assets: 'floorMarblebeige', contrastParam: 'floorMarblebeigeContrast',
              grade: { bright: 'floorMarblebeigeBright', warm: 'floorMarblebeigeWarmth', tint: 'floorMarblebeigeTint' },
              gloss: { amt: 'floorMarblebeigeGloss', rough: 'floorMarblebeigeGlossRough' },
              mat: { envMapIntensity: 0.7, normalScale: 0.5, matteBase: true } },
  marblegrey: { kind: 'tex', assets: 'floorMarblegrey', contrastParam: 'floorMarblegreyContrast',
              grade: { bright: 'floorMarblegreyBright', warm: 'floorMarblegreyWarmth', tint: 'floorMarblegreyTint' },
              gloss: { amt: 'floorMarblegreyGloss', rough: 'floorMarblegreyGlossRough' },
              mat: { envMapIntensity: 0.7, normalScale: 0.5, matteBase: true } },
  beech: { kind: 'tex', assets: 'floorBeech', contrastParam: 'floorBeechContrast',
              grade: { bright: 'floorBeechBright', warm: 'floorBeechWarmth', tint: 'floorBeechTint' },
              gloss: { amt: 'floorBeechGloss', rough: 'floorBeechGlossRough' },
              mat: { envMapIntensity: 0.6, normalScale: 0.7, matteBase: true } },
  terrazzowarm: { kind: 'tex', assets: 'floorTerrazzowarm', contrastParam: 'floorTerrazzowarmContrast',
              grade: { bright: 'floorTerrazzowarmBright', warm: 'floorTerrazzowarmWarmth', tint: 'floorTerrazzowarmTint' },
              gloss: { amt: 'floorTerrazzowarmGloss', rough: 'floorTerrazzowarmGlossRough' },
              mat: { envMapIntensity: 0.5, normalScale: 0.7, matteBase: true } },
  terrazzocool: { kind: 'tex', assets: 'floorTerrazzocool', contrastParam: 'floorTerrazzocoolContrast',
              grade: { bright: 'floorTerrazzocoolBright', warm: 'floorTerrazzocoolWarmth', tint: 'floorTerrazzocoolTint' },
              gloss: { amt: 'floorTerrazzocoolGloss', rough: 'floorTerrazzocoolGlossRough' },
              mat: { envMapIntensity: 0.5, normalScale: 0.7, matteBase: true } },
};
const FLOOR_ORDER = ['white', 'stone', 'linoleum', 'oak', 'beech', 'marblewhite', 'marblebeige', 'marblegrey', 'cloth'];

// Per-shape positions for the five canonical layouts. Each layout caps at a
// specific shape mix; smaller user sets pick the first N slots of each shape.
// Units: metres, Three.js Y-up. Extracted from Shapr3D STEP files (mm → m,
// Z-up → Y-up axis swap) and layer-normalised so the lowest piece sits on y=0.
// Centre of each piece = mean of its RIM circles (largest radius). The rim is
// concentric (round) / symmetric (oval ends, square corners) → exact geometric
// centre. bbox-of-ALL-circles was biased up to ~34 mm by the asymmetric fillet
// arcs — that was the "rounds positioned wrong" bug. Composition-centred on
// origin, Y layer-normalised; ovals carry ry from their STEP long-axis.
const LAYOUTS = {
  // 1 square + 2 ovals + 0-3 rounds. First five slots = the original hand-
  // extracted Shapr3D composition; the appended round keeps existing sets
  // pixel-identical while allowing a 6th piece.
  S1O2R3: [
    { shape: 'square', x:  -0.0297, y: 0.000, z:  -0.0640, ry:  0 },
    { shape: 'oval',   x:  -0.0793, y: 0.012, z:  -0.0638, ry:  0 }, // 1st oval on top of the square
    { shape: 'oval',   x:   0.0575, y: 0.000, z:   0.1415, ry: 90 }, // 2nd oval on the floor (centred under the front round so it sits on the oval's right @50%, per Roman)
    { shape: 'round',  x:   0.0575, y: 0.012, z:   0.1421, ry:  0 }, // 1st round: front, rests on the 2nd oval (settles to ground w/o it)
    { shape: 'round',  x:   0.0193, y: 0.012, z:  -0.1128, ry:  0 }, // 2nd round: behind, on the square
    { shape: 'round',  x:  -0.1600, y: 0.000, z:   0.0900, ry:  0 }, // 3rd round: floor, front-left
  ],
  // 1 square + 1 oval + 0-4 rounds. Base = S1O2R3 without the floor oval, so
  // sets that grow from 1 oval to 2 keep their existing pieces in place.
  S1O1R4: [
    { shape: 'square', x:  -0.0297, y: 0.000, z:  -0.0640, ry:  0 },
    { shape: 'oval',   x:  -0.0793, y: 0.012, z:  -0.0638, ry:  0 }, // on the square
    { shape: 'round',  x:   0.0575, y: 0.012, z:   0.1421, ry:  0 }, // #1 settles to the floor (front)
    { shape: 'round',  x:   0.0193, y: 0.012, z:  -0.1128, ry:  0 }, // #2 on the square
    { shape: 'round',  x:  -0.1690, y: 0.000, z:   0.0980, ry:  0 }, // #3 floor, front-left (nudged left per Roman's scribble; swapped with #4)
    { shape: 'round',  x:   0.1696, y: 0.000, z:   0.0134, ry:  0 }, // #4 floor, right-back — last round added (swapped with #3)
  ],
  // 1 square + 0 ovals + 0-4 rounds. Identical square + round slots to S1O1R4
  // (same order) so removing the oval from a 1S+1O+nR set leaves every round
  // exactly where it was (Roman's spec).
  S1O0R4: [
    { shape: 'square', x:  -0.0297, y: 0.000, z:  -0.0640, ry:  0 },
    { shape: 'round',  x:   0.0575, y: 0.000, z:   0.1421, ry:  0 }, // #1 front
    { shape: 'round',  x:  -0.0790, y: 0.012, z:  -0.1080, ry:  0 }, // #2 on the square, LEFT corner (nudged forward so it no longer overhangs; oval-free layout)
    { shape: 'round',  x:  -0.1690, y: 0.000, z:   0.0980, ry:  0 }, // #3 floor, front-left (nudged left per Roman's scribble; swapped with #4, mirrors S1O1R4)
    { shape: 'round',  x:   0.1696, y: 0.000, z:   0.0134, ry:  0 }, // #4 floor, right-back (swapped with #3, mirrors S1O1R4)
  ],
  // 0 square + 1 oval + 0-4 rounds. Round slots (same order) are IDENTICAL to
  // S0O2R4's, so adding/removing the 2nd oval never moves a round (Roman's
  // spec): the round that sat on oval 2 keeps its x/z and just settles to the
  // ground here; the stacked pair is unchanged.
  S0O1R4: [
    { shape: 'oval',   x:  -0.0506, y: 0.000, z:  -0.0669, ry: 90 },
    { shape: 'round',  x:  -0.0995, y: 0.012, z:  -0.0673, ry:  0 }, // on the oval
    { shape: 'round',  x:   0.0998, y: 0.012, z:   0.0670, ry:  0 }, // was on oval 2 → settles to ground when unsupported
    { shape: 'round',  x:  -0.1375, y: 0.000, z:   0.1360, ry:  0 }, // floor, front (stacked pair)
    { shape: 'round',  x:  -0.1375, y: 0.012, z:   0.1360, ry:  0 }, // stacked on the 3rd round
  ],
  // 0 square + 2 ovals + 0-4 rounds — 3rd round floor front-left, 4th
  // stacked on it (Roman's spec)
  S0O2R4: [
    { shape: 'oval',   x:  -0.0506, y: 0.000, z:  -0.0669, ry: 90 },
    { shape: 'oval',   x:   0.0503, y: 0.000, z:   0.0673, ry: 90 },
    { shape: 'round',  x:  -0.0995, y: 0.012, z:  -0.0673, ry:  0 }, // on oval 1
    { shape: 'round',  x:   0.0998, y: 0.012, z:   0.0670, ry:  0 }, // on oval 2
    { shape: 'round',  x:  -0.1375, y: 0.000, z:   0.1360, ry:  0 }, // floor, front (stacked pair, moved down per Roman's arrow @35%)
    { shape: 'round',  x:  -0.1375, y: 0.012, z:   0.1360, ry:  0 }, // stacked exactly on the 3rd round
  ],
  // 0 square + 0 ovals + 1-4 rounds — all rounds flat on the floor, original
  // 4-piece composition (Roman: round-only sets stay capped at 4)
  S0O0R4: [
    { shape: 'round', x:  -0.1201, y: 0.000, z:   0.0247, ry: 0 },
    { shape: 'round', x:  -0.0262, y: 0.000, z:  -0.1075, ry: 0 },
    { shape: 'round', x:   0.0554, y: 0.000, z:   0.1067, ry: 0 },
    { shape: 'round', x:   0.0909, y: 0.000, z:  -0.0238, ry: 0 },
  ],
};

function pickLayout(s, o, r) {
  if (s === 1 && o >= 2) return 'S1O2R3';
  if (s === 1 && o === 1) return 'S1O1R4';
  if (s === 1)           return 'S1O0R4';
  if (o === 2)           return 'S0O2R4';
  if (o === 1)           return 'S0O1R4';
  return 'S0O0R4';
}

// ── Constraint helpers ────────────────────────────────────────────────────────
function countByShape(pieces) {
  const c = { round: 0, oval: 0, square: 0 };
  for (const p of pieces) c[p.shape]++;
  return c;
}

// Six canonical layouts, one per square/oval mix. Caps (Roman's 2026-07-08
// spec): max 1 square, max 2 ovals, max 4 rounds, max MAX_PIECES total.
// Resulting maxima: 4R / 1O+4R / 2O+4R / 1S+4R / 1S+1O+4R / 1S+2O+3R.
const MAX_PIECES = 6;
function roundMaxFor(square, oval) {
  return Math.min(4, MAX_PIECES - square - oval);
}
function canAdd(pieces, newShape) {
  const c = countByShape(pieces);
  const after = { ...c };
  after[newShape]++;
  if (after.square + after.oval + after.round > MAX_PIECES) return { ok: false };
  if (after.square > 1) return { ok: false };
  if (after.oval > 2)   return { ok: false };
  if (after.round > roundMaxFor(after.square, after.oval)) return { ok: false };
  return { ok: true };
}

// ── Zufalls-Set ("feeling lucky") ────────────────────────────────────────────
// Baut ein regelkonformes Set aus mindestens `minPieces` Teilen, indem es
// zufällige Formen über canAdd() zieht (bleibt automatisch im Constraint-Set).
// Square gibt es nur in Creme + Light-Pink.
function randomSet(minPieces) {
  const SQUARE_COLORS = COLORS.filter((c) => c === 'creme' || c === 'light-pink');
  const min = Math.max(2, Math.min(MAX_PIECES, minPieces || 2));
  for (let attempt = 0; attempt < 20; attempt++) {
    const pieces = [];
    const target = Math.min(MAX_PIECES, min + Math.floor(Math.random() * 3));
    let guard = 40;
    while (pieces.length < target && guard-- > 0) {
      const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
      if (!canAdd(pieces, shape).ok) continue;
      const pool = shape === 'square' ? SQUARE_COLORS : COLORS;
      pieces.push({ shape, color: pool[Math.floor(Math.random() * pool.length)] });
    }
    if (pieces.length >= min) return pieces;
  }
  return [{ shape: 'round', color: 'creme' }, { shape: 'oval', color: 'blue' }];
}

// ── Variant lookup ────────────────────────────────────────────────────────────
function buildVariantIndex(variants) {
  const byKey = new Map();
  for (const v of variants) byKey.set(`${v.shape}|${v.color}`, v);
  return byKey;
}
function lookupVariant(idx, shape, color) {
  return idx.get(`${shape}|${color}`) || null;
}
function availableColorsFor(idx, shape, currentColor) {
  return COLORS.map(c => {
    const v = lookupVariant(idx, shape, c);
    return {
      color: c,
      variantId: v ? v.id : null,
      available: !!(v && v.available),
      exists: !!v,
      isCurrent: c === currentColor,
    };
  });
}

// ── URL sync ──────────────────────────────────────────────────────────────────
function serializeUrl(pieces) { return pieces.map(p => `${p.shape}-${p.color}`).join(','); }
function parseUrl(str) {
  if (!str) return [];
  return str.split(',').reduce((acc, token) => {
    const [shape, ...rest] = token.trim().split('-');
    const color = rest.join('-');
    if (SHAPES.includes(shape) && COLORS.includes(color)) acc.push({ shape, color });
    return acc;
  }, []);
}
function getSetFromUrl() { return parseUrl(new URLSearchParams(window.location.search).get('set')); }
function writeSetToUrl(pieces, floor) {
  // Manually rebuild the search string so commas in our value stay unencoded
  // (`URLSearchParams.set` would turn them into %2C and clutter the URL).
  const existing = new URLSearchParams(window.location.search);
  existing.delete('set');
  existing.delete('floor');
  existing.delete('lino');
  const params = [];
  for (const [k, v] of existing) params.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  if (pieces.length) params.push(`set=${serializeUrl(pieces)}`);
  if (floor && floor !== 'white') params.push(`floor=${encodeURIComponent(floor)}`);
  if (floor === 'linoleum' && SCENE_PARAMS.floorLinoColor !== DEFAULT_LINO_COLOR) params.push(`lino=${encodeURIComponent(SCENE_PARAMS.floorLinoColor)}`);
  const search = params.length ? '?' + params.join('&') : '';
  window.history.replaceState({}, '', window.location.pathname + search + window.location.hash);
}

// ── Layout: pick positions for the current set ────────────────────────────────
// Choose one of the 5 canonical layouts based on the user's shape mix, then
// assign each user piece (in insertion order) to the next available slot of
// its shape in that layout. Subsets of the layout simply use fewer slots.
function layoutForSet(pieces) {
  if (pieces.length === 0) return [];
  const c = countByShape(pieces);
  const key = pickLayout(c.square, c.oval, c.round);
  const layout = LAYOUTS[key] || [];
  const buckets = { square: [], oval: [], round: [] };
  for (const slot of layout) buckets[slot.shape].push(slot);
  const used = { square: 0, oval: 0, round: 0 };
  const positions = pieces.map((p, i) => {
    const slot = buckets[p.shape][used[p.shape]++];
    return slot
      ? { shape: p.shape, color: p.color, uid: p.uid, pieceIndex: i, x: slot.x, y: slot.y, z: slot.z, ry: slot.ry }
      : { shape: p.shape, color: p.color, uid: p.uid, pieceIndex: i, x: 0, y: 0, z: 0, ry: 0 };
  });
  return settleLayers(positions);
}

// A raised slot (y>0) is only valid if the piece it was designed to rest on is
// actually present in this subset. If nothing sits under it, drop it to the
// ground — otherwise it hovers in mid-air. Test: the raised piece's centre must
// fall within a lower piece's footprint.
// Plan-view half extents (m) of a placed piece — used by the layer-settling
// check and the composition-radius camera fit.
function halfExt(p) {
  if (p.shape === 'round')  return [0.049, 0.049];
  if (p.shape === 'square') return [0.098, 0.098];
  return p.ry === 90 ? [0.098, 0.049] : [0.049, 0.098]; // oval
}

// Outer radius (m, from the origin) of a composition in plan view. Drives the
// dynamic camera fit: compositions bigger than camFitRef dolly the camera out.
function compositionRadius(positions) {
  let r = 0;
  for (const p of positions) {
    const [hx, hz] = halfExt(p);
    r = Math.max(r, Math.hypot(Math.abs(p.x) + hx, Math.abs(p.z) + hz));
  }
  return r;
}

function settleLayers(positions) {
  for (const p of positions) {
    if (p.y <= 0.0001) continue; // already on the ground
    let supported = false;
    for (const q of positions) {
      if (q === p || q.y >= p.y - 0.0001) continue; // q must be below p
      const [qx, qz] = halfExt(q);
      if (Math.abs(p.x - q.x) < qx && Math.abs(p.z - q.z) < qz) { supported = true; break; }
    }
    if (!supported) p.y = 0;
  }
  return positions;
}


// ── Studio "window" environment (HDR equirect with a bright soft rectangle).
// Ported from the round-ceramics prototype: PMREM-baked into scene.environment
// so the glossy clearcoat reflects a clean softbox-style window instead of the
// default neutral RoomEnvironment.
// -----------------------------------------------------------------------------
function buildWindowEnvironment(renderer, pmrem) {
  const smoothstep = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  const W = 768, H = 384, p = SCENE_PARAMS;
  const data = new Float32Array(W * H * 4);
  const wAz = p.winAzimuth * Math.PI / 180;
  const wAz2 = (p.winAzimuth + 180) * Math.PI / 180; // opposite side of the room
  const wEl = p.winElevation * Math.PI / 180;
  const halfW = (p.winWidth * Math.PI / 180) / 2;
  const halfH = (p.winHeight * Math.PI / 180) / 2;
  const soft = Math.max(0.0017, p.winSoftness * Math.PI / 180);
  const b2 = p.winBrightness * (p.win2Factor ?? 0);
  const b3 = p.win3Brightness ?? 0;
  const wAz3 = ((p.winAzimuth + (p.win3Azimuth ?? 120)) % 360) * Math.PI / 180; // relative to the main window
  const wEl3 = (p.win3Elevation ?? -49) * Math.PI / 180;
  const halfW3 = ((p.win3Width ?? 8) * Math.PI / 180) / 2;
  const halfH3 = ((p.win3Height ?? 12) * Math.PI / 180) / 2;
  // Mullions (Fensterstreben): dark bars split the light panel into panes so the
  // glossy pieces reflect a real paned window instead of a plain glowing box.
  const panesX = p.winPanesX ?? 2, panesY = p.winPanesY ?? 3, barHalf = p.winMullion ?? 0.10;
  const gdist = (t, panes) => { const s = (t * 0.5 + 0.5) * panes; return Math.abs(s - Math.round(s)) / panes; };
  const mull = (daz, del) => {
    if (barHalf <= 0) return 1;
    const g = Math.min(gdist(daz / halfW, panesX), gdist(del / halfH, panesY));
    // HARD-EDGED bars: near-step transition (only ~8% of the bar width soft,
    // just enough to avoid aliasing) — soft ramps read as smudges in the
    // reflections, Roman wants crisp frame lines (2026-07-22).
    return smoothstep(barHalf * 0.92, barHalf, g);
  };
  const wallWarm = p.wallWarmth ?? 0.045; // subtle warm tint on the ambient/wall
  for (let y = 0; y < H; y++) {
    const el = (0.5 - (y + 0.5) / H) * Math.PI;
    for (let x = 0; x < W; x++) {
      const az = ((x + 0.5) / W) * 2 * Math.PI - Math.PI;
      const tUp = Math.max(0, el / (Math.PI / 2));
      let v = p.roomBrightness * (0.45 + 0.55 * tUp);
      // darker toward the floor
      if (el < 0) v *= 0.55 + 0.45 * (1 + el / (Math.PI / 2));
      let dazWall = az - wAz; dazWall = Math.atan2(Math.sin(dazWall), Math.cos(dazWall));
      // Optional room details — multiplicative on the ambient, BEFORE the
      // windows are added (so they never dim the window light itself).
      if (p.envWallGrad) {
        v *= 1 - p.envWallGradStrength * smoothstep(0.35, 2.6, Math.abs(dazWall));
      }
      // Clerestory LIGHT BAND — alternating bright/dark sectors painted straight
      // into the env at world el ≈ +35..+65° (the band flat tops mirror), full
      // 360°. This is THE reliable channel for top reflections: the painted
      // window demonstrably reaches the tops through the probe's background
      // capture, while 3D ceiling geometry arrives washed-out (live-proven
      // 2026-07-22 — even a half-black 3D ceiling barely registered). Sector
      // count 5 → 36° features, wide enough to survive the glaze blur.
      if (p.envBand) {
        // texture-el convention: negative el = world-up (same as winElevation)
        const bandMask = smoothstep(-1.31, -1.08, el) * (1 - smoothstep(-0.72, -0.5, el));
        const sect = 0.5 + 0.5 * Math.cos((az - wAz) * p.envBandSectors);
        const sectPulse = smoothstep(0.35, 0.65, sect);
        v += p.envBandBright * bandMask * sectPulse;
        v *= 1 - 0.85 * bandMask * (1 - sectPulse); // darken the off sectors
      }
      if (p.envWallGlow) {
        // bright upper-wall band (world el ≈ +25..+65° — the zone the flat piece
        // tops actually reflect; this is what makes the WALL visible in them)
        const glowBand = smoothstep(-1.2, -0.85, el) * (1 - smoothstep(-0.55, -0.3, el));
        v += p.envWallGlowBright * glowBand * (1 - smoothstep(1.4, 2.8, Math.abs(dazWall)));
      }
      if (p.envSilhouettes) {
        // dark verticals (door/shelf silhouettes) at fixed offsets from the window;
        // reach extended upward so they stay visible in the tops' reflection band
        const silHalf = (p.envSilWidth * Math.PI / 180) / 2;
        const hMask = 1 - smoothstep(1.15, 1.45, Math.abs(el));
        // bars flank the window (±26°) so they land in the zone the piece
        // tops visibly reflect, plus wider offsets for the rims
        for (const off of [-0.45, 0.45, -1.15, 1.15, 2.8]) {
          let d = az - (wAz + off); d = Math.atan2(Math.sin(d), Math.cos(d));
          v *= 1 - p.envSilStrength * hMask * (1 - smoothstep(silHalf, silHalf + 0.07, Math.abs(d)));
        }
      }
      if (p.envFurniture) {
        // darker furniture band ABOVE the horizon (world +6..+25°): from the
        // pieces' floor-level viewpoint, furniture across the room rises above
        // their horizon — and only above-horizon env is ever reflected (below-
        // horizon directions show the real floor, not the env).
        const band = smoothstep(-0.5, -0.32, el) * (1 - smoothstep(-0.14, -0.05, el));
        v *= 1 - p.envFurnStrength * band;
      }
      const del = el - wEl;
      const my = 1 - smoothstep(halfH, halfH + soft, Math.abs(del));
      // primary window (with mullions). The window rect REPLACES the wall:
      // bright glass panes against TRULY BLACK mullion bars (v → 0), so the
      // bars read as crisp dark frame lines in the reflections — merely
      // removing the window light left the bars at wall brightness, which
      // washed them out inside the blazing panes (Roman, 2026-07-22).
      let daz = az - wAz; daz = Math.atan2(Math.sin(daz), Math.cos(daz));
      const wv0 = (1 - smoothstep(halfW, halfW + soft, Math.abs(daz))) * my;
      if (wv0 > 0) {
        const pane = mull(daz, del);
        v = v * (1 - wv0) + wv0 * p.winBrightness * pane;
      }
      // second window (opposite side)
      if (b2 > 0) {
        let daz2 = az - wAz2; daz2 = Math.atan2(Math.sin(daz2), Math.cos(daz2));
        const wv20 = (1 - smoothstep(halfW, halfW + soft, Math.abs(daz2))) * my;
        if (wv20 > 0) {
          const pane2 = mull(daz2, del);
          v = v * (1 - wv20) + wv20 * b2 * pane2;
        }
      }
      // third window — plain panel (no mullions), offset from the main window
      if (b3 > 0) {
        const del3 = el - wEl3;
        let daz3 = az - wAz3; daz3 = Math.atan2(Math.sin(daz3), Math.cos(daz3));
        const wv3 = (1 - smoothstep(halfW3, halfW3 + soft, Math.abs(daz3))) *
                    (1 - smoothstep(halfH3, halfH3 + soft, Math.abs(del3)));
        v += b3 * wv3;
      }
      let warmAdd = 0;
      if (p.envCeilGlow) {
        // soft ceiling light pool above the window side (world-up = negative el
        // in this texture's flipped convention — same convention as winElevation)
        const dCeil = el + 1.22; // el ≈ −70°
        v += p.envCeilBright * (1 - smoothstep(0.35, 0.75, Math.abs(dCeil))) *
             (1 - smoothstep(0.9, 1.6, Math.abs(dazWall)));
      }
      if (p.envWarmAccent) {
        // small warm lamp opposite the window, slightly above the horizon
        let dA = az - (wAz + Math.PI); dA = Math.atan2(Math.sin(dA), Math.cos(dA));
        const dE = el + 0.26;
        warmAdd = p.envWarmBright * (1 - smoothstep(0.12, 0.3, Math.sqrt(dA * dA + dE * dE)));
      }
      const i = (y * W + x) * 4;
      // slight warm tint on the ambient/wall
      data[i] = v + warmAdd;
      data[i + 1] = v * (1 - wallWarm * 0.3) + warmAdd * 0.72;
      data[i + 2] = v * (1 - wallWarm) + warmAdd * 0.4;
      data[i + 3] = 1;
    }
  }
  const tex = new THREE.DataTexture(data, W, H, THREE.RGBAFormat, THREE.FloatType);
  tex.mapping = THREE.EquirectangularReflectionMapping;
  tex.needsUpdate = true;
  const env = pmrem.fromEquirectangular(tex).texture;
  // The RAW equirect texture is kept alive for the probe bake: rendering the
  // PMREM env as scene.background re-photographs an already-filtered CubeUV
  // texture and the probe captures blurred mush — the raw texture stays sharp.
  return { env, tex };
}

// ── Procedural ceramic surface textures. Normal + roughness maps carry just
// fine pores (pits) — the leather macro-relief was removed (not wanted for
// ceramic). Shared across all materials/pieces.
// -----------------------------------------------------------------------------
function makeCeramicTextures(renderer) {
  const N = 512, NORMAL_GAIN = 4.0;
  const REPEAT = SCENE_PARAMS.texRepeat;
  const PIT_COUNT = SCENE_PARAMS.pitCount, PIT_DEPTH = SCENE_PARAMS.pitDepth, PIT_RMIN = 0.6, PIT_RMAX = 2.2;

  const pitsH = new Float32Array(N * N);
  for (let i = 0; i < PIT_COUNT; i++) {
    const cx = (((Math.sin(i * 12.9898) * 43758.5453) % 1) + 1) % 1;
    const cy = (((Math.sin(i * 78.233) * 12543.1234) % 1) + 1) % 1;
    const px = Math.floor(cx * N), py = Math.floor(cy * N);
    const r = PIT_RMIN + ((Math.sin(i * 3.17) + 1) * 0.5) * (PIT_RMAX - PIT_RMIN);
    const ri = Math.ceil(r);
    for (let dy = -ri; dy <= ri; dy++) for (let dx = -ri; dx <= ri; dx++) {
      const dist = Math.hypot(dx, dy);
      if (dist > r) continue;
      const xx = (px + dx + N) % N, yy = (py + dy + N) % N;
      const prof = Math.cos((dist / r) * Math.PI) * 0.5 + 0.5;
      const idx = yy * N + xx;
      if (prof > pitsH[idx]) pitsH[idx] = prof;
    }
  }

  const mkCanvas = () => { const c = document.createElement('canvas'); c.width = c.height = N; return c; };
  const nC = mkCanvas(), rC = mkCanvas();
  const nctx = nC.getContext('2d'), rctx = rC.getContext('2d');
  const nimg = nctx.createImageData(N, N), rimg = rctx.createImageData(N, N);

  const at = (x, y) => {
    const i = ((y + N) % N) * N + ((x + N) % N);
    return -PIT_DEPTH * pitsH[i];
  };
  for (let y = 0; y < N; y++) for (let x = 0; x < N; x++) {
    const dx = (at(x - 1, y) - at(x + 1, y)) * NORMAL_GAIN;
    const dy = (at(x, y - 1) - at(x, y + 1)) * NORMAL_GAIN;
    const len = Math.hypot(dx, dy, 1);
    const i = (y * N + x) * 4;
    nimg.data[i]     = ((dx / len) * 0.5 + 0.5) * 255;
    nimg.data[i + 1] = ((dy / len) * 0.5 + 0.5) * 255;
    nimg.data[i + 2] = ((1 / len) * 0.5 + 0.5) * 255;
    nimg.data[i + 3] = 255;
  }
  nctx.putImageData(nimg, 0, 0);
  for (let i = 0; i < N * N; i++) {
    const rough = Math.max(0, Math.min(1, 0.5 + pitsH[i] * PIT_DEPTH * 0.4));
    rimg.data[i * 4] = rimg.data[i * 4 + 1] = rimg.data[i * 4 + 2] = rough * 255;
    rimg.data[i * 4 + 3] = 255;
  }
  rctx.putImageData(rimg, 0, 0);
  const mk = (canvas) => {
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(REPEAT, REPEAT);
    t.colorSpace = THREE.NoColorSpace;
    t.anisotropy = renderer.capabilities.getMaxAnisotropy();
    return t;
  };
  return { normalMap: mk(nC), roughnessMap: mk(rC) };
}

// ── Glaze-edge + speckle shader (ported from round-ceramics prototype) ────────
// Fixed params (the prototype's GUI defaults that Roman dialled in).
// ── Single source of truth for every tunable scene parameter. Defaults mirror
// the round-ceramics prototype. The debug GUI (?pcdebug=1) mutates this live.
// Registries let live edits reach already-created materials.
const SCENE_PARAMS = {
  // 5 product colours (ceramic material tint) — Roman's tuned values
  colors: {
    'creme':       '#f2ecde',
    'light-pink':  '#ffe5e3',
    'blue':        '#7a98c7',
    'red':         '#ff551a',
    'light-green': '#cfdc98',
  },
  saturation: 1,
  // Tone mapping: 'ACESFilmic' | 'Neutral' | 'AgX' | 'Cineon' | 'Linear'
  toneMapping: 'Neutral',
  // material / glaze
  roughness: 0.1, clearcoat: 1, clearcoatRoughness: 0, envIntensity: 0.4,
  // Normal-incidence reflectivity of the glaze (ior 1.5 ≈ 4% F0, 2.333 ≈ 16%).
  // Flat piece TOPS reflect at F0 — stock 4% is why scene objects never showed
  // in them; a hochglänzende Glasur reads correct at ~2.0 (live-QA'd 2026-07-22).
  pieceIor: 1.62,
  pieceMetalness: 0,
  sheen: 0.02, sheenRoughness: 0, sheenColor: '#ffffff',
  // glaze edge
  rimStrength: 0.39, edgeCenter: 0.71, edgeTight: 4.1, upBias: 0.25, rimColor: '#eee8d7',
  // colour speckle
  speckleAmount: 0.15, speckleScale: 2950, speckleDensity: 1.08,
  // procedural surface texture — just fine pores (pits); leather relief removed.
  pitCount: 160, pitDepth: 0.12, texRepeat: 5,
  // window-light environment (primary window)
  winBrightness: 2, winWidth: 36, winHeight: 13, winElevation: -45,
  winAzimuth: -48, winSoftness: 0, roomBrightness: 0.36,
  // second window (opposite side) relative brightness → second edge reflection
  win2Factor: 0.83,
  // third window — another strong edge reflection. brightness 0 = off. NO
  // mullions (plain panel); azimuth is RELATIVE to the main window (Δ°).
  win3Brightness: 5, win3Azimuth: -154, win3Elevation: -44, win3Width: 8, win3Height: 7,
  // window mullions (panes) + warm-wall enrichment of the reflected "room"
  winPanesX: 3, winPanesY: 1, winMullion: 0.095, wallWarmth: 0,
  envBand: false, envBandBright: 5, envBandSectors: 5,
  sheenLight: 0,
  sheenSpread: 0.8,
  // lights — intensities + colours
  hemiIntensity: 0.17, keyIntensity: 2.8, fillIntensity: 2, rimIntensity: 0.3,
  keyColor: '#fffbf5', fillColor: '#ffffff', rimLightColor: '#ffffff',
  hemiSkyColor: '#f5f5f5', hemiGroundColor: '#f6f6f4',
  // key-light position (drives shadow direction + length)
  keyX: -1.95, keyY: 2, keyZ: -0.8,
  // shadow tuning. shadowRadius = blur (works now — PCFShadowMap, not Soft).
  shadowBias: 0.0005, shadowNormalBias: 0, shadowRadius: 2,
  // anti-aliasing / quality
  pixelRatio: 4,        // render supersampling (higher = smoother edges, slower)
  shadowMapSize: 4096,  // shadow resolution (higher = finer/less-pixelated shadow edge)
  // scene / render
  // shadowOpacity = the ShadowMaterial catcher darkness. 0.14 was too faint to
  // read on the bright floors (looked like "no shadow"); 0.3 shows a clear but
  // still soft contact shadow. Tunable in the debug GUI (Scene / render).
  background: '#ffffff', floorColor: '#ffffff', exposure: 1, shadowOpacity: 0.54,
  // floor selection (see FLOORS): 'white' | 'stone' | 'linoleum' | 'oak'
  floor: 'white',
  // linoleum: selectable colour + palette, homogeneity (lower colorContrast =
  // flatter) and fibre-mottle grain (see floorLinoGrain*).
  floorLinoColor: 'grey',
  floorLinoColors: {
    grey:      '#e0dedc',
    blue:      '#8ea6cc',
    green:     '#d1df9a',
    burgundy:  '#3e6a4b', // label "Dunkelgrün" — key kept for URL compatibility
    mushroom:  '#c3bbac',
    orange:    '#db6939',
    anthrazit: '#595959',
  },
  floorLinoContrast: 0,
  // Lino grain — the mottled fibre look of real linoleum (replaces the old
  // leather/crease relief): colour mottle amount, frequency ×, micro-relief.
  floorLinoGrain: 0.03, floorLinoGrainScale: 1.55, floorLinoGrainRelief: 0.2,
  // per-floor texture scale (tiles across the 40 m plane — higher = finer/smaller
  // grain) + wood texture contrast (1 = original, <1 flattens the grain contrast).
  floorStoneScale: 27, floorStoneContrast: 0.89,
  // Texture-floor colour grading (stone + wood): brightness ×, warmth (−1 cold
  // … +1 warm), multiplicative tint (white = untouched).
  floorStoneBright: 2.51, floorStoneWarmth: -0.63, floorStoneTint: '#e5eaff',
  floorOakBright: 1.25, floorOakWarmth: -0.02, floorOakTint: '#fffcf0',
  // Floor glossiness: clearcoat amount (reflections) + its roughness (sharpness).
  // Lino gloss also drives the Neutral floor (same material family).
  floorLinoGloss: 0.09, floorLinoGlossRough: 0.11,
  floorStoneGloss: 0.5, floorStoneGlossRough: 0.18,
  floorOakGloss: 0.2, floorOakGlossRough: 0.21, floorLinoScale: 120, floorOakScale: 28, floorOakContrast: 0.97,
  // Stainless steel (AmbientCG Metal011) — a real metal: metalness 1 + its
  // metalness/roughness maps drive the brushed grain, no clearcoat, and a strong
  // env so the window actually reflects in it (that IS the look of steel).
  floorSteelScale: 94, floorSteelContrast: 0.4,
  floorSteelBright: 4.4, floorSteelWarmth: 0.13, floorSteelTint: '#fefff7',
  floorSteelGloss: 0, floorSteelGlossRough: 0.19,  // 'reflection' = clearcoat strength (counterpart to roughness)
  // The two levers the steel look actually hangs on: at metalness 1 there is no
  // diffuse, so brightness comes from the env, and roughness sets mirror↔brushed.
  floorSteelEnv: 3.75, floorSteelRough: 0.76,
  // White tablecloth (AmbientCG Fabric081C) — dielectric, rough, with sheen for
  // the soft fabric falloff; weave comes from the normal map.
  floorClothScale: 100, floorClothContrast: 1.34,
  floorClothBright: 1.3, floorClothWarmth: -0.11, floorClothTint: '#ffffff',
  floorClothGloss: 0, floorClothGlossRough: 0.6,
  // weave = normal-map strength (thread relief), sheen = the soft fabric falloff.
  floorClothEnv: 0.35, floorClothWeave: 4, floorClothSheen: 0.02,
  // Marble / wood / terrazzo photoreal floors (AmbientCG) — dielectric tex floors.
  floorMarblewhiteScale: 54, floorMarblewhiteContrast: 0.67,
  floorMarblewhiteBright: 0.87, floorMarblewhiteWarmth: -0.38, floorMarblewhiteTint: '#ffffff',
  floorMarblewhiteGloss: 0.28, floorMarblewhiteGlossRough: 0.1,
  floorMarblebeigeScale: 38, floorMarblebeigeContrast: 1,
  floorMarblebeigeBright: 0.95, floorMarblebeigeWarmth: -0.57, floorMarblebeigeTint: '#ffffff',
  floorMarblebeigeGloss: 0, floorMarblebeigeGlossRough: 1,
  floorMarblegreyScale: 56, floorMarblegreyContrast: 1,
  floorMarblegreyBright: 0.85, floorMarblegreyWarmth: 0.08, floorMarblegreyTint: '#ffffff',
  floorMarblegreyGloss: 1, floorMarblegreyGlossRough: 0.19,
  floorBeechScale: 64, floorBeechContrast: 0.87,
  floorBeechBright: 1.07, floorBeechWarmth: -0.09, floorBeechTint: '#eef4ff',
  floorBeechGloss: 0.36, floorBeechGlossRough: 0.12,
  floorTerrazzowarmScale: 103, floorTerrazzowarmContrast: 1,
  floorTerrazzowarmBright: 1.09, floorTerrazzowarmWarmth: -0.3, floorTerrazzowarmTint: '#ffffff',
  floorTerrazzowarmGloss: 0.18, floorTerrazzowarmGlossRough: 0.25,
  floorTerrazzocoolScale: 62, floorTerrazzocoolContrast: 0.93,
  floorTerrazzocoolBright: 1.27, floorTerrazzocoolWarmth: -0.19, floorTerrazzocoolTint: '#ffffff',
  floorTerrazzocoolGloss: 1, floorTerrazzocoolGlossRough: 0.1,
  // default camera
  camFov: 16, camX: -0.65, camY: 0.95, camZ: 0.71, camTargetX: 0.015, camTargetY: -0.035,  // pan: X shifts left, Y (neg) lifts objects up in frame
  // Aspect-aware zoom: the static camera is shared between the square mobile
  // stage (aspect ~1) and the wide desktop stage (~1.9). At the base distance the
  // set fills the square nicely but looks small/lost on the wide desktop frame.
  // So dolly IN as the stage widens: scale = 1 at aspect <= Lo, ramping to
  // camZoomWide at aspect >= Hi. Mobile stays exactly as before (scale 1).
  camZoomWide: 0.6, camZoomAspectLo: 1.25, camZoomAspectHi: 2.1,
  // Extra global dolly-in applied on top of the aspect zoom (multiplies the
  // camera distance at every aspect). 0.92 = ~9% closer — a middle ground between
  // the original framing (1.0) and the tighter 0.8 photo-real crop.
  camZoom: 1.2,
  // Dynamic composition fit: plan-view outer radius (m) the default framing was
  // tuned for. Bigger sets dolly the camera out by radius/camFitRef (tweened,
  // capped at camFitMax) so 6-piece compositions never crop.
  camFitRef: 0.24, camFitMax: 1.4,
  // ── Animations (each independently toggleable in the debug GUI; ALL skipped
  // under prefers-reduced-motion). animPieces covers the full piece lifecycle:
  // drop-in on add (the "materialize" fall), glide when a re-layout moves
  // existing pieces, fade+shrink on remove.
  animPieces: true,
  introDrop: 0.02,      // drop distance (m)
  introFallTime: 0.65,  // s per piece
  introStagger: 0.09,   // s between piece starts
  animMoveMs: 790,      // re-layout glide duration (ms)
  animRemoveMs: 70,     // remove fade duration (ms)
  animColorFade: true,  // crossfade the glaze colour on swap
  animColorMs: 380,
  animCamIntro: true,  // camera dolly-in on the first load
  introCamPull: 0.14,   // camera starts this fraction further out
  introCamTime: 1.6,    // s camera settle
  animIdleDrift: true,  // gentle idle yaw sway (renders continuously while on)
  idleDriftDeg: 3, idleDriftPeriod: 15,
  // ── Photorealism (kept after A/B: GTAO + orange peel; contact blobs,
  // floor reflection, HDRI env and DoF were tried and removed) ──────────────
  fxGtao: true, fxGtaoRadius: 0.01, fxGtaoBlend: 0.55,  // screen-space ambient occlusion
  fxPeel: true, fxPeelStrength: 0.04, fxPeelScale: 2.5,   // clearcoat "orange peel" waviness
  // Shadowed glaze must not show full-strength window reflections (three.js
  // shadows only attenuate DIRECT light; IBL is unshadowed) — dim the env
  // specular by the key-light shadow factor. 0 = off, 1 = full occlusion.
  envShadowDim: 0,
  // Scene reflection probe: pieces reflect the REAL scene (chosen floor, room,
  // window, neighbouring pieces) via CubeCamera→PMREM, baked only on changes.
  probeEnabled: true, probeBoost: 1.8, probeRes: 512, probeFurniture: false, probeCeiling: false, probeDeco: false,
  probeStatus: 'noch kein Bake',
  // Room details drawn into the window env (reflect in floor + pieces).
  // Each feature is opt-in via its GUI checkbox.
  envWallGrad: false, envWallGradStrength: 0.67,
  envWallGlow: false, envWallGlowBright: 1.2,
  envSilhouettes: false, envSilStrength: 0.5, envSilWidth: 12,
  envFurniture: false, envFurnStrength: 0.35,
  envCeilGlow: false, envCeilBright: 1.5,
  envWarmAccent: false, envWarmBright: 2,
  // ── Post-processing (EffectComposer): one pipeline for all devices, on the
  // initial view AND fullscreen — bloom + static grain + SMAA anti-aliasing,
  // always on. Shadows are the DirectionalLight cast shadow + contact catcher.
  fxSamples: 4,            // composer MSAA
  fxPixelRatioCap: 2,      // cap render DPR
  fxGrain: 0.05,           // static film grain (no flicker)
  fxBloom: 0.13, fxBloomThreshold: 1.54, fxBloomRadius: 0.5,
  // ── Round-Oberseiten-Relief (Test nur an der runden Form; Oval/Square unberührt).
  // Minimale, produktionsechte Wölbung der TOP-Fläche: vom Rand fällt die Fläche in
  // eine flache Ring-Senke und steigt zur Mitte zu einer zentralen Erhebung an.
  // Unterseite bleibt flach. Live im Debug-GUI (?pcdebug=1) unter „Geometrie" regelbar.
  // ── Oberseiten-Relief (alle Formen, folgt der Außenkontur via SDF) — prozedurale
  // Normal-Perturbation im Shader, KEINE echte Geometrie: von der Kante fällt die
  // Fläche in eine flache Senke und steigt zur Mitte an (Round: Punkt, Oval: Grat,
  // Square: Plateau). Wirkt pro Pixel → umgeht die grobe Oberseiten-Tessellation.
  // Kontur je Form in RELIEF_DIMS; live im Debug-GUI unter „Geometrie".
  reliefEnabled: true,
  reliefStrength: 12,           // Gesamt-Stärke der Normal-Neigung (Romans Wahl nach Sichttest; live tunebar)
  reliefCrownWidth: 0.86,     // äußere Breite der zentralen Erhebung (Anteil des Radius)
  reliefCrownHeightMm: 0.08,  // Höhe der Mitten-Erhebung (mm, formgebend)
  reliefValleyDepthMm: 0.025,  // Tiefe der Ring-Senke (mm, formgebend)
  reliefValleyPos: 0.95,      // Lage der Senke (Anteil des Radius)
  reliefValleyWidth: 0.07,    // Breite der Senke (Anteil des Radius)
  reliefRimFlat: 0.28,        // äußeres Randband ohne Neigung (Anteil des Radius)
  // internal registries (not shown in GUI)
  _glaze: [],       // {u} uniform bags of live materials
  _mats: [],        // live MeshPhysicalMaterial refs
};
// Baked default linoleum tint — used to omit `lino=` from shareable URLs when
// the selection equals the default (kept in sync with SCENE_PARAMS above so a
// settings-JSON paste that changes floorLinoColor can't silently break links).
const DEFAULT_LINO_COLOR = SCENE_PARAMS.floorLinoColor;

const TONE_MAPPINGS = {
  ACESFilmic: THREE.ACESFilmicToneMapping,
  Neutral: THREE.NeutralToneMapping,
  AgX: THREE.AgXToneMapping,
  Cineon: THREE.CineonToneMapping,
  Linear: THREE.LinearToneMapping,
};

// Apply the global saturation multiplier to a hex → THREE.Color.
function saturatedColor(hex) {
  const c = new THREE.Color().set(hex);
  const hsl = {};
  c.getHSL(hsl, THREE.SRGBColorSpace);
  c.setHSL(hsl.h, Math.min(1, hsl.s * SCENE_PARAMS.saturation), hsl.l, THREE.SRGBColorSpace);
  return c;
}

// Bake per-vertex attributes used by the glaze-edge shader:
//   aEdge   = |normal · thinAxis|  (~1 on flat faces, ~0 on the side wall)
//   aConvex = signed convexity (>0 outward), from welded neighbours
// Runs ONCE per shared geometry (clones reuse it).
function computeEdgeConvexity(geometry) {
  if (geometry.getAttribute('aEdge')) return; // already baked
  const pos = geometry.attributes.position;
  const nor = geometry.attributes.normal;
  if (!pos || !nor) return;
  const count = pos.count;

  geometry.computeBoundingBox();
  const bb = geometry.boundingBox;
  const ext = [bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z];
  let axis = 0;
  if (ext[1] < ext[axis]) axis = 1;
  if (ext[2] < ext[axis]) axis = 2;
  const getC = axis === 0 ? nor.getX : axis === 1 ? nor.getY : nor.getZ;
  const edgeArr = new Float32Array(count);
  for (let i = 0; i < count; i++) edgeArr[i] = Math.abs(getC.call(nor, i));
  geometry.setAttribute('aEdge', new THREE.BufferAttribute(edgeArr, 1));

  const keyToId = new Map();
  const repId = new Int32Array(count);
  const wPos = [], wNor = [];
  const Q = 1e5;
  for (let i = 0; i < count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const key = `${Math.round(x * Q)}_${Math.round(y * Q)}_${Math.round(z * Q)}`;
    let id = keyToId.get(key);
    if (id === undefined) { id = wPos.length / 3; keyToId.set(key, id); wPos.push(x, y, z); wNor.push(0, 0, 0); }
    repId[i] = id;
    wNor[id * 3] += nor.getX(i); wNor[id * 3 + 1] += nor.getY(i); wNor[id * 3 + 2] += nor.getZ(i);
  }
  const wc = wPos.length / 3;
  for (let i = 0; i < wc; i++) {
    const l = Math.hypot(wNor[i * 3], wNor[i * 3 + 1], wNor[i * 3 + 2]) || 1;
    wNor[i * 3] /= l; wNor[i * 3 + 1] /= l; wNor[i * 3 + 2] /= l;
  }
  const sumP = new Float32Array(wc * 3);
  const cnt = new Uint32Array(wc);
  const add = (a, b) => { sumP[a*3]+=wPos[b*3]; sumP[a*3+1]+=wPos[b*3+1]; sumP[a*3+2]+=wPos[b*3+2]; cnt[a]++; };
  const tri = (a, b, c) => { add(a,b); add(a,c); add(b,a); add(b,c); add(c,a); add(c,b); };
  const idx = geometry.index;
  if (idx) { for (let i = 0; i < idx.count; i += 3) tri(repId[idx.getX(i)], repId[idx.getX(i+1)], repId[idx.getX(i+2)]); }
  else { for (let i = 0; i < count; i += 3) tri(repId[i], repId[i+1], repId[i+2]); }
  const conv = new Float32Array(wc);
  let maxAbs = 1e-6;
  for (let i = 0; i < wc; i++) {
    if (!cnt[i]) continue;
    const cx = sumP[i*3]/cnt[i] - wPos[i*3];
    const cy = sumP[i*3+1]/cnt[i] - wPos[i*3+1];
    const cz = sumP[i*3+2]/cnt[i] - wPos[i*3+2];
    conv[i] = -(cx*wNor[i*3] + cy*wNor[i*3+1] + cz*wNor[i*3+2]);
    if (Math.abs(conv[i]) > maxAbs) maxAbs = Math.abs(conv[i]);
  }
  const convArr = new Float32Array(count);
  for (let i = 0; i < count; i++) convArr[i] = conv[repId[i]] / maxAbs;
  geometry.setAttribute('aConvex', new THREE.BufferAttribute(convArr, 1));
}

function deriveSpeckleColor(baseHex) {
  // Prototype derived speckle = same hue, S×0.72, L×1.0. On the pale PLAYTS
  // pastels that's nearly invisible, so darken a touch (L×0.86) to read as
  // faint flecks like the saturated prototype reference did.
  const c = new THREE.Color().set(baseHex);
  const hsl = {};
  c.getHSL(hsl, THREE.SRGBColorSpace);
  c.setHSL(hsl.h, hsl.s * 0.72, hsl.l * 0.86, THREE.SRGBColorSpace);
  return c;
}

// Außenkontur je Form als Rounded-Box (Halb-Extents b minus Eckradius r, in Metern,
// lokales XZ). Damit folgt das Oberseiten-Relief der Außenform: Round = Kreis,
// Oval = Stadium (Längsachse Z), Square = stark gerundetes Quadrat (r aus GLB gemessen).
const RELIEF_DIMS = {
  round:  { b: [0, 0],           r: 0.049  },
  oval:   { b: [0, 0.049],       r: 0.049  },
  square: { b: [0.0557, 0.0557], r: 0.0423 },
};

// Attach the glaze-edge + colour-speckle injection to a MeshPhysicalMaterial.
// Formen mit RELIEF_DIMS-Eintrag bekommen zusätzlich das prozedurale Oberseiten-Relief.
function applyGlazeShader(mat, baseHex, shape) {
  const P = SCENE_PARAMS;
  const u = {
    uRimStrength: { value: P.rimStrength },
    uEdgeCenter:  { value: P.edgeCenter },
    uEdgeTight:   { value: P.edgeTight },
    uUpBias:      { value: P.upBias },
    uRimColor:    { value: new THREE.Color(P.rimColor) },
    uSpeckleColor: { value: deriveSpeckleColor(baseHex) },
    uSpeckleAmount: { value: P.speckleAmount },
    uSpeckleScale:  { value: P.speckleScale },
    uSpeckleThresh: { value: 0.85 - 0.55 * P.speckleDensity },
    uEnvShadow:     { value: P.envShadowDim },
  };
  // Relief-Uniforms für alle Formen mit Kontur-Definition (#ifdef PC_RELIEF gegated).
  const rd = RELIEF_DIMS[shape];
  if (rd) {
    Object.assign(u, {
      uReliefEnabled:     { value: P.reliefEnabled ? 1 : 0 },
      uReliefStrength:    { value: P.reliefStrength },
      uReliefB:           { value: new THREE.Vector2(rd.b[0], rd.b[1]) },
      uReliefCornerR:     { value: rd.r },
      uReliefInmax:       { value: Math.min(rd.b[0], rd.b[1]) + rd.r },
      uReliefCrownWidth:  { value: P.reliefCrownWidth },
      uReliefCrownH:      { value: P.reliefCrownHeightMm * 0.001 },
      uReliefValleyD:     { value: P.reliefValleyDepthMm * 0.001 },
      uReliefValleyPos:   { value: P.reliefValleyPos },
      uReliefValleyWidth: { value: P.reliefValleyWidth },
      uReliefRimFlat:     { value: P.reliefRimFlat },
    });
    mat.defines = Object.assign({}, mat.defines, { PC_RELIEF: '' });
  }
  SCENE_PARAMS._glaze.push(u); // register for live debug updates
  mat.userData.pcGlazeU = u;   // back-ref so disposal can deregister it
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, u);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>',
        '#include <common>\nattribute float aEdge;\nattribute float aConvex;\nvarying float vEdge;\nvarying float vConvex;\nvarying float vUpN;\nvarying vec3 vLocalPos;\n#ifdef PC_RELIEF\nvarying vec3 vNMx;\nvarying vec3 vNMz;\nvarying float vLocalNy;\n#endif')
      .replace('#include <beginnormal_vertex>',
        '#include <beginnormal_vertex>\nvUpN = normalize(mat3(modelMatrix) * objectNormal).y;\n#ifdef PC_RELIEF\nvLocalNy = objectNormal.y;\nvNMx = normalMatrix * vec3(1.0,0.0,0.0);\nvNMz = normalMatrix * vec3(0.0,0.0,1.0);\n#endif')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvEdge = aEdge;\nvConvex = aConvex;\nvLocalPos = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
        uniform float uRimStrength; uniform float uEdgeCenter; uniform float uEdgeTight;
        uniform float uUpBias; uniform vec3 uRimColor;
        uniform vec3 uSpeckleColor; uniform float uSpeckleAmount; uniform float uSpeckleScale; uniform float uSpeckleThresh;
        uniform float uEnvShadow;
        varying float vEdge; varying float vConvex; varying float vUpN; varying vec3 vLocalPos;
        float spHash(float n){ return fract(sin(n) * 1e4); }
        float spNoise3D(vec3 x){
          const vec3 stp = vec3(110.0, 241.0, 171.0);
          vec3 i = floor(x); vec3 f = fract(x);
          float n = dot(i, stp);
          vec3 u2 = f * f * (3.0 - 2.0 * f);
          return mix(mix(mix(spHash(n + dot(stp, vec3(0,0,0))), spHash(n + dot(stp, vec3(1,0,0))), u2.x),
                         mix(spHash(n + dot(stp, vec3(0,1,0))), spHash(n + dot(stp, vec3(1,1,0))), u2.x), u2.y),
                     mix(mix(spHash(n + dot(stp, vec3(0,0,1))), spHash(n + dot(stp, vec3(1,0,1))), u2.x),
                         mix(spHash(n + dot(stp, vec3(0,1,1))), spHash(n + dot(stp, vec3(1,1,1))), u2.x), u2.y), u2.z);
        }
        #ifdef PC_RELIEF
        uniform float uReliefEnabled; uniform float uReliefStrength;
        uniform vec2 uReliefB; uniform float uReliefCornerR; uniform float uReliefInmax;
        uniform float uReliefCrownWidth; uniform float uReliefCrownH; uniform float uReliefValleyD;
        uniform float uReliefValleyPos; uniform float uReliefValleyWidth; uniform float uReliefRimFlat;
        varying vec3 vNMx; varying vec3 vNMz; varying float vLocalNy;
        // Rounded-Box-SDF der Außenkontur (lokales XZ): <0 innen, 0 auf der Kante.
        float pcSdRBox(vec2 p, vec2 b, float r){
          vec2 q = abs(p) - b;
          return length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0) - r;
        }
        #endif`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
        #ifdef PC_RELIEF
        if (uReliefEnabled > 0.5) {
          // Konturabstand statt Kreisradius: rn = 0 auf der Mittel-„Wirbelsäule"
          // (Punkt/Grat/Plateau je Form), 1 auf der Außenkante → Profil folgt der Außenform.
          vec2 pxz = vLocalPos.xz;
          float sd = pcSdRBox(pxz, uReliefB, uReliefCornerR);
          float rn = 1.0 + sd / max(1e-4, uReliefInmax);
          float topMask = smoothstep(0.35, 0.9, vLocalNy);   // nur die Oberseite
          if (topMask > 0.001 && rn > 0.001 && rn < 1.0) {
            // analytische Ableitung des Höhenprofils dh/drn:
            float cW = max(1e-3, uReliefCrownWidth);
            float dcrown = rn < cW ? (-uReliefCrownH * 0.5 * sin(PI * rn / cW) * (PI / cW)) : 0.0;
            float vW = max(1e-3, uReliefValleyWidth);
            float dvv = (rn - uReliefValleyPos) / vW;
            float dvalley = uReliefValleyD * exp(-dvv * dvv) * (-2.0 * dvv / vW);
            float outerMask = 1.0 - smoothstep(1.0 - uReliefRimFlat, 1.0, rn);
            float dhdr = ((dcrown - dvalley) * outerMask) / max(1e-4, uReliefInmax);  // Steigung
            // Auswärts-Richtung = SDF-Gradient (numerisch, 4 Taps)
            float e = 0.0005;
            vec2 dir = vec2(
              pcSdRBox(pxz + vec2(e, 0.0), uReliefB, uReliefCornerR) - pcSdRBox(pxz - vec2(e, 0.0), uReliefB, uReliefCornerR),
              pcSdRBox(pxz + vec2(0.0, e), uReliefB, uReliefCornerR) - pcSdRBox(pxz - vec2(0.0, e), uReliefB, uReliefCornerR));
            float dl = length(dir);
            if (dl > 1e-6) {
              dir /= dl;
              // object-space Neigung → view-space via übergebene Normalmatrix-Spalten
              vec3 tiltView = vNMx * (-dhdr * dir.x) + vNMz * (-dhdr * dir.y);
              normal = normalize(normal + tiltView * (topMask * uReliefStrength));
            }
          }
        }
        #endif`)
      .replace('#include <color_fragment>', `#include <color_fragment>
        {
          float n = spNoise3D(vLocalPos * uSpeckleScale);
          float speckle = smoothstep(uSpeckleThresh, uSpeckleThresh + 0.10, n);
          float varN = spNoise3D(vLocalPos * uSpeckleScale * 1.7 + 31.0);
          vec3 spCol = uSpeckleColor * (0.7 + 0.6 * varN);
          diffuseColor.rgb = mix(diffuseColor.rgb, spCol, speckle * uSpeckleAmount);
        }`)
      .replace('#include <tonemapping_fragment>', `{
          float uu = clamp(vEdge, 0.0, 1.0);
          float dd = uu - uEdgeCenter;
          float band = exp(-dd * dd * (uEdgeTight * uEdgeTight));
          band *= smoothstep(0.03, 0.14, uu);
          band *= (1.0 - smoothstep(0.93, 0.999, uu));
          band *= smoothstep(0.0, 0.15, vConvex);
          band *= smoothstep(uUpBias - 0.12, uUpBias + 0.12, vUpN);
          gl_FragColor.rgb = mix(gl_FragColor.rgb, uRimColor, clamp(band, 0.0, 1.0) * uRimStrength);
        }
        #include <tonemapping_fragment>`);
    // ── Shadow-aware env reflections ─────────────────────────────────────────
    // three.js shadow maps only attenuate DIRECT light; IBL (the window env)
    // shines at full strength inside shadows, so reflections visually
    // "overwrite" the shadow. Capture the key light's shadow factor from the
    // lights chunk and dim the indirect specular (radiance + clearcoat) by it.
    // Defensive: if the chunk source ever changes shape, we fall back to the
    // stock chunk (no dimming) instead of breaking the shader.
    try {
      const dirShadowRe = /directLight\.color \*= \( directLight\.visible && receiveShadow \) \? getShadow\( (directionalShadowMap\[ i \][^;]*?) \) : 1\.0;/;
      const stock = THREE.ShaderChunk.lights_fragment_begin;
      if (dirShadowRe.test(stock) && shader.fragmentShader.includes('#include <lights_fragment_begin>')) {
        const patched = stock.replace(dirShadowRe, (m, args) =>
          m + '\n\t\tpcShadowF = min( pcShadowF, ( directLight.visible && receiveShadow ) ? getShadow( ' + args + ' ) : 1.0 );');
        shader.fragmentShader = shader.fragmentShader
          .replace('#include <lights_fragment_begin>', 'float pcShadowF = 1.0;\n' + patched)
          .replace('#include <lights_fragment_maps>', `#include <lights_fragment_maps>
        radiance *= mix( 1.0, pcShadowF, uEnvShadow );
        clearcoatRadiance *= mix( 1.0, pcShadowF, uEnvShadow );`);
      }
    } catch (e) { console.warn('PLAYTS glaze: env-shadow patch skipped', e); }
  };
  // Vary the cache key per base colour so each colour compiles its own program
  // (and keeps its own speckle-tint uniform); clones of the same colour share it.
  mat.customProgramCacheKey = () => 'ceramic-glaze-v12-' + baseHex;
  return mat;
}

// ── Procedural floor textures ─────────────────────────────────────────────────
// Photoreal-ish matte floors baked once to canvas textures (cached). Technique
// (see research): layer several noise octaves (fBm) so it never looks tiled,
// drive BOTH colour AND roughness from the noise (roughness micro-variation is
// what sells realism under the window light), plus a subtle normal from the
// height field. Everything is tileable (noise wraps at the grid size).
function _hex2rgb(hex) {
  const h = (hex || '#ffffff').replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}
function _noiseGrid(seed, n) {
  let s = (seed >>> 0) || 1;
  const a = new Float32Array(n * n);
  for (let i = 0; i < a.length; i++) { s = (s * 1664525 + 1013904223) >>> 0; a[i] = s / 4294967296; }
  return a;
}
function _sampleNoise(grid, n, x, y) {
  const fx = x * n, fy = y * n, ix = Math.floor(fx), iy = Math.floor(fy);
  const x0 = ((ix % n) + n) % n, y0 = ((iy % n) + n) % n, x1 = (x0 + 1) % n, y1 = (y0 + 1) % n;
  let tx = fx - ix, ty = fy - iy; tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
  const a = grid[y0 * n + x0], b = grid[y0 * n + x1], c = grid[y1 * n + x0], d = grid[y1 * n + x1];
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
}
function _fbm(octaves, x, y) {
  let sum = 0, amp = 0, a = 1;
  for (const o of octaves) { sum += _sampleNoise(o.grid, o.n, x, y) * a; amp += a; a *= 0.5; }
  return sum / amp;
}
const _floorMapCache = new Map();
// opts (lino only): { colorContrast, grain, grainScale, grainRelief } —
// colorContrast low = homogeneous; grain = fibre-mottle (colour + relief + sheen).
function makeFloorMaps(kind, tintHex, opts) {
  opts = opts || {};
  const isLino = kind === 'lino';
  const colorContrast = isLino ? (opts.colorContrast ?? 0.04) : 0.17;   // homogeneity
  const grain      = isLino ? (opts.grain ?? 0.14) : 0;       // fibre-mottle colour amplitude
  const grainScale = isLino ? (opts.grainScale ?? 1) : 1;     // mottle frequency ×
  const grainRelief = isLino ? (opts.grainRelief ?? 1.2) : 0; // mottle micro-relief (normal)
  const cacheKey = kind + '|' + (tintHex || '') + '|' + colorContrast + '|' + grain + '|' + grainScale + '|' + grainRelief;
  if (_floorMapCache.has(cacheKey)) return _floorMapCache.get(cacheKey);
  const SZ = 1024; // 1K keeps the grain crisp at the zoomed-in camera (512 blurred)
  const octs = [4, 8, 16, 32, 64].map(n => ({ n, grid: _noiseGrid((isLino ? 700 : 300) + n, n) }));
  const base = isLino ? _hex2rgb(tintHex || '#c2cdd8') : [201, 196, 186]; // warm grey stone
  // One global grain treatment for every lino tint (no luminance-dependent
  // boost — Roman: identical settings for all colours incl. Neutral).
  const speckleAmp    = isLino ? 3 : 11; // lino: fewer flecks → more homogeneous
  const roughBase     = isLino ? 0.6 : 0.74;
  const roughVar      = isLino ? 0.05 : 0.16;
  const baseNormalStr = isLino ? 0.35 : 2.4;

  // low-frequency height field (drives subtle colour + roughness variation)
  const H = new Float32Array(SZ * SZ);
  for (let y = 0; y < SZ; y++) for (let x = 0; x < SZ; x++) H[y * SZ + x] = _fbm(octs, x / SZ, y / SZ);

  // Fibre-mottle grain (real linoleum look): dense multi-scale clumps of
  // lighter/darker material, isotropic (no creases). Tileable value noise.
  let G = null;
  if (isLino && grain > 0) {
    const gn = (n) => Math.max(8, Math.min(512, Math.round(n * grainScale)));
    const gOcts = [
      { n: gn(96),  grid: _noiseGrid(2100 + gn(96), gn(96)),  w: 0.45 },
      { n: gn(192), grid: _noiseGrid(2300 + gn(192), gn(192)), w: 0.35 },
      { n: gn(384), grid: _noiseGrid(2500 + gn(384), gn(384)), w: 0.20 },
    ];
    G = new Float32Array(SZ * SZ);
    for (let y = 0; y < SZ; y++) for (let x = 0; x < SZ; x++) {
      let v = 0;
      for (const o of gOcts) v += _sampleNoise(o.grid, o.n, x / SZ, y / SZ) * o.w;
      G[y * SZ + x] = v;
    }
  }

  const mk = () => { const c = document.createElement('canvas'); c.width = c.height = SZ; return c; };
  const colorC = mk(), roughC = mk(), normC = mk();
  const cid = colorC.getContext('2d').createImageData(SZ, SZ);
  const rid = roughC.getContext('2d').createImageData(SZ, SZ);
  const nid = normC.getContext('2d').createImageData(SZ, SZ);
  const atH = (x, y) => H[(((y % SZ) + SZ) % SZ) * SZ + (((x % SZ) + SZ) % SZ)];
  const atG = (x, y) => G[(((y % SZ) + SZ) % SZ) * SZ + (((x % SZ) + SZ) % SZ)];
  const grainRel = grainRelief;
  for (let y = 0; y < SZ; y++) {
    for (let x = 0; x < SZ; x++) {
      const i = y * SZ + x, p = i * 4, h = H[i];
      const sp = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453;
      const fleck = ((sp - Math.floor(sp)) - 0.5) * 2 * speckleAmp;
      let shade = 1 + (h - 0.5) * colorContrast * 2;
      if (G) shade += (G[i] - 0.5) * grain; // fibre mottle on colour
      cid.data[p]     = Math.max(0, Math.min(255, base[0] * shade + fleck));
      cid.data[p + 1] = Math.max(0, Math.min(255, base[1] * shade + fleck));
      cid.data[p + 2] = Math.max(0, Math.min(255, base[2] * shade + fleck));
      cid.data[p + 3] = 255;
      let r = roughBase + (h - 0.5) * roughVar * 2 + (fleck / 255) * 0.1;
      if (G) r += (G[i] - 0.5) * 0.5 * grain; // mottle modulates the sheen
      r = Math.max(0, Math.min(1, r));
      rid.data[p] = rid.data[p + 1] = rid.data[p + 2] = r * 255; rid.data[p + 3] = 255;
      // normal from base height + fibre-grain micro relief
      let dx = (atH(x + 1, y) - atH(x - 1, y)) * baseNormalStr;
      let dy = (atH(x, y + 1) - atH(x, y - 1)) * baseNormalStr;
      if (G) {
        dx += (atG(x + 1, y) - atG(x - 1, y)) * grainRel;
        dy += (atG(x, y + 1) - atG(x, y - 1)) * grainRel;
      }
      const len = Math.hypot(dx, dy, 1);
      nid.data[p]     = (-dx / len * 0.5 + 0.5) * 255;
      nid.data[p + 1] = (-dy / len * 0.5 + 0.5) * 255;
      nid.data[p + 2] = (1 / len * 0.5 + 0.5) * 255;
      nid.data[p + 3] = 255;
    }
  }
  colorC.getContext('2d').putImageData(cid, 0, 0);
  roughC.getContext('2d').putImageData(rid, 0, 0);
  normC.getContext('2d').putImageData(nid, 0, 0);
  const mkTex = (c, srgb) => {
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    return t;
  };
  const maps = { map: mkTex(colorC, true), roughnessMap: mkTex(roughC, false), normalMap: mkTex(normC, false) };
  _floorMapCache.set(cacheKey, maps);
  return maps;
}

// ── Animation / FX helpers ────────────────────────────────────────────────────
function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
// Tileable low-frequency noise normal map — the glaze "orange peel" waviness.
function makePeelNormalTexture() {
  const S = 256, grid = 8; // value-noise lattice (wraps → tileable)
  const lat = [];
  for (let i = 0; i < grid * grid; i++) lat.push(Math.random());
  const val = (x, y) => lat[((y % grid) + grid) % grid * grid + ((x % grid) + grid) % grid];
  const sm = t => t * t * (3 - 2 * t);
  const h = new Float32Array(S * S);
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    const fx = x / S * grid, fy = y / S * grid;
    const ix = Math.floor(fx), iy = Math.floor(fy);
    const tx = sm(fx - ix), ty = sm(fy - iy);
    const a = val(ix, iy), b = val(ix + 1, iy), c2 = val(ix, iy + 1), d = val(ix + 1, iy + 1);
    h[y * S + x] = a + (b - a) * tx + (c2 - a) * ty + (a - b - c2 + d) * tx * ty;
  }
  const c = document.createElement('canvas'); c.width = c.height = S;
  const ctx = c.getContext('2d'), img = ctx.createImageData(S, S);
  for (let y = 0; y < S; y++) for (let x = 0; x < S; x++) {
    const dx = h[y * S + ((x + 1) % S)] - h[y * S + ((x - 1 + S) % S)];
    const dy = h[((y + 1) % S) * S + x] - h[((y - 1 + S) % S) * S + x];
    const i = (y * S + x) * 4;
    img.data[i]     = Math.max(0, Math.min(255, 128 - dx * 255));
    img.data[i + 1] = Math.max(0, Math.min(255, 128 - dy * 255));
    img.data[i + 2] = 255;
    img.data[i + 3] = 255;
  }
  ctx.putImageData(img, 0, 0);
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  return t;
}

// ── Three.js scene ────────────────────────────────────────────────────────────
class ConfigScene {
  constructor(canvas, assets, callbacks) {
    this.canvas = canvas;
    this.assets = assets;
    this.callbacks = callbacks;
    this.gltfs = {};
    this.materials = new Map();
    this.pieceGroups = []; // each entry: { group: Object3D, pieceIndex: number }
    this.hoveredIndex = -1;

    this.scene = new THREE.Scene();
    // Transparent renderer + CSS-painted stage background so the glass
    // backdrop-filter has something to blur over even before the first frame.
    // preserveDrawingBuffer: with render-on-demand we stop drawing when idle, so
    // the buffer must be kept or the canvas can blank out between renders.
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: true });
    this.renderer.setClearColor(0x000000, 0);
    // Anti-aliasing: MSAA (antialias:true) smooths geometry edges cheaply; on top
    // a supersample factor (SCENE_PARAMS.pixelRatio, live-tunable in debug). The
    // jaggies at the object↔shadow contact are the SHADOW-MAP edge — raise the
    // shadow-map size and/or shadow blur for those, not only SSAA.
    this.renderer.setPixelRatio(SCENE_PARAMS.pixelRatio);
    this.renderer.shadowMap.enabled = true;
    // PCFShadowMap (not Soft): PCFSoftShadowMap IGNORES shadow.radius, so the
    // blur control does nothing there. PCFShadowMap honours radius → real,
    // configurable shadow blur.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = TONE_MAPPINGS[SCENE_PARAMS.toneMapping] || THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = SCENE_PARAMS.exposure;

    // Default framing: elevated 3/4 view, zoomed in (users can zoom out in the
    // 3D viewer via the higher maxDistance to see the whole set if they want).
    this._camScale = 1;
    this.camera = new THREE.PerspectiveCamera(SCENE_PARAMS.camFov, 1, 0.01, 100);
    this.camera.position.set(SCENE_PARAMS.camX * this._camScale, SCENE_PARAMS.camY * this._camScale, SCENE_PARAMS.camZ * this._camScale);
    this.camera.lookAt(0, 0, 0);
    this._defaultTarget = new THREE.Vector3(SCENE_PARAMS.camTargetX || 0, SCENE_PARAMS.camTargetY || 0, 0);

    // Studio window environment baked once; reflected in the glaze layer.
    this._pmrem = new THREE.PMREMGenerator(this.renderer);
    this._pmrem.compileEquirectangularShader();
    const we = buildWindowEnvironment(this.renderer, this._pmrem);
    this._windowEnv = we.env; this._windowEnvTex = we.tex;
    this.scene.environment = this._windowEnv;

    // Shared procedural surface textures (one set, used by every material).
    this.surfaceTextures = makeCeramicTextures(this.renderer);

    // Opaque floor (prototype behaviour). It stays white because the light
    // COLOURS are exposed for tuning — set the key light to neutral white and
    // the floor renders white. It receives shadows so pieces drop real shadows
    // onto it; a ShadowMaterial catcher just above adds an opacity-controllable
    // contact shadow. Pieces also receiveShadow → shadows fall onto each other.
    this._flatFloorMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(SCENE_PARAMS.floorColor), roughness: 0.9, metalness: 0.0 });
    this.ground = new THREE.Mesh(new THREE.PlaneGeometry(40, 40), this._flatFloorMat);
    this.ground.rotation.x = -Math.PI / 2;
    this._floorMatCache = {}; // id → built material (procedural + wood)
    this._maxAniso = this.renderer.capabilities.getMaxAnisotropy();
    // Floor does NOT receive shadows — the ShadowMaterial catcher is the single
    // floor-shadow layer. Two receivers (floor + catcher) at slightly different
    // heights produce offset shadows → a dark ring with a bright centre.
    this.ground.receiveShadow = false;
    this.scene.add(this.ground);

    this.shadowCatcher = new THREE.Mesh(
      new THREE.PlaneGeometry(40, 40),
      new THREE.ShadowMaterial({ opacity: SCENE_PARAMS.shadowOpacity }),
    );
    this.shadowCatcher.rotation.x = -Math.PI / 2;
    this.shadowCatcher.position.y = 0.0004; // right under the piece bottoms (~0.0005) for a tight contact shadow
    this.shadowCatcher.receiveShadow = true;
    // Render FIRST among transparent objects: pieces fading in/out (entrance /
    // remove animations) are transparent too, and distance-based sorting can
    // draw them before the catcher — their depth then rejects the shadow, so a
    // ghost piece blends against the unshadowed white floor (white halo where
    // the shadow should be). Fixed order: shadow backdrop, then pieces.
    this.shadowCatcher.renderOrder = -1;
    this.scene.add(this.shadowCatcher);

    this._tweens = [];
    // Peel texture must exist BEFORE the first materials if the toggle defaults
    // on (makeCeramicMaterial reads it at creation time).
    this._peelTex = SCENE_PARAMS.fxPeel ? makePeelNormalTexture() : null;
    if (this._peelTex) this._peelTex.repeat.set(SCENE_PARAMS.fxPeelScale, SCENE_PARAMS.fxPeelScale);

    // Hemi + key + fill + rim (prototype recipe), scaled to our small scene.
    this.hemi = new THREE.HemisphereLight(new THREE.Color(SCENE_PARAMS.hemiSkyColor), new THREE.Color(SCENE_PARAMS.hemiGroundColor), SCENE_PARAMS.hemiIntensity);
    this.scene.add(this.hemi);

    this.key = new THREE.DirectionalLight(new THREE.Color(SCENE_PARAMS.keyColor), SCENE_PARAMS.keyIntensity);
    // Raking angle (like the prototype) so shadows extend sideways and are
    // visible from the top-down camera — a near-overhead light hides the shadow
    // under each piece. normalBias kept tiny: the pieces are only 12 mm thin, so
    // a large normalBias (0.02) offset the shadow sample off the piece entirely.
    this.key.position.set(SCENE_PARAMS.keyX, SCENE_PARAMS.keyY, SCENE_PARAMS.keyZ);
    this.key.castShadow = true;
    this.key.shadow.mapSize.set(SCENE_PARAMS.shadowMapSize, SCENE_PARAMS.shadowMapSize);
    // Tiny biases — the pieces are ~98 mm wide, so a large normalBias offsets the
    // shadow off the contact point and leaves a bright halo (peter-panning). At
    // this scale sub-mm values keep the shadow glued to the object base.
    this.key.shadow.bias = SCENE_PARAMS.shadowBias;
    this.key.shadow.normalBias = SCENE_PARAMS.shadowNormalBias;
    this.key.shadow.radius = SCENE_PARAMS.shadowRadius;
    const sc = this.key.shadow.camera;
    sc.near = 0.1; sc.far = 6;
    sc.left = -0.5; sc.right = 0.5; sc.top = 0.5; sc.bottom = -0.5;
    sc.updateProjectionMatrix();
    this.scene.add(this.key);
    this.scene.add(this.key.target); // ensure the shadow camera aims at origin

    this.fill = new THREE.DirectionalLight(new THREE.Color(SCENE_PARAMS.fillColor), SCENE_PARAMS.fillIntensity);
    this.fill.position.set(-3.0, 1.6, 1.0);
    this.scene.add(this.fill);

    this.rim = new THREE.DirectionalLight(new THREE.Color(SCENE_PARAMS.rimLightColor), SCENE_PARAMS.rimIntensity);
    this.rim.position.set(-1.5, 2.2, -3.0);
    this.scene.add(this.rim);

    // Top-sheen light PAIR — the product-photo trick: two soft point lights
    // flanking the MIRROR position of the default camera (about the up-normal).
    // The pair produces TWO soft washes with a darker seam between them across
    // the flat tops in the STANDARD view — the light/dark structure of Roman's
    // reference photo. Direct specular is the one channel that reliably reads
    // on the tops at flat viewing angles (live-proven 2026-07-22: env-content
    // routes — 3D ceilings, painted bands — arrive washed-out there). The
    // spread is perpendicular to the mirror azimuth; keep it ≤ ~5° as seen
    // from the pieces or the sharp clearcoat lobe misses both lights.
    this.sheenLights = [new THREE.PointLight(new THREE.Color('#fff8ee'), SCENE_PARAMS.sheenLight, 0, 2),
                        new THREE.PointLight(new THREE.Color('#fff8ee'), SCENE_PARAMS.sheenLight, 0, 2)];
    for (const l of this.sheenLights) this.scene.add(l);
    this._placeSheenLights();

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.orbit = null;
    this._defaultCamPos = this.camera.position.clone();
    this._bindPointer();

    this._initPostFX();

    // Render-on-demand: the scene is static (no animation), so we only render
    // after a change — a short burst covers async loads / transitions. This makes
    // the heavy FX pipeline essentially free when idle. Orbit renders live.
    this._renderUntil = 0;
    this.invalidate(1200); // initial burst for env/PMREM + first composition

    this._needsResize = true;
    this._loop = this._loop.bind(this);
    this._loop();
    window.addEventListener('resize', () => { this._needsResize = true; this.invalidate(); });
    // A window 'resize' does NOT fire when only the canvas element changes size
    // (full-bleed CSS applying after first paint, fonts/below-grid reflow, the
    // debug sidebar, container queries…). Without catching those the drawing
    // buffer keeps a stale aspect and the scene renders horizontally stretched
    // on desktop. A ResizeObserver on the canvas covers every case.
    if (typeof ResizeObserver !== 'undefined') {
      this._resizeObs = new ResizeObserver(() => { this._needsResize = true; this.invalidate(); });
      this._resizeObs.observe(this.canvas);
    }
  }

  // Request rendering for the next `ms` milliseconds (covers transitions/loads).
  invalidate(ms = 400) {
    const now = (typeof performance !== 'undefined' ? performance.now() : 0);
    this._renderUntil = Math.max(this._renderUntil || 0, now + ms);
  }

  // ── Tween engine (drives all piece/camera animations) ──────────────────────
  // spec = { dur (ms), delay?, tick(t 0..1), done?() }
  _tween(spec) {
    spec.t0 = (typeof performance !== 'undefined' ? performance.now() : 0) + (spec.delay || 0);
    this._tweens.push(spec);
    this.invalidate((spec.delay || 0) + spec.dur + 250);
  }
  _animTick(now) {
    if (!this._tweens.length) return;
    const keep = [];
    for (const tw of this._tweens) {
      const t = (now - tw.t0) / tw.dur;
      if (t < 0) { keep.push(tw); continue; }
      if (t < 1) { tw.tick(t); keep.push(tw); }
      else { tw.tick(1); if (tw.done) tw.done(); }
    }
    this._tweens = keep;
  }
  // Dispose a ceramic material AND deregister it from the live-GUI registries
  // (they are incremental now — compositions no longer rebuild everything).
  _releaseMat(m) {
    if (!m) return;
    const P = SCENE_PARAMS;
    P._mats = P._mats.filter(x => x !== m);
    if (m.userData && m.userData.pcGlazeU) P._glaze = P._glaze.filter(g => g !== m.userData.pcGlazeU);
    m.dispose();
  }

  // ── Photorealism experiment hooks (debug GUI) ───────────────────────────────
  // Rebuild the whole composer (GTAO / DoF passes are added conditionally).
  rebuildFx() {
    try { this.composer && this.composer.dispose && this.composer.dispose(); } catch (e) {}
    this._initPostFX();
    this._needsResize = true;
    this.invalidate(600);
  }
  applyGtaoLive() {
    const P = SCENE_PARAMS;
    if (!!this._gtao !== !!P.fxGtao) { this.rebuildFx(); return; }
    if (this._gtao) {
      this._gtao.blendIntensity = P.fxGtaoBlend;
      this._gtao.updateGtaoMaterial({
        radius: P.fxGtaoRadius, distanceExponent: 1, thickness: 0.02,
        scale: 1, samples: 16, distanceFallOff: 1, screenSpaceRadius: false,
      });
    }
    this.invalidate();
  }
  // Clearcoat orange-peel normal (glaze waviness) on every live material.
  applyPeelLive() {
    const P = SCENE_PARAMS;
    if (P.fxPeel && !this._peelTex) this._peelTex = makePeelNormalTexture();
    if (this._peelTex) this._peelTex.repeat.set(P.fxPeelScale, P.fxPeelScale);
    for (const m of P._mats) {
      m.clearcoatNormalMap = P.fxPeel ? this._peelTex : null;
      if (m.clearcoatNormalScale) m.clearcoatNormalScale.set(P.fxPeelStrength, P.fxPeelStrength);
      m.needsUpdate = true;
    }
    this.invalidate();
  }

  // ── Post-processing pipeline — one setup for all devices ─────────────────────
  _initPostFX() {
    this.composer = this._bloom = this._grainPass = this._smaa = null;
    const P = SCENE_PARAMS;
    try {
      const rect = this.canvas.getBoundingClientRect();
      const w = Math.max(2, rect.width || 800), h = Math.max(2, rect.height || 500);
      // Cap render DPR (composer MSAA handles edge AA instead of 4× SSAA).
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, P.fxPixelRatioCap));
      const composer = new EffectComposer(this.renderer, new THREE.WebGLRenderTarget(w, h, { samples: P.fxSamples, type: THREE.HalfFloatType }));
      composer.setSize(w, h);
      this._fxRenderPass = new RenderPass(this.scene, this.camera);
      composer.addPass(this._fxRenderPass);
      // GTAO (screen-space ambient occlusion).
      this._gtao = null;
      if (P.fxGtao) {
        this._gtao = new GTAOPass(this.scene, this.camera, w, h);
        this._gtao.output = GTAOPass.OUTPUT.Default;
        this._gtao.blendIntensity = P.fxGtaoBlend;
        this._gtao.updateGtaoMaterial({
          radius: P.fxGtaoRadius, distanceExponent: 1, thickness: 0.02,
          scale: 1, samples: 16, distanceFallOff: 1, screenSpaceRadius: false,
        });
        composer.addPass(this._gtao);
      }
      // Bloom (subtle specular glow)
      this._bloom = new UnrealBloomPass(new THREE.Vector2(w, h), P.fxBloom, P.fxBloomRadius, P.fxBloomThreshold);
      composer.addPass(this._bloom);
      // Static film grain (fixed per-pixel pattern — no per-frame flicker)
      this._grainPass = new ShaderPass({
        uniforms: { tDiffuse: { value: null }, uAmount: { value: P.fxGrain } },
        vertexShader: 'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.,1.);}',
        fragmentShader: 'uniform sampler2D tDiffuse;uniform float uAmount;varying vec2 vUv;' +
          'float h(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}' +
          'void main(){vec4 c=texture2D(tDiffuse,vUv);c.rgb+=(h(vUv)-0.5)*uAmount;gl_FragColor=c;}'
      });
      composer.addPass(this._grainPass);
      composer.addPass(new OutputPass());
      // SMAA — cheap edge anti-aliasing on the final image.
      this._smaa = new SMAAPass(w, h);
      composer.addPass(this._smaa);
      this.composer = composer;
    } catch (e) {
      console.error('PLAYTS post-FX init failed — falling back to direct render', e);
      this.composer = null;
      this.renderer.setPixelRatio(SCENE_PARAMS.pixelRatio);
    }
  }

  enableOrbit() {
    if (this.orbit) return;
    this.orbit = new OrbitControls(this.camera, this.canvas);
    this.orbit.target.copy(this._defaultTarget);
    this.orbit.enablePan = false;
    this.orbit.minDistance = 0.15;
    this.orbit.maxDistance = 4.0; // zoom out far enough to see the whole set on mobile
    this.orbit.maxPolarAngle = Math.PI / 2 - 0.05;
    this.orbit.enableDamping = true;
    this.orbit.dampingFactor = 0.08;
    this.orbit.addEventListener('change', () => this.invalidate(200));
    this.orbit.update();
  }

  disableOrbit() {
    if (!this.orbit) return;
    this.orbit.dispose();
    this.orbit = null;
    this._driftPhase = 0;
    // Reset camera back to the static framing
    this.camera.position.copy(this._defaultCamPos);
    this.camera.lookAt(this._defaultTarget);
    this.invalidate();
  }

  _bindPointer() {
    // Any touch pauses the idle drift for a few seconds.
    this.canvas.addEventListener('pointerdown', () => {
      this._driftPause = (typeof performance !== 'undefined' ? performance.now() : 0) + 4000;
    }, { passive: true });
    const eventXY = (e) => {
      const r = this.canvas.getBoundingClientRect();
      const cx = (e.touches ? e.touches[0].clientX : e.clientX);
      const cy = (e.touches ? e.touches[0].clientY : e.clientY);
      this.pointer.x = ((cx - r.left) / r.width) * 2 - 1;
      this.pointer.y = -((cy - r.top) / r.height) * 2 + 1;
    };

    this.canvas.addEventListener('pointermove', (e) => {
      // No hover highlight in the fullscreen 3D viewer (orbit mode) — it made
      // pieces flash white on mouse-move / tap.
      if (this.orbit) { if (this.hoveredIndex !== -1) { this.hoveredIndex = -1; this._applyHoverOutline(); } return; }
      eventXY(e);
      const idx = this._raycastPieceIndex();
      if (idx !== this.hoveredIndex) {
        this.hoveredIndex = idx;
        this._applyHoverOutline();
        this.canvas.style.cursor = idx >= 0 ? 'pointer' : '';
      }
    });
    this.canvas.addEventListener('pointerleave', () => {
      this.hoveredIndex = -1;
      this._applyHoverOutline();
      this.canvas.style.cursor = '';
    });
    this.canvas.addEventListener('click', (e) => {
      // Fullscreen mode: OrbitControls owns the gesture, no piece-click.
      if (this.orbit) return;
      eventXY(e);
      const idx = this._raycastPieceIndex();
      if (idx >= 0 && this.callbacks?.onPieceClick) this.callbacks.onPieceClick(idx);
      else if (idx < 0 && this.callbacks?.onEmptyClick) this.callbacks.onEmptyClick();
    });
  }

  _raycastPieceIndex() {
    if (!this.pieceGroups.length) return -1;
    this.raycaster.setFromCamera(this.pointer, this.camera);
    const allMeshes = [];
    for (const entry of this.pieceGroups) {
      entry.group.traverse(o => { if (o.isMesh) { o.userData._pcIndex = entry.pieceIndex; allMeshes.push(o); } });
    }
    const hits = this.raycaster.intersectObjects(allMeshes, false);
    if (!hits.length) return -1;
    return hits[0].object.userData._pcIndex ?? -1;
  }

  _applyHoverOutline() {
    for (const entry of this.pieceGroups) {
      entry.group.traverse(o => {
        if (o.isMesh && o.material) {
          if (entry.pieceIndex === this.hoveredIndex) {
            o.material.emissive = new THREE.Color(0xffffff);
            o.material.emissiveIntensity = 0.18; // clearly visible incl. fullscreen
          } else {
            if (o.material.emissive) o.material.emissive.set(0x000000);
            o.material.emissiveIntensity = 0;
          }
        }
      });
    }
    this.invalidate();
  }

  async preloadAssets() {
    const loader = new GLTFLoader();
    // Draco-Dekoder für die komprimierten …-d.glb (12,2 MB → 0,18 MB gesamt).
    // Decoder-Dateien kommen vom selben CDN/Version wie three selbst (importmap).
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://unpkg.com/three@0.170.0/examples/jsm/libs/draco/gltf/');
    loader.setDRACOLoader(draco);
    const load = (url) => new Promise((res, rej) => loader.load(url, res, undefined, rej));
    const [r, o, s] = await Promise.all([
      load(this.assets.round),
      load(this.assets.oval),
      load(this.assets.square),
    ]);
    this.gltfs = { round: r.scene, oval: o.scene, square: s.scene };
    // Bake glaze-edge attributes once on each shared geometry + record the
    // half-thickness so pieces rest exactly on their layer height.
    for (const root of Object.values(this.gltfs)) {
      root.traverse(o => {
        if (o.isMesh) {
          if (!o.geometry.getAttribute('normal')) o.geometry.computeVertexNormals();
          computeEdgeConvexity(o.geometry);
        }
      });
    }
  }

  // Relief-Uniforms live aktualisieren (Debug-GUI). Reine Uniform-Updates → billig,
  // kein Re-Bake. Alle Formen tragen die Uniforms (Kontur je Form ist statisch).
  applyReliefLive() {
    const P = SCENE_PARAMS;
    for (const u of P._glaze) {
      if (!u.uReliefEnabled) continue;
      u.uReliefEnabled.value = P.reliefEnabled ? 1 : 0;
      u.uReliefStrength.value = P.reliefStrength;
      u.uReliefCrownWidth.value = P.reliefCrownWidth;
      u.uReliefCrownH.value = P.reliefCrownHeightMm * 0.001;
      u.uReliefValleyD.value = P.reliefValleyDepthMm * 0.001;
      u.uReliefValleyPos.value = P.reliefValleyPos;
      u.uReliefValleyWidth.value = P.reliefValleyWidth;
      u.uReliefRimFlat.value = P.reliefRimFlat;
    }
    this.invalidate();
  }

  // Build a FRESH glazed ceramic material per piece. We deliberately do NOT
  // clone a cached base: THREE.Material.clone() does not reliably carry over
  // onBeforeCompile, so cloned materials silently lose the speckle + glaze-edge
  // shader. Creating each one fresh guarantees the injection is applied.
  makeCeramicMaterial(color, shape) {
    const P = SCENE_PARAMS;
    const hex = P.colors[color] || '#efe7d6';
    const m = new THREE.MeshPhysicalMaterial({
      color: saturatedColor(hex),
      // Partial metalness = the "mirror ball" lever scaled down: it multiplies
      // the env reflection toward albedo-tinted mirror AND trims the diffuse
      // wash, which is what lets room shapes read on the flat tops at all
      // (Roman's reference photo: hand + blue piece mirrored in red glaze).
      metalness: P.pieceMetalness,
      ior: P.pieceIor,
      roughness: P.roughness,
      clearcoat: P.clearcoat,
      clearcoatRoughness: P.clearcoatRoughness,
      sheen: P.sheen,
      sheenRoughness: P.sheenRoughness,
      sheenColor: new THREE.Color(P.sheenColor),
      envMapIntensity: P.envIntensity,
      normalMap: this.surfaceTextures.normalMap,
      normalScale: new THREE.Vector2(1, 1),
      roughnessMap: this.surfaceTextures.roughnessMap,
      clearcoatNormalMap: (SCENE_PARAMS.fxPeel && this._peelTex) ? this._peelTex : null,
      clearcoatNormalScale: new THREE.Vector2(SCENE_PARAMS.fxPeelStrength, SCENE_PARAMS.fxPeelStrength),
    });
    m.userData.pcColor = color;
    applyGlazeShader(m, hex, shape);
    if (P.probeEnabled && this._probeEnv) { m.envMap = this._probeEnv; m.envMapIntensity = P.envIntensity * P.probeBoost; }
    P._mats.push(m);
    return m;
  }


  // ── Composition with a uid-diffed piece lifecycle ──────────────────────────
  // Instead of rebuild-all: pieces carry a stable uid, so we can tell adds,
  // moves, colour swaps, and removals apart and animate each (all toggleable).
  setComposition(positions) {
    const P = SCENE_PARAMS;
    const reduced = prefersReducedMotion();
    const camIntro = this.introNext === 'all'; this.introNext = null;
    if (this._forceRebuild) { // debug "replay entrance": drop everything first
      for (const e of this.pieceGroups) this._destroyEntry(e);
      this.pieceGroups = [];
      this._forceRebuild = false;
    }
    const animate = P.animPieces && !reduced;
    const hadPieces = this.pieceGroups.length > 0;
    const old = new Map(this.pieceGroups.map(e => [e.uid, e]));
    const next = [], newcomers = [];
    for (const pos of positions) {
      const targetY = pos.y + 0.006 + 0.0005; // half thickness + anti-z-fight
      let e = (pos.uid != null) ? old.get(pos.uid) : null;
      if (e) {
        old.delete(pos.uid);
        e.pieceIndex = pos.pieceIndex;
        if (e.color !== pos.color) this._recolorEntry(e, pos.color, P.animColorFade && !reduced);
        this._moveEntry(e, pos, targetY, animate);
        next.push(e);
      } else {
        e = this._createEntry(pos, targetY);
        if (e) { next.push(e); newcomers.push(e); }
      }
    }
    for (const e of old.values()) this._removeEntry(e, animate);
    this.pieceGroups = next;
    this.hoveredIndex = -1;
    if (newcomers.length && animate) {
      newcomers.sort((a, b) => a.targetY - b.targetY); // ground layer falls first
      this._dropIn(newcomers);
    }
    // Dynamic camera fit: dolly out (tweened) when the composition outgrows the
    // reference radius, back in when it shrinks. Instant on the first fill so
    // the initial framing is correct from frame one.
    let fit = Math.min(P.camFitMax ?? 1.4,
      Math.max(1, compositionRadius(positions) / (P.camFitRef || 0.24)));
    // Visible deco (carafe + glasses) stands outside the piece layouts; the
    // tele frustum frames only the composition, so dolly out far enough that
    // the deco cluster (radius ≈ 0.5 m) is actually inside the frame.
    if (P.probeDeco) fit = Math.max(fit, Math.min(2.2, 0.55 / (P.camFitRef || 0.24)));
    this._applyFitScale(fit, animate && hadPieces);
    if (camIntro && P.animCamIntro && !reduced && !this.orbit && this.pieceGroups.length) this._camIntro();
    this.invalidate(600);
    // Re-bake the reflection probe once the pieces have settled.
    this._scheduleProbe(animate ? (P.animMoveMs || 790) + (P.animColorMs || 380) + 150 : 120);
  }

  _createEntry(pos, targetY) {
    const proto = this.gltfs[pos.shape];
    if (!proto) return null;
    const instance = proto.clone(true); // shares geometry (incl. baked attrs)
    const mat = this.makeCeramicMaterial(pos.color, pos.shape);
    instance.traverse(o => {
      if (o.isMesh) { o.material = mat; o.castShadow = true; o.receiveShadow = true; }
    });
    instance.position.set(pos.x, targetY, pos.z);
    instance.rotation.y = (pos.ry || 0) * Math.PI / 180;
    this.scene.add(instance);
    return { group: instance, uid: pos.uid, pieceIndex: pos.pieceIndex, shape: pos.shape, color: pos.color, targetY };
  }

  _destroyEntry(e) {
    this.scene.remove(e.group);
    const mats = new Set();
    e.group.traverse(o => { if (o.isMesh && o.material) mats.add(o.material); });
    for (const m of mats) this._releaseMat(m);
  }

  _moveEntry(e, pos, targetY, animate) {
    const P = SCENE_PARAMS;
    const ry = (pos.ry || 0) * Math.PI / 180;
    const g = e.group;
    const moved = Math.abs(g.position.x - pos.x) > 1e-5 || Math.abs(g.position.z - pos.z) > 1e-5 ||
                  Math.abs((e.targetY ?? g.position.y) - targetY) > 1e-5 || Math.abs(g.rotation.y - ry) > 1e-4;
    e.targetY = targetY;
    if (!moved) return;
    if (!animate || P.animMoveMs <= 0) {
      g.position.set(pos.x, targetY, pos.z); g.rotation.y = ry;
      return;
    }
    const from = { x: g.position.x, y: g.position.y, z: g.position.z, r: g.rotation.y };
    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // easeInOutCubic
    this._tween({
      dur: P.animMoveMs,
      tick: (t) => {
        const k = ease(t);
        g.position.set(from.x + (pos.x - from.x) * k, from.y + (targetY - from.y) * k, from.z + (pos.z - from.z) * k);
        g.rotation.y = from.r + (ry - from.r) * k;
      },
    });
  }

  _recolorEntry(e, color, fade) {
    const P = SCENE_PARAMS;
    const oldMats = new Set();
    e.group.traverse(o => { if (o.isMesh && o.material) oldMats.add(o.material); });
    const fromColor = saturatedColor(P.colors[e.color] || '#efe7d6');
    const mat = this.makeCeramicMaterial(color, e.shape); // correct glaze/speckle for the new colour
    e.group.traverse(o => { if (o.isMesh) o.material = mat; });
    for (const m of oldMats) this._releaseMat(m);
    e.color = color;
    if (fade && P.animColorMs > 0) {
      const target = mat.color.clone();
      mat.color.copy(fromColor);
      this._tween({
        dur: P.animColorMs,
        tick: (t) => { mat.color.copy(fromColor).lerp(target, t); },
      });
    }
  }

  _removeEntry(e, animate) {
    const P = SCENE_PARAMS;
    if (!animate || P.animRemoveMs <= 0) { this._destroyEntry(e); return; }
    const mats = new Set();
    e.group.traverse(o => { if (o.isMesh && o.material) mats.add(o.material); });
    for (const m of mats) m.transparent = true;
    e.group.traverse(o => { if (o.isMesh) o.castShadow = false; }); // no 100% shadow under a fading ghost
    this._tween({
      dur: P.animRemoveMs,
      tick: (t) => {
        const o = 1 - t;
        for (const m of mats) m.opacity = o;
        e.group.scale.setScalar(1 - 0.08 * t);
      },
      done: () => this._destroyEntry(e),
    });
  }

  // Entrance drop ("materialize"): gravity-honest fall + fade, staggered.
  _dropIn(entries) {
    const P = SCENE_PARAMS;
    const easeIn = t => t * t;
    const easeOut = t => 1 - Math.pow(1 - t, 3);
    entries.forEach((e, i) => {
      const mats = new Set(), meshes = [];
      e.group.traverse(o => { if (o.isMesh) { meshes.push(o); if (o.material) mats.add(o.material); } });
      for (const m of mats) { m.transparent = true; m.opacity = 0; }
      // Shadow maps are binary — a ghost piece would cast a 100%-dark shadow.
      // Keep castShadow off until the piece is fully opaque (t = 0.6).
      for (const o of meshes) o.castShadow = false;
      let shadowOn = false;
      const restY = e.targetY;
      e.group.position.y = restY + P.introDrop;
      e.group.scale.setScalar(0.96);
      this._tween({
        dur: P.introFallTime * 1000,
        delay: i * P.introStagger * 1000,
        tick: (t) => {
          e.group.position.y = restY + P.introDrop * (1 - easeIn(t));
          e.group.scale.setScalar(0.96 + 0.04 * easeOut(t));
          const o = Math.min(1, t / 0.6); // fully opaque at 60% of the fall
          for (const m of mats) m.opacity = o;
          if (!shadowOn && t >= 0.6) { shadowOn = true; for (const o2 of meshes) o2.castShadow = true; }
        },
        done: () => {
          for (const m of mats) { m.opacity = 1; m.transparent = false; }
          for (const o of meshes) o.castShadow = true;
          e.group.position.y = restY;
          e.group.scale.setScalar(1);
        },
      });
    });
  }

  // Camera dolly-in on the initial load. Reads _defaultCamPos live so a
  // mid-intro resize keeps the correct rest framing; orbit takes over anytime.
  _camIntro() {
    const P = SCENE_PARAMS;
    const easeOut = t => 1 - Math.pow(1 - t, 3);
    this._tween({
      dur: P.introCamTime * 1000,
      cam: true, // pauses the idle drift while the camera is scripted
      tick: (t) => {
        if (this.orbit) return;
        const k = easeOut(t);
        this.camera.position.copy(this._defaultCamPos).sub(this._defaultTarget)
          .multiplyScalar(1 + P.introCamPull * (1 - k)).add(this._defaultTarget);
        this.camera.lookAt(this._defaultTarget);
      },
      done: () => {
        if (this.orbit) return;
        this.camera.position.copy(this._defaultCamPos);
        this.camera.lookAt(this._defaultTarget);
      },
    });
  }

  // Dolly the default camera to a new composition-fit factor. Animated as a
  // tween so a re-layout that grows the set glides the camera out in sync with
  // the piece moves; instant on the first composition (nothing to glide from).
  // Composes with _camIntro: this updates the REST position (_defaultCamPos),
  // the intro positions the camera relative to it each tick.
  _applyFitScale(fit, animate) {
    const from = this._fitScale || 1;
    if (Math.abs(fit - from) < 0.001) return;
    const apply = (v) => {
      this._fitScale = v;
      this._camScale = this._aspectCamScale(this.camera.aspect);
      const P = SCENE_PARAMS, k = this._camScale;
      this._defaultCamPos.set(P.camX * k, P.camY * k, P.camZ * k);
      if (this.orbit) return; // fullscreen orbit owns the live camera
      this.camera.position.copy(this._defaultCamPos);
      this.camera.lookAt(this._defaultTarget);
    };
    if (!animate) { apply(fit); this.invalidate(); return; }
    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    this._tween({
      dur: SCENE_PARAMS.animMoveMs || 450,
      tick: (t) => apply(from + (fit - from) * ease(t)),
    });
  }

  // Zoom factor for the static camera as a function of the stage aspect ratio.
  // 1.0 (mobile/square) → camZoomWide (wide desktop), linearly between Lo and Hi.
  // Multiplied by the per-composition fit (_fitScale): sets bigger than
  // camFitRef dolly the camera out proportionally so 6-piece sets stay framed.
  _aspectCamScale(aspect) {
    const P = SCENE_PARAMS;
    const t = Math.min(1, Math.max(0, (aspect - P.camZoomAspectLo) / (P.camZoomAspectHi - P.camZoomAspectLo)));
    return (1 + t * (P.camZoomWide - 1)) * (P.camZoom ?? 1) * (this._fitScale || 1);
  }

  _resizeIfNeeded() {
    if (!this._needsResize) return;
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    this.renderer.setSize(rect.width, rect.height, false);
    this.camera.aspect = rect.width / rect.height;
    // Re-dolly the static camera for the new aspect (fullscreen/orbit owns its
    // own distance, so skip while orbiting).
    if (!this.orbit) {
      this._camScale = this._aspectCamScale(this.camera.aspect);
      const P = SCENE_PARAMS, k = this._camScale;
      this.camera.position.set(P.camX * k, P.camY * k, P.camZ * k);
      this.camera.lookAt(this._defaultTarget);
      this._defaultCamPos.copy(this.camera.position);
    }
    this.camera.updateProjectionMatrix();
    if (this.composer) {
      // composer.setSize resizes EVERY pass to CSS×pixelRatio (device) resolution.
      // Do NOT call pass.setSize() manually with CSS pixels afterward — that
      // downsizes SSR/GTAO/SMAA to 1× and makes the whole image pixelated.
      this.composer.setSize(rect.width, rect.height);
    }
    this._needsResize = false;
    this.invalidate();
  }

  _loop() {
    requestAnimationFrame(this._loop);
    this._resizeIfNeeded();
    // Render-on-demand: draw while orbiting (live) or during an invalidate burst;
    // otherwise skip the (heavy) frame so the idle scene costs almost nothing. A
    // slow heartbeat (every 2 s) still repaints so the canvas can never be left
    // blank/stale if a burst frame was ever missed (e.g. a throttled tab).
    const P = SCENE_PARAMS;
    const now = (typeof performance !== 'undefined' ? performance.now() : 0);
    // Idle drift (debug toggle): gentle yaw sway — keeps the frame alive while on.
    const drifting = P.animIdleDrift && this.pieceGroups.length > 0 && !this.orbit && !prefersReducedMotion() && now > (this._driftPause || 0); // empty set: static background, no idle sway
    if (drifting) this._renderUntil = Math.max(this._renderUntil || 0, now + 50);
    const idle = !this.orbit && !this._debugAlwaysRender && now > (this._renderUntil || 0);
    if (idle && now - (this._lastRenderT || 0) < 2000) return;
    this._lastRenderT = now;
    this._animTick(now);
    // Piece tweens (add/move/remove) do NOT touch the camera — drift continues
    // through them; only camera tweens (intro) pause it. The phase is integrated
    // per rendered frame (dt-capped), so pauses/orbit never cause a phase jump —
    // the sway always resumes exactly where it stopped. (The old absolute-time
    // sine snapped the camera after every add-item animation.)
    if (drifting && !this._tweens.some(tw => tw.cam)) {
      const dt = Math.min(100, now - (this._driftLastT || now));
      this._driftPhase = (this._driftPhase || 0) + (dt / (P.idleDriftPeriod * 1000)) * Math.PI * 2;
      const a = Math.sin(this._driftPhase) * P.idleDriftDeg * Math.PI / 180;
      const p = this._defaultCamPos, t = this._defaultTarget;
      const dx = p.x - t.x, dz = p.z - t.z, ca = Math.cos(a), sa = Math.sin(a);
      this.camera.position.set(t.x + dx * ca - dz * sa, p.y, t.z + dx * sa + dz * ca);
      this.camera.lookAt(t);
    }
    this._driftLastT = now;
    if (this.orbit) this.orbit.update();
    // Composer (bloom + grain + SMAA + GTAO) runs everywhere.
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
  }

  // ── Live update hooks used by the debug GUI ─────────────────────────────────
  applyMaterialLive() {
    const P = SCENE_PARAMS;
    for (const m of P._mats) {
      m.roughness = P.roughness;
      m.sheen = P.sheen;
      m.sheenRoughness = P.sheenRoughness;
      m.sheenColor.set(P.sheenColor);
      m.clearcoat = P.clearcoat;
      m.clearcoatRoughness = P.clearcoatRoughness;
      m.ior = P.pieceIor;
      m.metalness = P.pieceMetalness;
      m.envMapIntensity = P.envIntensity * ((P.probeEnabled && this._probeEnv) ? P.probeBoost : 1);
      m.needsUpdate = true;
    }
  }
  applyColorsLive() {
    const P = SCENE_PARAMS;
    for (const m of P._mats) {
      const hex = P.colors[m.userData.pcColor];
      if (hex) m.color.copy(saturatedColor(hex));
    }
    // Speckle colour derives from base — refresh registered uniforms too.
    for (let i = 0; i < P._mats.length; i++) {
      const m = P._mats[i], u = P._glaze[i];
      if (u && m) u.uSpeckleColor.value.copy(deriveSpeckleColor(P.colors[m.userData.pcColor] || '#efe7d6'));
    }
  }
  applyGlazeLive() {
    const P = SCENE_PARAMS;
    for (const u of P._glaze) {
      u.uRimStrength.value = P.rimStrength;
      u.uEdgeCenter.value = P.edgeCenter;
      u.uEdgeTight.value = P.edgeTight;
      u.uUpBias.value = P.upBias;
      u.uRimColor.value.set(P.rimColor);
      u.uSpeckleAmount.value = P.speckleAmount;
      u.uSpeckleScale.value = P.speckleScale;
      u.uSpeckleThresh.value = 0.85 - 0.55 * P.speckleDensity;
      u.uEnvShadow.value = P.envShadowDim;
    }
  }
  rebuildTextures() {
    const old = this.surfaceTextures;
    this.surfaceTextures = makeCeramicTextures(this.renderer);
    for (const m of SCENE_PARAMS._mats) {
      m.normalMap = this.surfaceTextures.normalMap;
      m.roughnessMap = this.surfaceTextures.roughnessMap;
      m.needsUpdate = true;
    }
    if (old) { old.normalMap.dispose(); old.roughnessMap.dispose(); }
  }
  // ── Scene reflection probe ──────────────────────────────────────────────
  // One shared CubeCamera at the composition centre; the result is PMREM'd and
  // set as every glaze material's envMap, so pieces reflect the actual floor,
  // room and neighbour pieces. Re-baked (debounced) only on composition/floor/
  // env changes — zero per-frame cost. Toggle + strength in the debug GUI.
  _scheduleProbe(delay = 150) {
    if (!SCENE_PARAMS.probeEnabled) return;
    clearTimeout(this._probeTimer);
    this._probeTimer = setTimeout(() => this._bakeSceneEnv(), delay);
  }
  // Cube RT → equirect, on the GPU. Direction math verified 2026-07-22 by
  // rendering a v-coordinate colour code through the whole PMREM+sampler chain:
  // +sin(el) stores world-UP at v=1, which is exactly where the material env
  // sampler reads for up-facing surfaces. (A −sin(el) variant put the FLOOR
  // into the tops; fromCubemap without this conversion did the same.) Any
  // change here MUST be re-verified with a metalness-1 piece: the tops must
  // mirror the striped light ceiling, not a flat dark tone.
  _cubeToEquirect() {
    if (!this._c2eRT) {
      this._c2eMat = new THREE.ShaderMaterial({
        uniforms: { uCube: { value: null } },
        vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
        fragmentShader: [
          'precision highp float; uniform samplerCube uCube; varying vec2 vUv;',
          'void main(){',
          '  float az = (vUv.x - 0.5) * 6.2831853;',
          '  float el = (vUv.y - 0.5) * 3.14159265;',
          '  vec3 dir = vec3(cos(el) * cos(az), sin(el), cos(el) * sin(az));',
          '  gl_FragColor = vec4(textureCube(uCube, dir).rgb, 1.0);',
          '}',
        ].join('\n'),
        depthTest: false, depthWrite: false,
      });
      this._c2eScene = new THREE.Scene();
      this._c2eScene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this._c2eMat));
      this._c2eCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
      this._c2eRT = new THREE.WebGLRenderTarget(768, 384, {
        type: this._probeByteFallback ? THREE.UnsignedByteType : THREE.HalfFloatType,
      });
      this._c2eRT.texture.mapping = THREE.EquirectangularReflectionMapping;
    }
    this._c2eMat.uniforms.uCube.value = this._probeRT.texture;
    const prev = this.renderer.getRenderTarget();
    this.renderer.setRenderTarget(this._c2eRT);
    this.renderer.render(this._c2eScene, this._c2eCam);
    this.renderer.setRenderTarget(prev);
    return this._c2eRT.texture;
  }
  // Real furniture stand-ins (boxes) that exist ONLY for the reflection probe:
  // invisible for the screen render (zero impact on the visible scene, picking
  // or shadows), shown just for the 6 probe faces — so the pieces reflect
  // actual parallax-correct objects instead of painted env bars. Placed in the
  // +x/−z quadrant that the flat piece tops visibly mirror toward the camera.
  // SPLIT into two independently toggleable sub-groups (Roman 2026-07-22: the
  // combined "Möbel + Lichtdecke" switch did too much at once): _probeFurnBoxes
  // (dark furniture volumes) and _probeCeilGroup (striped light ceiling), each
  // driven by its own param in _bakeSceneEnv.
  _ensureProbeFurniture() {
    if (this._probeFurnGroup) return this._probeFurnGroup;
    const g = new THREE.Group();
    const boxes = new THREE.Group();
    const add = (x, z, w, h, d, color, ry = 0, glow = 0.4) => {
      // GRADUATED self-lit floor per object (dark shapes stay DARK, lit
      // surfaces BRIGHT): the uniform 1.15 boost from the Fresnel-formula era
      // killed all radiance contrast — everything glowed alike and no shape
      // could read in the tops (Roman: "alles leuchtet, aber keine Formen").
      // High contrast between neighbours is what survives the reflection path
      // (live-QA'd 2026-07-22).
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(w, h, d),
        new THREE.MeshStandardMaterial({ color, roughness: 0.92,
          emissive: new THREE.Color(color), emissiveIntensity: glow })
      );
      m.position.set(x, h / 2, z);
      m.rotation.y = ry;
      boxes.add(m);
    };
    add(2.6, -0.9, 1.2, 2.3, 0.5, '#3a3733', 0, 0.3);      // wardrobe — dark, tall
    add(-2.3, -2.1, 2.3, 0.85, 0.45, '#7a746c', 0, 0.55);  // sideboard — mid grey
    add(-2.9, 1.1, 1.0, 2.2, 0.4, '#4a453f', 0.4, 0.4);    // shelf silhouette, camera-left
    add(0.85, -0.75, 0.55, 1.75, 0.35, '#453626', 2.29, 0.25);   // dark shelf column
    g.add(boxes);
    this._probeFurnBoxes = boxes;
    // CLEANED UP 2026-07-22 (Roman: "räume die settings auf"): the art wall,
    // paned window front, lit stele and pendant lamp from the pre-orientation-
    // fix era were removed — they pumped a lot of light into the probe env
    // (toggling the furniture checkbox visibly brightened the whole scene) and
    // none of them ever read on the flat tops at the default camera. Top
    // reflections in the STANDARD view come from the sheen-light pair (direct
    // specular); the striped ceiling below covers steep/orbit views.
    // STRIPED light ceiling — COPLANAR bright strips alternating with
    // near-black fillers. Coarse pitch 2.8 m: oblique views foreshorten the
    // pattern (a 1.25 m pitch shrank to ~14° at 44° incidence and blurred
    // away); 2.8 m stays ≥25° at every usable angle. Must stay COPLANAR
    // (hanging grids self-occlude at oblique angles) and GEOMETRY (emissiveMap
    // textures failed silently in the probe).
    const ceil = new THREE.Group();
    for (let i = -2; i <= 2; i++) {
      const strip = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 14),
        new THREE.MeshStandardMaterial({ color: '#000000',
          emissive: new THREE.Color('#fff6e6'), emissiveIntensity: 5,
          side: THREE.DoubleSide }));
      strip.rotation.x = Math.PI / 2;
      strip.position.set(i * 2.8, 2.6, 0);
      ceil.add(strip);
      const gap = new THREE.Mesh(new THREE.PlaneGeometry(1.4, 14),
        new THREE.MeshStandardMaterial({ color: '#070605', roughness: 0.95,
          side: THREE.DoubleSide }));
      gap.rotation.x = Math.PI / 2;
      gap.position.set(i * 2.8 + 1.4, 2.6, 0);
      ceil.add(gap);
    }
    ceil.rotation.y = 47.5 * Math.PI / 180;
    g.add(ceil);
    this._probeCeilGroup = ceil;
    g.visible = false;
    this.scene.add(g);
    this._probeFurnGroup = g;
    return g;
  }
  // Tableware deco (carafe + two glasses) right next to the set — close, so it
  // reads LARGE in the glaze reflections. Probe-only, like the furniture.
  _ensureProbeDeco() {
    if (this._probeDecoGroup) return this._probeDecoGroup;
    const g = new THREE.Group();
    // Dark glass — pale tableware vanished against the pale room; dark glossy
    // glass survives the orange-peel blur (live-tuned 2026-07-21).
    const cyl = (rTop, rBot, h, x, z, color, y) => {
      const m = new THREE.Mesh(new THREE.CylinderGeometry(rTop, rBot, h, 32),
        new THREE.MeshStandardMaterial({ color, roughness: 0.06, metalness: 0 }));
      m.position.set(x, y ?? h / 2, z);
      m.castShadow = true;
      g.add(m);
    };
    // Positions sit OUTSIDE the largest piece layouts (~0.4 m radius) so the
    // visible deco never collides with a 6-piece set.
    cyl(0.05, 0.062, 0.2, 0.3, -0.2, '#2e4238');            // carafe body — green glass
    cyl(0.017, 0.026, 0.075, 0.3, -0.2, '#2e4238', 0.235);  // carafe neck
    cyl(0.03, 0.027, 0.082, 0.18, -0.3, '#7a5c30');         // glass — amber
    cyl(0.03, 0.027, 0.082, 0.4, -0.08, '#3a3a40');         // glass — smoke
    g.visible = !!SCENE_PARAMS.probeDeco;
    this.scene.add(g);
    this._probeDecoGroup = g;
    return g;
  }
  _bakeSceneEnv() {
    const P = SCENE_PARAMS;
    if (!P.probeEnabled) return;
    try {
      const res = +P.probeRes || 256;
      if (this._probeRT && this._probeRT.width !== res) {
        this._probeRT.dispose(); this._probeRT = null; this._probeCam = null;
      }
      if (!this._probeRT) {
        // HalfFloat: the window is HDR (radiance ~6) — an 8-bit target clamps
        // it to 1.0 and the window streak drowns in the room ambience.
        this._probeRT = new THREE.WebGLCubeRenderTarget(res, { type: this._probeByteFallback ? THREE.UnsignedByteType : THREE.HalfFloatType });
        this._probeCam = new THREE.CubeCamera(0.02, 30, this._probeRT);
        this.scene.add(this._probeCam);
      }
      // Probe at the composition centre, just above the pieces.
      let cx = 0, cz = 0;
      if (this.pieceGroups.length) {
        for (const e of this.pieceGroups) { cx += e.group.position.x; cz += e.group.position.z; }
        cx /= this.pieceGroups.length; cz /= this.pieceGroups.length;
      }
      this._probeCam.position.set(cx, 0.06, cz);
      // Bake linear — tone mapping belongs to the screen pass only. The window
      // env is shown as the scene BACKGROUND during the bake: windows/room only
      // exist in that texture (not as geometry), so without this the probe saw
      // a plain background colour and the pieces lost their window reflections.
      if (P.probeFurniture || P.probeCeiling) {
        const fg = this._ensureProbeFurniture();
        fg.visible = true;
        this._probeFurnBoxes.visible = !!P.probeFurniture;
        this._probeCeilGroup.visible = !!P.probeCeiling;
        // Probe-only fill light at the scene centre: lights the faces the
        // pieces actually mirror, giving the reflections a natural
        // bright-to-dark gradient instead of flat emissive colour.
        if (!this._probeLight) {
          this._probeLight = new THREE.PointLight('#fffdf5', 5, 0, 2);
          this._probeLight.position.set(0, 1.1, 0);
          this._probeLight.visible = false;
          this.scene.add(this._probeLight);
        }
        this._probeLight.visible = !!P.probeFurniture;
      }
      // deco (carafe + glasses) is a real VISIBLE scene object — no toggling
      const tm = this.renderer.toneMapping;
      const bg = this.scene.background;
      this.renderer.toneMapping = THREE.NoToneMapping;
      // Raw equirect texture, NOT the PMREM env: re-photographing the filtered
      // CubeUV background blurred the whole room into structureless cream.
      this.scene.background = this._windowEnvTex || this._windowEnv;
      this._probeCam.update(this.renderer, this.scene);
      this.scene.background = bg;
      this.renderer.toneMapping = tm;
      if (this._probeFurnGroup) this._probeFurnGroup.visible = false;
      if (this._probeLight) this._probeLight.visible = false;
      // PMREM the equirect with the SHARED generator (this._pmrem) — the same
      // instance that builds the window env. A second, lazily created
      // PMREMGenerator produced a broken CubeUV on this three build (mirror
      // tops rendered dark although the equirect rows held the content; the
      // shared instance, which had compileEquirectangularShader() called at
      // init, renders the identical input correctly — A/B-proven 2026-07-22).
      const rt = this._pmrem.fromEquirectangular(this._cubeToEquirect());
      const oldRT = this._probeEnvRT;
      this._probeEnvRT = rt; this._probeEnv = rt.texture;
      this._applyProbeToMats();
      if (oldRT) oldRT.dispose();
      P.probeStatus = 'OK · ' + res + 'px ' + (this._probeByteFallback ? '8bit' : 'HDR') +
        ' · ' + P._mats.length + ' Mat · Deko:' + (P.probeDeco ? 'an' : 'aus') +
        ' Möbel:' + (P.probeFurniture ? 'an' : 'aus') +
        ' Decke:' + (P.probeCeiling ? 'an' : 'aus');
      console.info('[PLAYTS probe]', P.probeStatus);
    } catch (err) {
      if (!this._probeByteFallback) {
        // HalfFloat cube targets can fail on some GPUs/browsers — retry once
        // in 8-bit (weaker window contrast) instead of killing the feature.
        console.warn('PLAYTS probe: HalfFloat bake failed — retrying 8-bit', err);
        this._probeByteFallback = true;
        if (this._probeRT) { this._probeRT.dispose(); this._probeRT = null; this._probeCam = null; }
        if (this._c2eRT) { this._c2eRT.dispose(); this._c2eRT = null; }
        this._bakeSceneEnv();
        return;
      }
      console.error('PLAYTS probe: bake failed — disabling', err);
      SCENE_PARAMS.probeEnabled = false; this._probeEnv = null;
      SCENE_PARAMS.probeStatus = 'FEHLER: ' + (err && err.message || err);
      this._applyProbeToMats();
    }
  }
  // Point every glaze material at the probe env (or back at the window env).
  _applyProbeToMats() {
    const P = SCENE_PARAMS;
    const on = !!(P.probeEnabled && this._probeEnv);
    for (const m of P._mats) {
      m.envMap = on ? this._probeEnv : null;
      m.envMapIntensity = P.envIntensity * (on ? P.probeBoost : 1);
      m.needsUpdate = true;
    }
    this.invalidate();
  }
  rebuildEnv() {
    const old = this._windowEnv, oldTex = this._windowEnvTex;
    const we = buildWindowEnvironment(this.renderer, this._pmrem);
    this._windowEnv = we.env; this._windowEnvTex = we.tex;
    this.scene.environment = this._windowEnv;
    if (old && old !== this.scene.environment) old.dispose();
    if (oldTex && oldTex !== we.tex) oldTex.dispose();
    this._scheduleProbe(150);
    this.invalidate();
  }
  // Sheen pair placement: base point = 3.4 m out along the default camera's
  // mirror direction (+0.65, +0.95, −0.71 normalized), spread ± sheenSpread
  // horizontally, perpendicular to the mirror azimuth (~137°).
  _placeSheenLights() {
    const P = SCENE_PARAMS;
    if (!this.sheenLights) return;
    const L = Math.hypot(0.65, 0.95, 0.71);
    const bx = 0.65 / L * 3.4, by = 0.95 / L * 3.4, bz = -0.71 / L * 3.4;
    const sp = P.sheenSpread ?? 0.28;
    this.sheenLights[0].position.set(bx - sp * 0.73, by, bz - sp * 0.68);
    this.sheenLights[1].position.set(bx + sp * 0.73, by, bz + sp * 0.68);
  }
  applyLightsLive() {
    const P = SCENE_PARAMS;
    this.hemi.intensity = P.hemiIntensity;
    this.hemi.color.set(P.hemiSkyColor);
    this.hemi.groundColor.set(P.hemiGroundColor);
    this.key.intensity = P.keyIntensity;
    this.key.color.set(P.keyColor);
    this.fill.intensity = P.fillIntensity;
    this.fill.color.set(P.fillColor);
    this.rim.intensity = P.rimIntensity;
    if (this.sheenLights) {
      for (const l of this.sheenLights) l.intensity = P.sheenLight;
      this._placeSheenLights();
    }
    this.rim.color.set(P.rimLightColor);
    this.key.position.set(P.keyX, P.keyY, P.keyZ);
    this.key.shadow.bias = P.shadowBias;
    this.key.shadow.normalBias = P.shadowNormalBias;
    this.key.shadow.radius = P.shadowRadius;
    this.renderer.shadowMap.needsUpdate = true; // re-render map so bias/blur tweaks apply live
  }
  applyPixelRatioLive() {
    // When the FX composer is active, keep the DPR capped (SSAA is replaced by
    // composer MSAA); otherwise use the full SSAA pixel ratio.
    const dpr = this.composer ? Math.min(window.devicePixelRatio || 1, SCENE_PARAMS.fxPixelRatioCap) : SCENE_PARAMS.pixelRatio;
    this.renderer.setPixelRatio(dpr);
    this._needsResize = true;
  }
  applyFxLive() {
    const P = SCENE_PARAMS;
    if (this._grainPass) { this._grainPass.uniforms.uAmount.value = P.fxGrain; }
    if (this._bloom) { this._bloom.strength = P.fxBloom; this._bloom.radius = P.fxBloomRadius; this._bloom.threshold = P.fxBloomThreshold; }
    this.invalidate();
  }
  applyShadowMapSizeLive() {
    const s = SCENE_PARAMS.shadowMapSize;
    this.key.shadow.mapSize.set(s, s);
    if (this.key.shadow.map) { this.key.shadow.map.dispose(); this.key.shadow.map = null; }
    this.renderer.shadowMap.needsUpdate = true;
  }
  applySceneLive() {
    const P = SCENE_PARAMS;
    this.renderer.toneMapping = TONE_MAPPINGS[P.toneMapping] || THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = P.exposure;
    this.shadowCatcher.material.opacity = P.shadowOpacity;
    // tone mapping change requires material recompile
    for (const m of P._mats) m.needsUpdate = true;
    this.stageBgCb && this.stageBgCb(P.background);
  }

  // ── Floor switching ─────────────────────────────────────────────────────────
  _tuneFloorTex(tex, repeat) {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(repeat, repeat);
    tex.anisotropy = this._maxAniso;
    tex.needsUpdate = true;
    return tex;
  }
  // Texture scale (repeat) for a floor id — live-tunable via SCENE_PARAMS.
  _floorScale(id) {
    if ((FLOORS[id] || {}).proc === 'lino') return SCENE_PARAMS.floorLinoScale; // 'linoleum' AND 'white' (Neutral)
    // Convention: every tex floor has a SCENE_PARAMS.floor<Id>Scale key.
    const key = 'floor' + id.charAt(0).toUpperCase() + id.slice(1) + 'Scale';
    return SCENE_PARAMS[key] ?? 1;
  }
  _linoTint(colorId) { return SCENE_PARAMS.floorLinoColors[colorId || SCENE_PARAMS.floorLinoColor] || '#c2cdd8'; }
  _buildProcFloor(id) {
    const F = FLOORS[id];
    const isLino = F.proc === 'lino';               // 'linoleum' AND 'white' (Neutral = grey lino)
    const linoColor = F.lino || SCENE_PARAMS.floorLinoColor;
    const tint = isLino ? this._linoTint(linoColor) : '';
    const opts = isLino ? { colorContrast: SCENE_PARAMS.floorLinoContrast, grain: SCENE_PARAMS.floorLinoGrain, grainScale: SCENE_PARAMS.floorLinoGrainScale, grainRelief: SCENE_PARAMS.floorLinoGrainRelief } : undefined;
    // Cache per appearance so a colour/homogeneity/grain change makes a new mat.
    const cacheKey = isLino
      ? `linoleum|${id}|${linoColor}|${tint}|${SCENE_PARAMS.floorLinoContrast}|${SCENE_PARAMS.floorLinoGrain}|${SCENE_PARAMS.floorLinoGrainScale}|${SCENE_PARAMS.floorLinoGrainRelief}`
      : id + '|' + tint;
    if (this._floorMatCache[cacheKey]) return this._floorMatCache[cacheKey];
    const maps = makeFloorMaps(F.proc, tint, opts);
    const rep = this._floorScale(id);
    // Fresh texture instances (share the canvas image) so repeat/anisotropy are
    // independent of the cached generator output.
    const clone = (t, srgb) => { const c = t.clone(); if (srgb) c.colorSpace = THREE.SRGBColorSpace; this._tuneFloorTex(c, rep); return c; };
    const mat = new THREE.MeshPhysicalMaterial({
      map: clone(maps.map, true),
      roughnessMap: clone(maps.roughnessMap, false),
      normalMap: clone(maps.normalMap, false),
      normalScale: new THREE.Vector2(isLino ? 0.9 : 0.6, isLino ? 0.9 : 0.6),
      roughness: 1.0, metalness: 0.0,
      clearcoat: F.proc === 'lino' ? SCENE_PARAMS.floorLinoGloss : 0.0,
      clearcoatRoughness: SCENE_PARAMS.floorLinoGlossRough,
      envMapIntensity: 0.5,
    });
    this._floorMatCache[cacheKey] = mat;
    return mat;
  }
  // Lazy-load a photo-texture floor (oak wood / stone concrete). Per-floor
  // material knobs live in FLOORS[id].mat; contrast remaps the albedo around
  // its average colour so grain can be flattened without shifting the tone.
  _loadTexFloor(id) {
    if (this._floorMatCache[id]) return Promise.resolve(this._floorMatCache[id]);
    const F = FLOORS[id];
    const a = this.assets[F.assets];
    if (!a || !a.color) return Promise.reject(new Error('no texture assets for ' + id));
    const loader = new THREE.TextureLoader();
    const load = (url) => new Promise((res, rej) => loader.load(url, res, undefined, rej));
    // Optional 4th map: metalness (steel). Dielectric floors omit it.
    const urls = [load(a.color), F.mat.matteBase ? Promise.resolve(null) : load(a.rough), load(a.normal)];
    if (a.metal) urls.push(load(a.metal));
    return Promise.all(urls).then(([color, rough, normal, metal]) => {
      const rep = this._floorScale(id);
      color.colorSpace = THREE.SRGBColorSpace;
      const maps = [color, rough, normal];
      if (metal) maps.push(metal);
      maps.forEach(t => { if (t) this._tuneFloorTex(t, rep); });
      // Floors that name a *Param take their value from SCENE_PARAMS so the
      // debug GUI can drive them live (see applyFloorMatLive); the others keep
      // their static mat default.
      const lever = (p, dflt) => (p && SCENE_PARAMS[p] != null) ? SCENE_PARAMS[p] : dflt;
      const nrm = lever(F.mat.weaveParam, F.mat.normalScale);
      const mat = new THREE.MeshPhysicalMaterial({
        map: color, roughnessMap: F.mat.matteBase ? null : rough, normalMap: normal,
        metalnessMap: metal || null,
        normalScale: new THREE.Vector2(nrm, nrm),
        // Multiplies the roughness map. <1 sharpens the reflection (steel);
        // the dielectric floors keep the map as-authored.
        roughness: F.mat.matteBase ? 1.0 : lever(F.mat.roughParam, F.mat.roughness ?? 1.0),
        metalness: F.mat.metalness ?? 0.0,
        clearcoat: SCENE_PARAMS[F.gloss.amt] ?? 0.1,
        clearcoatRoughness: SCENE_PARAMS[F.gloss.rough] ?? 0.5,
        // clearcoat follows the surface relief so the gloss reflection has depth
        clearcoatNormalMap: F.mat.matteBase ? normal : null,
        clearcoatNormalScale: new THREE.Vector2(0.5, 0.5),
        envMapIntensity: lever(F.mat.envParam, F.mat.envMapIntensity),
        // Fabric sheen (0 elsewhere → no cost/effect on the other floors).
        sheen: lever(F.mat.sheenParam, F.mat.sheen ?? 0),
        sheenRoughness: F.mat.sheenRoughness ?? 1,
        sheenColor: new THREE.Color(F.mat.sheenColor || '#ffffff'),
      });
      mat.userData.woodContrast = { value: SCENE_PARAMS[F.contrastParam] ?? 1 };
      mat.userData.woodPivot = { value: this._avgColor(color.image) };
      // Colour grading (debug GUI): brightness ×, warm/cold shift, tint multiply.
      mat.userData.floorBright = { value: SCENE_PARAMS[F.grade.bright] ?? 1 };
      mat.userData.floorWarm = { value: SCENE_PARAMS[F.grade.warm] ?? 0 };
      mat.userData.floorTint = { value: new THREE.Color(SCENE_PARAMS[F.grade.tint] || '#ffffff') };
      mat.onBeforeCompile = (shader) => {
        shader.uniforms.uWoodContrast = mat.userData.woodContrast;
        shader.uniforms.uWoodPivot = mat.userData.woodPivot;
        shader.uniforms.uFloorBright = mat.userData.floorBright;
        shader.uniforms.uFloorWarm = mat.userData.floorWarm;
        shader.uniforms.uFloorTint = mat.userData.floorTint;
        shader.fragmentShader = shader.fragmentShader
          .replace('void main() {', 'uniform float uWoodContrast;\nuniform vec3 uWoodPivot;\nuniform float uFloorBright;\nuniform float uFloorWarm;\nuniform vec3 uFloorTint;\nvoid main() {')
          .replace('#include <map_fragment>', `#include <map_fragment>
\tdiffuseColor.rgb = mix( uWoodPivot, diffuseColor.rgb, uWoodContrast );
\tdiffuseColor.rgb *= uFloorTint * uFloorBright;
\tdiffuseColor.rgb *= vec3( 1.0 + uFloorWarm * 0.18, 1.0 + uFloorWarm * 0.04, 1.0 - uFloorWarm * 0.18 );`);
      };
      this._floorMatCache[id] = mat;
      return mat;
    });
  }
  // Average colour of a loaded image as a linear THREE.Color (wood contrast pivot).
  _avgColor(img) {
    const c = document.createElement('canvas'); c.width = c.height = 1;
    const ctx = c.getContext('2d'); ctx.drawImage(img, 0, 0, 1, 1);
    const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
    return new THREE.Color().setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
  }
  // Live: re-apply the active floor's texture scale (debug GUI).
  applyFloorScaleLive() {
    const m = this.ground.material, rep = this._floorScale(SCENE_PARAMS.floor);
    if (!m) return;
    [m.map, m.roughnessMap, m.normalMap, m.metalnessMap].forEach(t => { if (t) { t.repeat.set(rep, rep); t.needsUpdate = true; } });
  }
  // Live: wood texture contrast (debug GUI) — mutates the shared uniform.
  // Data-driven over FLOORS (was hard-coded to oak/stone, so newer texture
  // floors like steel/cloth silently ignored their own params).
  applyFloorContrastLive() {
    for (const [id, F] of Object.entries(FLOORS)) {
      if (F.kind !== 'tex' || !F.contrastParam) continue;
      const m = this._floorMatCache[id];
      if (m && m.userData.woodContrast) m.userData.woodContrast.value = SCENE_PARAMS[F.contrastParam] ?? 1;
    }
    this.invalidate();
  }
  // Live: floor glossiness (clearcoat reflections) on every cached floor mat.
  // Material levers that aren't grading: steel env/roughness, cloth weave/sheen.
  applyFloorMatLive() {
    for (const [id, F] of Object.entries(FLOORS)) {
      const m = this._floorMatCache[id];
      if (!m || !F.mat) continue;
      if (F.mat.envParam) m.envMapIntensity = SCENE_PARAMS[F.mat.envParam];
      if (F.mat.roughParam) m.roughness = SCENE_PARAMS[F.mat.roughParam];
      if (F.mat.sheenParam) m.sheen = SCENE_PARAMS[F.mat.sheenParam];
      if (F.mat.weaveParam) {
        const n = SCENE_PARAMS[F.mat.weaveParam];
        m.normalScale.set(n, n);
      }
      m.needsUpdate = true;
    }
    this.invalidate();
  }
  applyFloorGlossLive() {
    const P = SCENE_PARAMS;
    for (const [k, m] of Object.entries(this._floorMatCache)) {
      const F = FLOORS[k];
      let amt, rgh;
      if (F && F.kind === 'tex' && F.gloss) { amt = P[F.gloss.amt]; rgh = P[F.gloss.rough]; }
      else { amt = P.floorLinoGloss; rgh = P.floorLinoGlossRough; } // lino keys incl. Neutral
      m.clearcoat = amt; m.clearcoatRoughness = rgh; m.needsUpdate = true;
    }
    this.invalidate();
  }
  // Live: texture-floor colour grading (brightness / warmth / tint).
  applyFloorGradeLive() {
    for (const [id, F] of Object.entries(FLOORS)) {
      if (F.kind !== 'tex' || !F.grade) continue;
      const m = this._floorMatCache[id], g = F.grade;
      if (!m || !m.userData.floorBright) continue;
      m.userData.floorBright.value = SCENE_PARAMS[g.bright] ?? 1;
      m.userData.floorWarm.value = SCENE_PARAMS[g.warm] ?? 0;
      m.userData.floorTint.value.set(SCENE_PARAMS[g.tint] || '#ffffff');
    }
    this.invalidate();
  }
  // Returns a promise that resolves once the floor is applied (wood may load).
  setFloor(id) {
    if (!FLOORS[id]) id = 'white';
    SCENE_PARAMS.floor = id;
    // Overlay hints sit on the floor — flip them to white on dark floors.
    if (this.floorToneCb) {
      let lum = 1; // texture floors (stone/oak) render light
      const F = FLOORS[id];
      if (F.proc === 'lino') {
        const hex = this._linoTint(F.lino).replace('#', '');
        lum = (parseInt(hex.slice(0, 2), 16) * 0.299 + parseInt(hex.slice(2, 4), 16) * 0.587 + parseInt(hex.slice(4, 6), 16) * 0.114) / 255;
      }
      this.floorToneCb(lum < 0.45);
    }
    this.invalidate();
    if (FLOORS[id].kind === 'proc') { this.ground.material = this._buildProcFloor(id); this._scheduleProbe(120); return Promise.resolve(); }
    // texture floors (oak / stone) — lazy-loaded
    return this._loadTexFloor(id).then(mat => { if (SCENE_PARAMS.floor === id) { this.ground.material = mat; this._scheduleProbe(120); } this.invalidate(); })
      .catch(e => { console.error('PLAYTS floor: texture load failed', e); });
  }
  // Rebuild the linoleum material after a tint change (debug GUI).
  rebuildLinoFloor() {
    // drop cached lino materials so the new tint regenerates
    for (const k of Object.keys(this._floorMatCache)) if (k.startsWith('linoleum')) delete this._floorMatCache[k];
    const f = SCENE_PARAMS.floor;
    if (f === 'linoleum' || f === 'white') this.ground.material = this._buildProcFloor(f);
    this.invalidate();
  }
  applyCameraLive() {
    const P = SCENE_PARAMS, k = this._camScale || 1;
    this.camera.fov = P.camFov;
    this._defaultTarget.x = P.camTargetX || 0;
    this._defaultTarget.y = P.camTargetY || 0;
    if (this.orbit) this.orbit.target.copy(this._defaultTarget);
    if (!this.orbit) {
      this.camera.position.set(P.camX * k, P.camY * k, P.camZ * k);
      this.camera.lookAt(this._defaultTarget);
    }
    this._defaultCamPos.set(P.camX * k, P.camY * k, P.camZ * k);
    this.camera.updateProjectionMatrix();
  }
  // Dolly the default (non-fullscreen) camera in/out. factor <1 = zoom in.
  zoomBy(factor) {
    if (this.orbit) return; // fullscreen has its own scroll/pinch zoom
    const t = this._defaultTarget;
    const dir = this.camera.position.clone().sub(t);
    let d = dir.length() * factor;
    d = Math.max(0.45, Math.min(3.0, d));
    dir.setLength(d);
    this.camera.position.copy(t).add(dir);
    this._defaultCamPos.copy(this.camera.position);
  }
  dumpParams() {
    const P = SCENE_PARAMS, out = {};
    for (const k in P) { if (!k.startsWith('_')) out[k] = P[k]; }
    return JSON.stringify(out, null, 2);
  }
}

// ── Cart / pricing ────────────────────────────────────────────────────────────
async function addSetToCart(pieces, variantIndex) {
  const counts = new Map();
  const gifts = [];
  for (const p of pieces) {
    const v = lookupVariant(variantIndex, p.shape, p.color);
    if (!v) continue;
    // Restock-Aktion: der Gratis-Round (p.free) kommt als SEPARATE Zeile mit
    // Line-Property. Der RESTOCK-Bestellrabatt (−€14,72 = 1 Round − 8 %) macht
    // ihn im Warenkorb netto gratis; das Property hält ihn optisch getrennt.
    if (p.free) { gifts.push(v.id); }
    else { counts.set(v.id, (counts.get(v.id) || 0) + 1); }
  }
  const items = Array.from(counts.entries()).map(([id, qty]) => ({ id, quantity: qty }));
  for (const id of gifts) {
    items.push({ id, quantity: 1, properties: { '_gift_round': 'PLAYTS Round Gratis' } });
  }
  const resp = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ items }),
  });
  if (!resp.ok) throw new Error(`cart_add_failed: ${resp.status} ${(await resp.text()).slice(0, 200)}`);
  return resp.json();
}
function totalPrice(pieces, variantIndex) {
  let total = 0;
  for (const p of pieces) {
    const v = lookupVariant(variantIndex, p.shape, p.color);
    if (v) total += v.price;
  }
  return total;
}
function formatMoney(cents) { return `${(cents / 100).toFixed(2).replace('.', ',')} EUR`; }

// ── UI ────────────────────────────────────────────────────────────────────────
let PC_UID = 1; // stable per-piece identity (survives colour swaps via spread)
class ConfigUI {
  constructor(root, variants, strings, variantIndex, defaultColor, productImages) {
    this.root = root;
    this.variants = variants;
    this.strings = strings;
    this.variantIndex = variantIndex;
    this.defaultColor = defaultColor || 'creme';
    this.productImages = Array.isArray(productImages) ? productImages : [];
    this._galleryList = [];
    this._galleryIdx = 0;
    this._zoom = { scale: 1, tx: 0, ty: 0 }; // gallery pinch-zoom / pan state
    this.discountPercent = Math.max(0, parseFloat(root.dataset.pcDiscountPercent) || 0);
    this.pieces = [];
    // Restock-Aktion („ab X Teilen → 1 Round gratis"), nur für Abonnenten mit
    // aktivem Rabattcode. restockActive wird async aus /cart.js ermittelt.
    this.restockCode = (root.dataset.pcRestockCode || '').trim();
    this.restockMinPieces = Math.max(1, parseInt(root.dataset.pcRestockMin, 10) || 3);
    this.restockActive = false;
    this.restockTeaserTpl = root.dataset.pcRestockTeaser || 'Noch {n} Teile bis gratis PLAYTS Round';
    this.restockLinkText = root.dataset.pcRestockLink || '+ Gratis PLAYTS Round hinzufügen';
    this.restockAddedText = root.dataset.pcRestockAdded || 'Extra PLAYTS Round für 0 EUR';

    // Real Shopify variant colour names (title-cased) + first thumb per colour.
    const titleCase = (s) => s.replace(/\b\w/g, c => c.toUpperCase());
    this.colorNames = {};
    this.colorThumb = {};
    for (const v of variants) {
      if (v.colorName && !this.colorNames[v.color]) this.colorNames[v.color] = titleCase(v.colorName);
      if (v.thumb && !this.colorThumb[v.color]) this.colorThumb[v.color] = v.thumb;
    }

    this.canvas = root.querySelector('[data-pc-canvas]');
    this.stage = root.querySelector('[data-pc-stage]');
    this.empty = root.querySelector('[data-pc-empty]');
    this.hint = root.querySelector('[data-pc-hint]');
    this.loading = root.querySelector('[data-pc-loading]');
    this.below = root.querySelector('[data-pc-below]');
    this.slots = root.querySelector('[data-pc-slots]');
    this.setHeading = root.querySelector('.playts-config__set-heading');
    // Set-genaue Lieferzeit: Fallback-Text steht schon im Span, die zwei Varianten
    // (normal / backorder) in den data-Attributen. render() aktualisiert je Set.
    this.deliveryEl = root.querySelector('[data-pc-delivery]');
    this.deliveryNormal = this.deliveryEl ? (this.deliveryEl.dataset.pcDeliveryNormal || this.deliveryEl.textContent) : '';
    this.deliveryBackorder = this.deliveryEl ? (this.deliveryEl.dataset.pcDeliveryBackorder || this.deliveryNormal) : '';
    // Set products preload their composition from the `custom.set_code` metafield.
    this.initialSetStr = serializeUrl(parseUrl(root.dataset.pcInitialSet || ''));
    this.warning = root.querySelector('[data-pc-warning]');
    this.wasEl = root.querySelector('[data-pc-was]');
    this.totalEl = root.querySelector('[data-pc-total]');
    this.freeShip = root.querySelector('[data-pc-freeship]');
    this.freeShipFill = root.querySelector('[data-pc-freeship-fill]');
    this.freeShipText = root.querySelector('[data-pc-freeship-text]');
    this.freeShipThreshold = Math.max(0, parseInt(root.dataset.pcFreeShip, 10) || 0); // cents
    this.cartBtn = root.querySelector('[data-pc-cart]');
    this.giftHintEl = root.querySelector('[data-pc-gift-hint]');
    this.giftConfirmEl = root.querySelector('[data-pc-gift-confirm]');
    if (this.giftHintEl) {
      const addFree = () => { if (this.giftHintEl.classList.contains('is-link')) this._addFreeRound(); };
      this.giftHintEl.addEventListener('click', addFree);
      this.giftHintEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addFree(); } });
    }
    this._checkRestock();
    this.buyNowBtn = root.querySelector('[data-pc-buy-now]');
    this.resetBtn = root.querySelector('[data-pc-reset]');
    this.shareBtn = root.querySelector('[data-pc-share]');
    this.addBtn = root.querySelector('[data-pc-add]');
    this.randomBtn = root.querySelector('[data-pc-random]');
    this.fullscreenBtn = root.querySelector('[data-pc-fullscreen]');
    this.floorBtn = root.querySelector('[data-pc-floor]');
    this.fsClose = root.querySelector('[data-pc-fullscreen-close]');
    this.fsCart = root.querySelector('[data-pc-fullscreen-cart]');
    this.sheet = root.querySelector('[data-pc-sheet]');
    this.sheetBody = root.querySelector('[data-pc-sheet-body]');
    this.floorSheet = root.querySelector('[data-pc-floor-sheet]');
    this.floorSheetBody = root.querySelector('[data-pc-floor-sheet-body]');
    this.successOverlay = root.querySelector('[data-pc-success]');
    this.shareModal = root.querySelector('[data-pc-share-modal]');
    this.shareInput = root.querySelector('[data-pc-share-input]');
    this.saveBtn = root.querySelector('[data-pc-save]');
    this.saveModal = root.querySelector('[data-pc-save-modal]');
    this.photoModal = root.querySelector('[data-pc-photo-modal]');

    // Initial floor: URL param wins, else the store default (theme setting).
    const urlParams = new URLSearchParams(window.location.search);
    const urlFloor = urlParams.get('floor');
    this.floor = FLOORS[urlFloor] ? urlFloor : (FLOORS[root.dataset.pcDefaultFloor] ? root.dataset.pcDefaultFloor : 'white');
    SCENE_PARAMS.floor = this.floor;
    const urlLino = urlParams.get('lino');
    if (urlLino && SCENE_PARAMS.floorLinoColors[urlLino]) SCENE_PARAMS.floorLinoColor = urlLino;

    // Portal overlays to <body> so they escape any transformed/overflow-clipped
    // theme ancestor and truly cover the viewport (header/footer included).
    for (const el of [this.sheet, this.floorSheet, this.successOverlay, this.shareModal, this.saveModal, this.photoModal]) {
      if (el) document.body.appendChild(el);
    }

    // Set the discount-row label to include the configured percentage.
    if (this.discountLabel && this.discountPercent > 0) {
      this.discountLabel.textContent = `${this.strings.setDiscount} ${this.discountPercent} %`;
    }

    this.scene = new ConfigScene(
      this.canvas,
      {
        round:  root.dataset.pcAssetRound,
        oval:   root.dataset.pcAssetOval,
        square: root.dataset.pcAssetSquare,
        floorOak: {
          color:  root.dataset.pcFloorOakColor,
          normal: root.dataset.pcFloorOakNormal,
          rough:  root.dataset.pcFloorOakRough,
        },
        floorStone: {
          color:  root.dataset.pcFloorStoneColor,
          normal: root.dataset.pcFloorStoneNormal,
          rough:  root.dataset.pcFloorStoneRough,
        },
        floorSteel: {
          color:  root.dataset.pcFloorSteelColor,
          normal: root.dataset.pcFloorSteelNormal,
          rough:  root.dataset.pcFloorSteelRough,
          metal:  root.dataset.pcFloorSteelMetal,
        },
        floorCloth: {
          color:  root.dataset.pcFloorClothColor,
          normal: root.dataset.pcFloorClothNormal,
          rough:  root.dataset.pcFloorClothRough,
        },
        floorMarblewhite: {
          color:  root.dataset.pcFloorMarblewhiteColor,
          normal: root.dataset.pcFloorMarblewhiteNormal,
          rough:  root.dataset.pcFloorMarblewhiteRough,
        },
        floorMarblebeige: {
          color:  root.dataset.pcFloorMarblebeigeColor,
          normal: root.dataset.pcFloorMarblebeigeNormal,
          rough:  root.dataset.pcFloorMarblebeigeRough,
        },
        floorMarblegrey: {
          color:  root.dataset.pcFloorMarblegreyColor,
          normal: root.dataset.pcFloorMarblegreyNormal,
          rough:  root.dataset.pcFloorMarblegreyRough,
        },
        floorBeech: {
          color:  root.dataset.pcFloorBeechColor,
          normal: root.dataset.pcFloorBeechNormal,
          rough:  root.dataset.pcFloorBeechRough,
        },
        floorTerrazzowarm: {
          color:  root.dataset.pcFloorTerrazzowarmColor,
          normal: root.dataset.pcFloorTerrazzowarmNormal,
          rough:  root.dataset.pcFloorTerrazzowarmRough,
        },
        floorTerrazzocool: {
          color:  root.dataset.pcFloorTerrazzocoolColor,
          normal: root.dataset.pcFloorTerrazzocoolNormal,
          rough:  root.dataset.pcFloorTerrazzocoolRough,
        },
      },
      {
        onPieceClick: (idx) => this._openSlotSheet(idx),
        onEmptyClick: () => { if (!this.pieces.length) this._openAddSheet(); },
      },
    );
    // Let the scene push background changes onto the CSS stage element, and set
    // the initial stage bg to match the scene background (white) so there's no
    // off-white seam behind the transparent canvas.
    this.scene.stageBgCb = (hex) => { this.stage.style.background = hex; };
    this.scene.floorToneCb = (isDark) => { this.stage.classList.toggle('is-dark-floor', isDark); };
    this.stage.style.background = SCENE_PARAMS.background;

    this._bindEvents();
  }

  async init() {
    // Render the UI shell (buttons, pills, state) before waiting on GLBs so
    // controls aren't stuck in their pre-render state while the 3D loads.
    // Clamp shared links to the current constraints (older links may carry
    // sets that exceed today's caps — extra pieces would have no layout slot).
    const urlSet = [];
    // ?set=random (≥2 Teile) bzw. ?set=randomN erzeugt ein Zufalls-Set mit
    // mindestens N Teilen — z.B. für "feeling lucky"-Links aus dem Hero.
    const rawSetParam = new URLSearchParams(window.location.search).get('set') || '';
    const rndMatch = rawSetParam.match(/^random(\d)?$/i);
    if (rndMatch) {
      for (const p of randomSet(parseInt(rndMatch[1], 10) || 2)) urlSet.push(p);
    } else {
      for (const p of getSetFromUrl()) if (canAdd(urlSet, p.shape).ok) urlSet.push(p);
    }
    if (!urlSet.length && this.initialSetStr) {
      for (const p of parseUrl(this.initialSetStr)) if (canAdd(urlSet, p.shape).ok) urlSet.push(p);
    }
    if (urlSet.length) this.pieces = urlSet;
    for (const p of this.pieces) if (p.uid == null) p.uid = PC_UID++; // stable ids for the piece lifecycle
    this.render();

    // Apply the initial floor (URL/theme default). Procedural floors are instant;
    // white is the no-op default. Wood would lazy-load here if it were default.
    this.scene.setFloor(this.floor); // 'white' (Neutral) is grey lino now — always build

    this.loading.hidden = false;
    try { await this.scene.preloadAssets(); }
    catch (e) { console.error('PLAYTS configurator: asset load failed', e); }
    this.loading.hidden = true;
    this.scene.introNext = 'all'; // entrance animation on the first real composition
    this.render();

    // Just came back from a successful Save submit → reopen the modal in its
    // success state ("we've emailed your link, copy it here too").
    if (PC_SAVE_RETURN) {
      this._showSaveSuccess(PC_SAVE_RETURN.email, PC_SAVE_RETURN.url);
      PC_SAVE_RETURN = null;
    }

    // Debug GUI (?pcdebug=1) — exposes every scene parameter for fine-tuning.
    if (/[?&]pcdebug=1\b/.test(window.location.search)) this._enableDebug();
  }

  async _enableDebug() {
    let GUI;
    try { ({ GUI } = await import('three/addons/libs/lil-gui.module.min.js')); }
    catch (e) { console.error('PLAYTS debug: lil-gui failed to load', e); return; }
    const P = SCENE_PARAMS, s = this.scene;
    // Console handle for scripted debugging (pcdebug only).
    window.__pc = { scene: s, params: P, ui: this };
    // Render continuously while tuning so every slider updates live (the runtime
    // otherwise renders on-demand for performance).
    s._debugAlwaysRender = true;
    const gui = new GUI({ title: 'PLAYTS debug · build 84' });
    gui.domElement.style.zIndex = '100010';

    // Embed the panel as a right-docked sidebar that RESERVES space (pushes the
    // page content left) instead of floating over the buttons/controls.
    try {
      const W = Math.ceil(gui.domElement.getBoundingClientRect().width) || 245;
      Object.assign(gui.domElement.style, { position: 'fixed', top: '0', right: '0', maxHeight: '100vh', overflowY: 'auto' });
      document.body.style.marginRight = W + 'px';
      // The 3D stage + below row are full-bleed (width:100vw-…): subtract the panel
      // width so their right edge (and its overlay buttons) clears the sidebar.
      const st = document.createElement('style');
      st.textContent = `.playts-config__stage,.playts-config__below{width:calc(100vw - ${W}px - 2*var(--side-spacing,1rem)) !important;}`;
      document.head.appendChild(st);
      s._needsResize = true; s.invalidate();
      window.addEventListener('resize', () => { s._needsResize = true; s.invalidate(); });
    } catch (e) { console.error('PLAYTS debug: dock failed', e); }

    const fCol = gui.addFolder('Product colours');
    for (const key of COLORS) {
      if (!(key in P.colors)) continue;
      fCol.addColor(P.colors, key).name(key).onChange(() => s.applyColorsLive());
    }
    fCol.add(P, 'saturation', 1, 2, 0.01).name('saturation ×').onChange(() => s.applyColorsLive());

    const fMat = gui.addFolder('Material / glaze');
    fMat.add(P, 'roughness', 0, 1, 0.01).onChange(() => s.applyMaterialLive());
    fMat.add(P, 'clearcoat', 0, 1, 0.01).onChange(() => s.applyMaterialLive());
    fMat.add(P, 'clearcoatRoughness', 0, 0.6, 0.01).onChange(() => s.applyMaterialLive());
    fMat.add(P, 'pieceIor', 1.0, 2.333, 0.001).name('top reflectivity (ior)').onChange(() => s.applyMaterialLive());
    fMat.add(P, 'pieceMetalness', 0, 0.5, 0.01).name('mirror finish (metalness)').onChange(() => s.applyMaterialLive());
    fMat.add(P, 'sheen', 0, 1, 0.01).onChange(() => s.applyMaterialLive());
    fMat.add(P, 'sheenRoughness', 0, 1, 0.01).onChange(() => s.applyMaterialLive());
    fMat.addColor(P, 'sheenColor').onChange(() => s.applyMaterialLive());
    fMat.add(P, 'envIntensity', 0, 3, 0.05).onChange(() => s.applyMaterialLive());

    const fEdge = gui.addFolder('Glaze edge + speckle');
    fEdge.add(P, 'rimStrength', 0, 1.5, 0.01).onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'edgeCenter', 0.05, 0.95, 0.01).onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'edgeTight', 1, 14, 0.1).onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'upBias', -0.3, 0.9, 0.01).onChange(() => s.applyGlazeLive());
    fEdge.addColor(P, 'rimColor').onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'speckleAmount', 0, 1, 0.01).onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'speckleScale', 200, 8000, 50).onChange(() => s.applyGlazeLive());
    fEdge.add(P, 'speckleDensity', 0, 2, 0.01).onChange(() => s.applyGlazeLive());

    const fTex = gui.addFolder('Surface texture — pores (regen)');
    fTex.add(P, 'pitCount', 0, 3000, 20).onFinishChange(() => s.rebuildTextures());
    fTex.add(P, 'pitDepth', 0, 0.5, 0.005).onFinishChange(() => s.rebuildTextures());
    fTex.add(P, 'texRepeat', 1, 8, 1).onFinishChange(() => s.rebuildTextures());

    const fRelief = gui.addFolder('Geometrie — Relief (Normal, live)');
    const rel = () => s.applyReliefLive();
    fRelief.add(P, 'reliefEnabled').name('Relief an').onChange(rel);
    fRelief.add(P, 'reliefStrength', 0, 80, 0.5).name('Stärke').onChange(rel);
    fRelief.add(P, 'reliefCrownWidth', 0.05, 1, 0.01).name('Erhebung Außenbreite (×R)').onChange(rel);
    fRelief.add(P, 'reliefCrownHeightMm', 0, 0.5, 0.005).name('Erhebung Höhe (mm)').onChange(rel);
    fRelief.add(P, 'reliefValleyDepthMm', 0, 0.5, 0.005).name('Senke Tiefe (mm)').onChange(rel);
    fRelief.add(P, 'reliefValleyPos', 0.3, 1, 0.01).name('Senke Position (×R)').onChange(rel);
    fRelief.add(P, 'reliefValleyWidth', 0.05, 0.5, 0.01).name('Senke Breite (×R)').onChange(rel);
    fRelief.add(P, 'reliefRimFlat', 0, 0.3, 0.01).name('Rand flach (×R)').onChange(rel);

    const fEnv = gui.addFolder('Window light (env)');
    fEnv.add(P, 'winBrightness', 0, 150, 0.1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winWidth', 1, 180, 1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winHeight', 5, 120, 1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winElevation', -60, 90, 1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winAzimuth', -180, 180, 1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winSoftness', 0, 60, 1).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win2Factor', 0, 1.5, 0.01).name('2nd window ×').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'roomBrightness', 0, 0.8, 0.005).onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winPanesX', 1, 10, 1).name('mullions X').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winPanesY', 1, 10, 1).name('mullions Y').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'winMullion', 0, 0.6, 0.005).name('mullion width').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win3Brightness', 0, 150, 0.5).name('3rd window brightness').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win3Azimuth', -180, 180, 1).name('3rd window Δazimuth').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win3Elevation', -80, 80, 1).name('3rd window elevation').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win3Width', 1, 90, 1).name('3rd window width').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'win3Height', 1, 90, 1).name('3rd window height').onChange(() => s.rebuildEnv());
    fEnv.add(P, 'wallWarmth', 0, 0.2, 0.005).name('wall warmth').onChange(() => s.rebuildEnv());
    // Room details CLEANED UP 2026-07-22: the painted-env experiment toggles
    // (wall gradient/glow, silhouettes, furniture band, ceiling glow, warm
    // accent, light band) are gone from the GUI — none of them ever read on
    // the tops and they just confused the panel. The params still exist
    // (default off) so old saved settings don't break.
    const fRoom = gui.addFolder('Room details (reflections)');
    fRoom.add(P, 'envWallGrad').name('wall gradient').onChange(() => s.rebuildEnv());
    fRoom.add(P, 'envWallGradStrength', 0, 1, 0.01).name('gradient strength').onChange(() => s.rebuildEnv());
    fRoom.add(P, 'probeFurniture').name('Möbel (3D)').onChange(() => { if (P.probeFurniture) P.probeEnabled = true; s._bakeSceneEnv(); });
    fRoom.add(P, 'probeCeiling').name('Lichtdecke (3D)').onChange(() => { if (P.probeCeiling) P.probeEnabled = true; s._bakeSceneEnv(); });
    fRoom.add(P, 'probeDeco').name('deco: carafe + glasses (visible)').onChange(() => { if (P.probeDeco) P.probeEnabled = true; s._ensureProbeDeco().visible = P.probeDeco; this.render(); s._bakeSceneEnv(); s.invalidate(); });

    const fProbe = gui.addFolder('Reflections (probe)');
    fProbe.add(P, 'probeStatus').name('status').listen().disable();
    fProbe.add(P, 'probeEnabled').name('pieces reflect scene').onChange((v) => { v ? s._bakeSceneEnv() : s._applyProbeToMats(); });
    fProbe.add(P, 'probeBoost', 0, 3, 0.05).name('strength ×').onChange(() => s._applyProbeToMats());
    fProbe.add(P, 'probeRes', [128, 256, 512]).name('resolution').onChange(() => s._bakeSceneEnv());
    fProbe.add({ rebake: () => s._bakeSceneEnv() }, 'rebake').name('rebake now');
    const fFx = gui.addFolder('Post-FX');
    fFx.add(P, 'fxGrain', 0, 0.2, 0.005).name('film grain').onChange(() => s.applyFxLive());
    fFx.add(P, 'fxBloom', 0, 0.6, 0.01).name('bloom strength').onChange(() => s.applyFxLive());
    fFx.add(P, 'fxBloomThreshold', 0, 2, 0.01).name('bloom threshold').onChange(() => s.applyFxLive());

    const fLight = gui.addFolder('Lights');
    fLight.add(P, 'hemiIntensity', 0, 2, 0.01).onChange(() => s.applyLightsLive());
    fLight.add(P, 'keyIntensity', 0, 6, 0.05).onChange(() => s.applyLightsLive());
    fLight.add(P, 'fillIntensity', 0, 3, 0.05).onChange(() => s.applyLightsLive());
    fLight.add(P, 'sheenLight', 0, 4, 0.05).name('top sheen light').onChange(() => s.applyLightsLive());
    fLight.add(P, 'sheenSpread', 0, 0.8, 0.01).name('sheen spread (seam)').onChange(() => s.applyLightsLive());
    fLight.add(P, 'rimIntensity', 0, 3, 0.05).onChange(() => s.applyLightsLive());
    fLight.addColor(P, 'keyColor').name('key colour').onChange(() => s.applyLightsLive());
    fLight.addColor(P, 'fillColor').name('fill colour').onChange(() => s.applyLightsLive());
    fLight.addColor(P, 'rimLightColor').name('rim colour').onChange(() => s.applyLightsLive());
    fLight.addColor(P, 'hemiSkyColor').name('hemi sky').onChange(() => s.applyLightsLive());
    fLight.addColor(P, 'hemiGroundColor').name('hemi ground').onChange(() => s.applyLightsLive());
    fLight.add(P, 'keyX', -3, 3, 0.05).name('key light X').onChange(() => s.applyLightsLive());
    fLight.add(P, 'keyY', 0.3, 6, 0.05).name('key light Y (height)').onChange(() => s.applyLightsLive());
    fLight.add(P, 'keyZ', -3, 3, 0.05).name('key light Z').onChange(() => s.applyLightsLive());
    fLight.add(P, 'shadowNormalBias', 0, 0.005, 0.0001).name('shadow normalBias').onChange(() => s.applyLightsLive());
    fLight.add(P, 'shadowBias', -0.002, 0.0005, 0.00005).name('shadow bias').onChange(() => s.applyLightsLive());

    const fAA = gui.addFolder('Anti-aliasing / quality');
    fAA.add(P, 'pixelRatio', 1, 4, 0.25).name('pixel ratio (SSAA)').onChange(() => s.applyPixelRatioLive());
    fAA.add(P, 'shadowMapSize', [1024, 2048, 4096, 8192]).name('shadow map size').onChange(() => s.applyShadowMapSizeLive());
    fAA.add(P, 'shadowRadius', 0, 40, 0.5).name('shadow blur').onChange(() => s.applyLightsLive());

    const fScene = gui.addFolder('Scene / render');
    fScene.add(P, 'toneMapping', Object.keys(TONE_MAPPINGS)).name('tone mapping').onChange(() => s.applySceneLive());
    fScene.addColor(P, 'background').name('background').onChange(() => s.applySceneLive());
    fScene.addColor(P, 'floorColor').name('white floor tint').onChange(() => s.applySceneLive());
    fScene.add(P, 'exposure', 0.3, 2, 0.01).onChange(() => s.applySceneLive());
    fScene.add(P, 'shadowOpacity', 0, 0.8, 0.01).onChange(() => s.applySceneLive());

    const fFloor = gui.addFolder('Floor');
    fFloor.add(P, 'floor', FLOOR_ORDER).name('floor').onChange((v) => { this.floor = v; s.setFloor(v); writeSetToUrl(this.pieces, this.floor); });
    // Each floor lives in its own collapsible subfolder — the flat list had ~40
    // rows, so the newest floors (steel/cloth) were pushed far off-screen and
    // effectively unreachable. Subfolders keep Floor to a short header list.
    const fStone = fFloor.addFolder('Stone').close();
    fStone.add(P, 'floorStoneScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fStone.add(P, 'floorStoneContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fStone.add(P, 'floorStoneBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fStone.add(P, 'floorStoneWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fStone.addColor(P, 'floorStoneTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fStone.add(P, 'floorStoneGloss', 0, 1, 0.01).name('gloss').onChange(() => s.applyFloorGlossLive());
    fStone.add(P, 'floorStoneGlossRough', 0, 1, 0.01).name('gloss rough.').onChange(() => s.applyFloorGlossLive());
    const fLino = fFloor.addFolder('Linoleum').close();
    fLino.add(P, 'floorLinoScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fLino.add(P, 'floorLinoColor', Object.keys(P.floorLinoColors)).name('colour').onChange((v) => { this.floor = 'linoleum'; s.setFloor('linoleum'); writeSetToUrl(this.pieces, this.floor); });
    fLino.add(P, 'floorLinoContrast', 0, 0.2, 0.005).name('homogeneity ↓').onChange(() => s.rebuildLinoFloor());
    fLino.add(P, 'floorLinoGrain', 0, 0.6, 0.005).name('grain amount').onChange(() => s.rebuildLinoFloor());
    fLino.add(P, 'floorLinoGrainScale', 0.3, 3, 0.05).name('grain scale').onChange(() => s.rebuildLinoFloor());
    fLino.add(P, 'floorLinoGrainRelief', 0, 4, 0.05).name('grain relief').onChange(() => s.rebuildLinoFloor());
    fLino.add(P, 'floorLinoGloss', 0, 1, 0.01).name('gloss').onChange(() => s.applyFloorGlossLive());
    fLino.add(P, 'floorLinoGlossRough', 0, 1, 0.01).name('gloss rough.').onChange(() => s.applyFloorGlossLive());
    const fLinoCol = fLino.addFolder('Palette').close();
    for (const key of Object.keys(P.floorLinoColors)) fLinoCol.addColor(P.floorLinoColors, key).name(key).onChange(() => s.rebuildLinoFloor());
    const fOak = fFloor.addFolder('Oak / ash').close();
    fOak.add(P, 'floorOakScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fOak.add(P, 'floorOakContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fOak.add(P, 'floorOakBright', 0.4, 1.8, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fOak.add(P, 'floorOakWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fOak.addColor(P, 'floorOakTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fOak.add(P, 'floorOakGloss', 0, 1, 0.01).name('gloss').onChange(() => s.applyFloorGlossLive());
    fOak.add(P, 'floorOakGlossRough', 0, 1, 0.01).name('gloss rough.').onChange(() => s.applyFloorGlossLive());
    const fSteel = fFloor.addFolder('Steel').close();
    fSteel.add(P, 'floorSteelScale', 4, 160, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fSteel.add(P, 'floorSteelGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fSteel.add(P, 'floorSteelGlossRough', 0, 0.6, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    fSteel.add(P, 'floorSteelRough', 0.02, 1, 0.01).name('roughness').onChange(() => s.applyFloorMatLive());
    fSteel.add(P, 'floorSteelContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fSteel.add(P, 'floorSteelBright', 0.4, 6, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fSteel.add(P, 'floorSteelWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fSteel.addColor(P, 'floorSteelTint').name('tint').onChange(() => s.applyFloorGradeLive());
    const fCloth = fFloor.addFolder('Tablecloth').close();
    fCloth.add(P, 'floorClothScale', 4, 160, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fCloth.add(P, 'floorClothWeave', 0, 4, 0.05).name('weave').onChange(() => s.applyFloorMatLive());
    fCloth.add(P, 'floorClothSheen', 0, 2, 0.01).name('sheen').onChange(() => s.applyFloorMatLive());
    fCloth.add(P, 'floorClothEnv', 0, 2, 0.01).name('env').onChange(() => s.applyFloorMatLive());
    fCloth.add(P, 'floorClothContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fCloth.add(P, 'floorClothBright', 0.4, 2, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fCloth.add(P, 'floorClothWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fCloth.addColor(P, 'floorClothTint').name('tint').onChange(() => s.applyFloorGradeLive());
    const fMarblewhite = fFloor.addFolder('Marble white').close();
    fMarblewhite.add(P, 'floorMarblewhiteScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fMarblewhite.add(P, 'floorMarblewhiteContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fMarblewhite.add(P, 'floorMarblewhiteBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fMarblewhite.add(P, 'floorMarblewhiteWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fMarblewhite.addColor(P, 'floorMarblewhiteTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fMarblewhite.add(P, 'floorMarblewhiteGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fMarblewhite.add(P, 'floorMarblewhiteGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    const fMarblebeige = fFloor.addFolder('Marble beige').close();
    fMarblebeige.add(P, 'floorMarblebeigeScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fMarblebeige.add(P, 'floorMarblebeigeContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fMarblebeige.add(P, 'floorMarblebeigeBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fMarblebeige.add(P, 'floorMarblebeigeWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fMarblebeige.addColor(P, 'floorMarblebeigeTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fMarblebeige.add(P, 'floorMarblebeigeGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fMarblebeige.add(P, 'floorMarblebeigeGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    const fMarblegrey = fFloor.addFolder('Marble grey').close();
    fMarblegrey.add(P, 'floorMarblegreyScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fMarblegrey.add(P, 'floorMarblegreyContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fMarblegrey.add(P, 'floorMarblegreyBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fMarblegrey.add(P, 'floorMarblegreyWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fMarblegrey.addColor(P, 'floorMarblegreyTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fMarblegrey.add(P, 'floorMarblegreyGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fMarblegrey.add(P, 'floorMarblegreyGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    const fBeech = fFloor.addFolder('Beech').close();
    fBeech.add(P, 'floorBeechScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fBeech.add(P, 'floorBeechContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fBeech.add(P, 'floorBeechBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fBeech.add(P, 'floorBeechWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fBeech.addColor(P, 'floorBeechTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fBeech.add(P, 'floorBeechGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fBeech.add(P, 'floorBeechGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    const fTerrazzowarm = fFloor.addFolder('Terrazzo warm').close();
    fTerrazzowarm.add(P, 'floorTerrazzowarmScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fTerrazzowarm.add(P, 'floorTerrazzowarmContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fTerrazzowarm.add(P, 'floorTerrazzowarmBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fTerrazzowarm.add(P, 'floorTerrazzowarmWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fTerrazzowarm.addColor(P, 'floorTerrazzowarmTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fTerrazzowarm.add(P, 'floorTerrazzowarmGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fTerrazzowarm.add(P, 'floorTerrazzowarmGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());
    const fTerrazzocool = fFloor.addFolder('Terrazzo grey').close();
    fTerrazzocool.add(P, 'floorTerrazzocoolScale', 4, 120, 1).name('scale').onChange(() => s.applyFloorScaleLive());
    fTerrazzocool.add(P, 'floorTerrazzocoolContrast', 0, 1.5, 0.01).name('contrast').onChange(() => s.applyFloorContrastLive());
    fTerrazzocool.add(P, 'floorTerrazzocoolBright', 0.4, 3, 0.01).name('brightness').onChange(() => s.applyFloorGradeLive());
    fTerrazzocool.add(P, 'floorTerrazzocoolWarmth', -1, 1, 0.01).name('warmth').onChange(() => s.applyFloorGradeLive());
    fTerrazzocool.addColor(P, 'floorTerrazzocoolTint').name('tint').onChange(() => s.applyFloorGradeLive());
    fTerrazzocool.add(P, 'floorTerrazzocoolGloss', 0, 1, 0.01).name('reflection').onChange(() => s.applyFloorGlossLive());
    fTerrazzocool.add(P, 'floorTerrazzocoolGlossRough', 0, 1, 0.01).name('reflection blur').onChange(() => s.applyFloorGlossLive());

    const fCam = gui.addFolder('Camera (default)');
    fCam.add(P, 'camFov', 5, 60, 1).onChange(() => s.applyCameraLive());
    fCam.add(P, 'camX', -1, 1, 0.01).onChange(() => s.applyCameraLive());
    fCam.add(P, 'camTargetX', -0.3, 0.3, 0.005).name('pan (left +)').onChange(() => s.applyCameraLive());
    fCam.add(P, 'camTargetY', -0.3, 0.3, 0.005).name('pan (up −)').onChange(() => s.applyCameraLive());
    fCam.add(P, 'camY', 0.05, 1.5, 0.01).onChange(() => s.applyCameraLive());
    fCam.add(P, 'camZ', 0.05, 1.5, 0.01).onChange(() => s.applyCameraLive());
    fCam.add(P, 'camZoom', 0.5, 1.2, 0.01).name('global zoom').onChange(() => { s._needsResize = true; });
    fCam.add(P, 'camZoomWide', 0.5, 1.0, 0.01).name('desktop zoom').onChange(() => { s._needsResize = true; });
    fCam.add(P, 'camZoomAspectLo', 1.0, 2.0, 0.05).name('zoom aspect lo').onChange(() => { s._needsResize = true; });
    fCam.add(P, 'camZoomAspectHi', 1.0, 2.5, 0.05).name('zoom aspect hi').onChange(() => { s._needsResize = true; });
    fCam.add(P, 'camFitRef', 0.15, 0.4, 0.005).name('fit ref radius').onChange(() => this.render());
    fCam.add(P, 'camFitMax', 1.0, 2.0, 0.05).name('fit max dolly').onChange(() => this.render());

    const fAnim = gui.addFolder('Animations');
    fAnim.add(P, 'animPieces').name('piece add/move/remove');
    fAnim.add(P, 'introDrop', 0.02, 0.4, 0.01).name('drop height (m)');
    fAnim.add(P, 'introFallTime', 0.2, 1.5, 0.05).name('drop time (s)');
    fAnim.add(P, 'introStagger', 0, 0.4, 0.01).name('drop stagger (s)');
    fAnim.add(P, 'animMoveMs', 0, 1200, 10).name('move glide (ms)');
    fAnim.add(P, 'animRemoveMs', 0, 800, 10).name('remove fade (ms)');
    fAnim.add(P, 'animColorFade').name('colour crossfade');
    fAnim.add(P, 'animColorMs', 0, 800, 10).name('crossfade (ms)');
    fAnim.add(P, 'animCamIntro').name('camera intro');
    fAnim.add(P, 'introCamPull', 0, 0.4, 0.01).name('cam pull-back');
    fAnim.add(P, 'introCamTime', 0.4, 3, 0.05).name('cam time (s)');
    fAnim.add(P, 'animIdleDrift').name('idle drift').onChange(() => { s.applyCameraLive(); s.invalidate(500); });
    fAnim.add(P, 'idleDriftDeg', 0.5, 8, 0.1).name('drift ±deg');
    fAnim.add(P, 'idleDriftPeriod', 4, 40, 1).name('drift period (s)');
    fAnim.add({ replay: () => { s.introNext = 'all'; s._forceRebuild = true; this.render(); } }, 'replay').name('▶ replay entrance');

    const fReal = gui.addFolder('Photorealism');
    fReal.add(P, 'fxGtao').name('GTAO (ambient occl.)').onChange(() => s.applyGtaoLive());
    fReal.add(P, 'fxGtaoRadius', 0.005, 0.4, 0.005).name('GTAO radius').onChange(() => s.applyGtaoLive());
    fReal.add(P, 'fxGtaoBlend', 0, 2, 0.05).name('GTAO intensity').onChange(() => s.applyGtaoLive());
    fReal.add(P, 'fxPeel').name('glaze orange peel').onChange(() => s.applyPeelLive());
    fReal.add(P, 'fxPeelStrength', 0, 1, 0.01).name('peel strength').onChange(() => s.applyPeelLive());
    fReal.add(P, 'fxPeelScale', 0.5, 80, 0.5).name('peel scale').onChange(() => s.applyPeelLive());
    fReal.add(P, 'envShadowDim', 0, 1, 0.01).name('shadow dims reflections').onChange(() => s.applyGlazeLive());

    gui.add({ dump: () => {
      const txt = s.dumpParams();
      navigator.clipboard?.writeText(txt).catch(() => {});
      console.log('PLAYTS SCENE_PARAMS:\n' + txt);
    } }, 'dump').name('⇩ copy params to clipboard');
  }

  _bindEvents() {
    // Empty-state click → open add sheet (but don't swallow the link inside it)
    this.empty.addEventListener('click', (e) => {
      if (e.target.closest('[data-pc-stop-propagation]')) return;
      this._openAddSheet();
    });

    // Top-left + button
    this.addBtn.addEventListener('click', () => this._openAddSheet());
    if (this.randomBtn) {
      this.randomBtn.addEventListener('click', () => {
        const set = randomSet(2);
        for (const p of set) p.uid = PC_UID++;
        this.pieces = set;
        this.render();
        writeSetToUrl(this.pieces, this.floor);
      });
    }

    // Items list (delegated) — clicking the row opens the colour/remove sheet,
    // clicking the trash icon removes the piece directly without opening it.
    this.slots.addEventListener('click', (e) => {
      // Add-item placeholder tile → scroll up to the stage + open the add sheet.
      if (e.target.closest('[data-pc-add-tile]')) { this._scrollToStageAndAdd(); return; }
      // Click the real product photo → full-screen product-image gallery
      // (not the edit sheet), filtered to this variant and starting on its photo.
      const photo = e.target.closest('img.playts-config__item-thumb[data-pc-photo-variant]');
      if (photo) {
        e.preventDefault();
        e.stopPropagation();
        this._openGallery(photo.dataset.pcPhotoVariant, photo.dataset.pcPhotoImage);
        return;
      }
      const removeBtn = e.target.closest('[data-pc-remove-slot]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this._removePiece(Number(removeBtn.dataset.pcRemoveSlot));
        return;
      }
      const slotBtn = e.target.closest('[data-pc-slot]');
      if (!slotBtn) return;
      this._openSlotSheet(Number(slotBtn.dataset.pcSlot));
    });

    this.cartBtn.addEventListener('click', () => this._handleAddToCart());
    if (this.buyNowBtn) this.buyNowBtn.addEventListener('click', () => this._handleBuyNow());
    this.resetBtn.addEventListener('click', () => this._reset());
    this.shareBtn.addEventListener('click', () => this._openShare());
    if (this.floorBtn) this.floorBtn.addEventListener('click', () => this._openFloorSheet());
    if (this.floorSheet) {
      this.floorSheet.querySelectorAll('[data-pc-floor-sheet-close]').forEach(el => el.addEventListener('click', () => this._closeFloorSheet()));
    }
    // Pulse the rotate/zoom button + show the "Zoom & Rotate" hint on EVERY
    // page load, until the button is used once (per session — no persistence).
    const view3dHint = this.stage.querySelector('.playts-config__view3d-hint');
    this.fullscreenBtn.classList.add('pc-pulse');
    this.fullscreenBtn.addEventListener('click', () => {
      this.fullscreenBtn.classList.remove('pc-pulse');
      if (view3dHint) view3dHint.classList.add('is-done');
      this._enterFullscreen();
    });
    this.fsClose.addEventListener('click', () => this._exitFullscreen());
    this.fsCart.addEventListener('click', () => this._handleAddToCart());

    // Sheet/overlay close buttons (now portalled to body — query globally)
    this.sheet.querySelectorAll('[data-pc-sheet-close]').forEach(el => {
      el.addEventListener('click', () => this._closeSheet());
    });
    this.shareModal.querySelector('[data-pc-share-close]').addEventListener('click', () => this._closeShare());
    this.shareModal.querySelector('[data-pc-share-copy]').addEventListener('click', (e) => this._copyShare(e.currentTarget));
    this.shareModal.addEventListener('click', (e) => { if (e.target === this.shareModal) this._closeShare(); });

    // Save modal (email capture → customer + permanent link).
    if (this.saveBtn) this.saveBtn.addEventListener('click', () => this._openSave());
    if (this.saveModal) {
      const closeBtn = this.saveModal.querySelector('[data-pc-save-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this._closeSave());
      this.saveModal.addEventListener('click', (e) => { if (e.target === this.saveModal) this._closeSave(); });
      const saveCopy = this.saveModal.querySelector('[data-pc-save-copy]');
      if (saveCopy) saveCopy.addEventListener('click', (e) => this._copySaveLink(e.currentTarget));
      const saveForm = this.saveModal.querySelector('#pc-save-form');
      if (saveForm) saveForm.addEventListener('submit', (e) => this._onSaveSubmit(e, saveForm));
    }

    // Item-photo gallery: × closes; backdrop (only the dark area) closes; the
    // side arrows page through the product images.
    if (this.photoModal) {
      const pc = this.photoModal.querySelector('[data-pc-photo-close]');
      if (pc) pc.addEventListener('click', () => this._closePhoto());
      this.photoModal.addEventListener('click', (e) => { if (e.target === this.photoModal && !this._photoSwiped) this._closePhoto(); });
      const prev = this.photoModal.querySelector('[data-pc-photo-prev]');
      const next = this.photoModal.querySelector('[data-pc-photo-next]');
      if (prev) prev.addEventListener('click', () => this._galleryNav(-1));
      if (next) next.addEventListener('click', () => this._galleryNav(1));
      this._bindPhotoGestures();
    }

    document.addEventListener('keydown', (e) => {
      if (this.photoModal && !this.photoModal.hidden) {
        if (e.key === 'ArrowLeft') { this._galleryNav(-1); return; }
        if (e.key === 'ArrowRight') { this._galleryNav(1); return; }
      }
      if (e.key !== 'Escape') return;
      if (this.photoModal && !this.photoModal.hidden) this._closePhoto();
      else if (!this.sheet.hidden) this._closeSheet();
      else if (this.floorSheet && !this.floorSheet.hidden) this._closeFloorSheet();
      else if (!this.shareModal.hidden) this._closeShare();
      else if (this.saveModal && !this.saveModal.hidden) this._closeSave();
      else if (this.stage.classList.contains('is-fullscreen')) this._exitFullscreen();
    });

    const dismissBtn = this.successOverlay.querySelector('[data-pc-success-close]');
    if (dismissBtn) dismissBtn.addEventListener('click', () => { this.successOverlay.hidden = true; });
  }

  _scrollToStageAndAdd() {
    // The item list can sit far below the stage (esp. mobile) — bring the 3D
    // stage back into view, then open the add sheet. The sheet is a fixed
    // overlay, so it shows over the scroll; when it closes the user is already
    // looking at the stage and sees the new piece drop in.
    try { this.stage.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    catch (_) { this.stage.scrollIntoView(); }
    this._openAddSheet();
  }

  _hasSoldOutPieces() {
    return this.pieces.some(p => {
      const v = lookupVariant(this.variantIndex, p.shape, p.color);
      return !v || !v.available;
    });
  }

  // Set-genaue Lieferzeit: sobald EINE Backorder-Farbe (kaufbar, aber Bestand <= 0)
  // im aktuellen Set ist, steigt die Lieferzeit; sonst der normale Text. Leeres
  // Set = normal. Löst sich nach Restock von selbst auf (dann kein v.backordered).
  _updateDelivery() {
    if (!this.deliveryEl) return;
    const bo = this.pieces.some(p => {
      const v = lookupVariant(this.variantIndex, p.shape, p.color);
      return v && v.backordered;
    });
    const txt = bo ? this.deliveryBackorder : this.deliveryNormal;
    if (this.deliveryEl.textContent !== txt) this.deliveryEl.textContent = txt;
  }

  render() {
    // Restock: Gratis-Round dynamisch bestimmen, BEVOR Liste/Preis gerendert werden.
    this._syncFreeRound();
    const empty = this.pieces.length === 0;
    // Headline carries the piece count, e.g. "Dein PLAYTS Set (3-tlg)".
    if (this.setHeading) {
      if (this._setHeadingBase == null) {
        this._setHeadingBase = this.setHeading.textContent.trim();
        // Produktname als H1 nur für externe Einstiege (z.B. Google Shopping,
        // wo die Suchanfrage zum Produkttitel passen soll). Wer intern
        // navigiert, sieht immer die generische Konfigurator-Headline.
        // Server-seitig bleibt der Produkttitel im Markup (SEO/Crawler).
        const fb0 = this.setHeading.dataset.pcHeadingFallback;
        if (fb0 && this._setHeadingBase !== fb0) {
          let internal = false;
          try { internal = !!document.referrer && new URL(document.referrer).origin === location.origin; } catch (e) {}
          if (internal) this._setHeadingBase = fb0;
        }
      }
      const tpl = this.strings.setHeadingCount || '({count}-tlg)';
      // On a set product the H1 is the product title, but only while the set is
      // untouched — once it differs it is no longer that product, so fall back
      // to the generic configurator heading (and back again if restored).
      const fallback = this.setHeading.dataset.pcHeadingFallback;
      let base = this._setHeadingBase;
      if (fallback && this.initialSetStr && serializeUrl(this.pieces) !== this.initialSetStr) base = fallback;
      this.setHeading.textContent = empty
        ? base
        : `${base} ${tpl.replace('{count}', this.pieces.length)}`;
    }
    this.empty.hidden = !empty;
    this.stage.classList.toggle('is-empty', empty);
    // Below-stage layout (item list + summary card) is ALWAYS visible now — even
    // with 0 pieces it shows a "add pieces above" placeholder row and the summary
    // card at 0 €. this.below.classList tracks empty for placeholder styling.
    this.below.hidden = false;
    this.below.classList.toggle('is-empty', empty);
    this.hint.hidden = empty;
    this._updateDelivery();

    // Items list (vertical). Order per row: main (swatch + text + sold-out
     // badge) · trash icon · price column at the far right (visually lines
     // up with the Gesamt amount in the summary column below on mobile).
    this.slots.innerHTML = '';
    this.pieces.forEach((p, i) => {
      const v = lookupVariant(this.variantIndex, p.shape, p.color);
      const soldOut = !v || !v.available;
      const li = document.createElement('li');
      li.className = `playts-config__item ${soldOut ? 'is-soldout' : ''} ${p.free ? 'playts-config__item--free' : ''}`;
      // Actual per-variant photo (shape + colour), not just a per-colour thumb.
      const img = v && v.thumb ? v.thumb : this.colorThumb[p.color];
      // Click-to-view opens the product gallery filtered to THIS variant: its own
      // photo + every image not assigned to any variant (see _galleryListFor).
      const galleryAttr = (this.productImages.length && v)
        ? ` data-pc-photo-variant="${v.id}" data-pc-photo-image="${v.imageId != null ? v.imageId : ''}"`
        : '';
      const photoAlt = `${this.strings.shapeNames[p.shape] || p.shape} ${this.colorNames[p.color] || p.color}`;
      // Row: photo · text block (title / sold-out / price stacked) · divider ·
      // swap + trash icons on the right.
      li.innerHTML = `
        <button type="button" class="playts-config__item-main" data-pc-slot="${i}">
          ${img
            ? `<img class="playts-config__item-thumb" src="${img}"${galleryAttr} alt="${photoAlt}" loading="lazy">`
            : `<span class="playts-config__item-swatch" style="background:${COLOR_HEX[p.color]}"></span>`}
          <span class="playts-config__item-text">
            <span class="playts-config__item-titles">
              <span class="playts-config__item-head">
                <span class="playts-config__item-desc">${this.strings.shapeNames[p.shape] || p.shape}</span>
                ${soldOut ? `<span class="playts-config__item-badge">${this.strings.soldOut}</span>` : ''}
              </span>
              <span class="playts-config__item-color">${this.colorNames[p.color] || p.color}</span>
            </span>
            <span class="playts-config__item-price">${p.free ? 'Gratis' : (v ? formatMoney(v.price) : '—')}</span>
          </span>
        </button>
        <span class="playts-config__item-actions">
          <button type="button" class="playts-config__item-swap" data-pc-slot="${i}" aria-label="${this.strings.changeColor}" title="${this.strings.changeColor}">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="21 4 21 9 16 9"/>
              <polyline points="3 20 3 15 8 15"/>
              <path d="M4.4 9 a8 8 0 0 1 13.3 -3 L21 9 M3 15 l3.3 3 A8 8 0 0 0 19.6 15"/>
            </svg>
          </button>
          <button type="button" class="playts-config__item-remove" data-pc-remove-slot="${i}" aria-label="${this.strings.remove}" title="${this.strings.remove}">
            <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M4 7 h16 M10 4 h4 a1 1 0 0 1 1 1 v2 M6 7 v13 a2 2 0 0 0 2 2 h8 a2 2 0 0 0 2 -2 V7 M10 11 v7 M14 11 v7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </span>
      `;
      this.slots.appendChild(li);
    });

    // Overlay-bar buttons enable/disable. At the piece cap the add button stays
    // visible but reads "Max. Anzahl erreicht" (plus icon hidden).
    const anyShapeCanBeAdded = SHAPES.some(s => canAdd(this.pieces, s).ok);

    // Add-item placeholder tile — mirrors a real item card exactly (it reuses the
    // item-main / item-thumb / item-text classes) so shape, size and formatting
    // match; only the OUTER border is DOTTED and the thumbnail is a grey "empty
    // image" placeholder holding a "+". Shown whenever more pieces can still be
    // added (empty AND partial sets; hidden at the cap). "Teil hinzufügen" sits
    // where the shape name goes; the price shows the cheapest variant as "ab …".
    // Clicking it scrolls up to the stage and opens the add sheet.
    if (anyShapeCanBeAdded) {
      const label = this.strings.addShort || 'Teil hinzufügen';
      const minPrice = Math.min(...this.variants.map(v => v.price));
      const fromPrice = `${this.strings.priceFrom || 'ab'} ${formatMoney(minPrice)}`;
      const li = document.createElement('li');
      li.className = 'playts-config__item playts-config__item--add';
      li.innerHTML = `
        <button type="button" class="playts-config__item-main" data-pc-add-tile aria-label="${label}">
          <span class="playts-config__item-thumb playts-config__item-thumb--add" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M12 5 V19 M5 12 H19" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
            </svg>
          </span>
          <span class="playts-config__item-text">
            <span class="playts-config__item-titles">
              <span class="playts-config__item-head">
                <span class="playts-config__item-desc">${label}</span>
              </span>
            </span>
            <span class="playts-config__item-price">${fromPrice}</span>
          </span>
        </button>
      `;
      this.slots.appendChild(li);
    }
    this.addBtn.disabled = !anyShapeCanBeAdded;
    const addLabel = this.addBtn.querySelector('span');
    if (addLabel) {
      if (this._addLabelDefault === undefined) this._addLabelDefault = addLabel.textContent;
      addLabel.textContent = anyShapeCanBeAdded
        ? this._addLabelDefault
        : (this.strings.maxReached || 'Max. Set-Größe erreicht');
    }
    const addIcon = this.addBtn.querySelector('svg');
    if (addIcon) addIcon.style.display = anyShapeCanBeAdded ? '' : 'none';
    this.resetBtn.disabled = empty;
    this.shareBtn.disabled = empty;
    if (this.saveBtn) this.saveBtn.disabled = empty;

    // Sold-out banner + cart disable
    const hasSoldOut = this._hasSoldOutPieces();
    this.warning.hidden = !hasSoldOut;
    const cartDisabled = empty || hasSoldOut;
    this.cartBtn.disabled = cartDisabled;
    this.fsCart.disabled = cartDisabled;
    if (this.buyNowBtn) this.buyNowBtn.disabled = cartDisabled;

    // Price — one line: struck original (subtotal) before the discounted total.
    // The set discount kicks in from 2 pieces up.
    // Restock: der Gratis-Round (p.free) zählt NICHT zum Preis (er ist gratis)
    // und NICHT zur Rabatt-Basis — so bleibt die Summe beim Hinzufügen gleich.
    const paidPieces = this.pieces.filter(p => !p.free);
    const discountActive = paidPieces.length > 1;
    const pct = discountActive ? this.discountPercent : 0;
    const subtotal = totalPrice(paidPieces, this.variantIndex);
    const discount = pct > 0 ? Math.round(subtotal * pct / 100) : 0;
    const finalTotal = subtotal - discount;
    this.totalEl.textContent = formatMoney(finalTotal);
    if (this.wasEl) {
      const showWas = !empty && discount > 0;
      this.wasEl.hidden = !showWas;
      this.wasEl.textContent = showWas ? formatMoney(subtotal) : '';
    }
    // Restock-Hinweis/Link/Bestätigung (nur Abonnenten mit aktivem Code).
    this._renderRestock();

    // Free-shipping progress bar (based on the set's discounted total).
    if (this.freeShip && this.freeShipThreshold > 0) {
      const thr = this.freeShipThreshold;
      const reached = finalTotal >= thr;
      const pct = Math.max(0, Math.min(100, Math.round((finalTotal / thr) * 100)));
      if (this.freeShipFill) this.freeShipFill.style.width = pct + '%';
      this.freeShip.classList.toggle('is-reached', reached);
      if (this.freeShipText) {
        if (reached) {
          this.freeShipText.textContent = this.strings.freeShipReached || 'Kostenloser Versand';
        } else {
          const tpl = this.strings.freeShipRemaining || 'Noch {amount}';
          this.freeShipText.innerHTML = tpl.replace('{amount}', '<strong>' + formatMoney(thr - finalTotal) + '</strong>');
        }
      }
      this.freeShip.hidden = false;
    }

    // Success-overlay cart/checkout links carry the /discount/CODE permalink —
    // swap them for the plain destinations while the discount doesn't apply.
    if (this.successOverlay) {
      const cartA = this.successOverlay.querySelector('.playts-config__overlay-cart');
      const checkoutA = this.successOverlay.querySelector('.playts-config__overlay-checkout');
      if (cartA) {
        if (this._cartHrefDiscount == null) this._cartHrefDiscount = cartA.getAttribute('href');
        cartA.setAttribute('href', discountActive ? this._cartHrefDiscount : '/cart');
      }
      if (checkoutA) {
        if (this._checkoutHrefDiscount == null) this._checkoutHrefDiscount = checkoutA.getAttribute('href');
        checkoutA.setAttribute('href', discountActive ? this._checkoutHrefDiscount : '/checkout');
      }
    }

    // 3D scene
    this.scene.setComposition(layoutForSet(this.pieces));

    writeSetToUrl(this.pieces, this.floor);
  }

  // ── Sheet contents ─────────────────────────────────────────────────────────
  _openAddSheet() {
    // Build a flat 12-card grid of every PLAYTS variant. Each card is a
    // single-click "add this exact shape+colour". Constraint-blocked shapes
    // appear disabled; sold-out variants are addable but flagged.
    const shapeOrder = { round: 0, oval: 1, square: 2 };
    const sortedVariants = this.variants.slice().sort((a, b) =>
      (shapeOrder[a.shape] ?? 99) - (shapeOrder[b.shape] ?? 99));
    const cards = sortedVariants.map(v => {
      const c = canAdd(this.pieces, v.shape);
      const blockedByConstraint = !c.ok;
      const soldOut = !v.available;
      const disabled = blockedByConstraint; // sold-out is NOT disabled — addable
      const cls = [
        'playts-config__variant-card',
        disabled ? 'is-disabled' : '',
        soldOut ? 'is-soldout' : '',
      ].filter(Boolean).join(' ');
      const subline = disabled
        ? `<span class="playts-config__variant-reason">${this.strings.constraintBlocked}</span>`
        : soldOut
          ? `<span class="playts-config__variant-reason">${this.strings.soldOut}</span>`
          : '';
      return `
        <button type="button"
                class="${cls}"
                data-pc-add-variant="${v.shape}|${v.color}"
                ${disabled ? 'aria-disabled="true"' : ''}>
          <span class="playts-config__variant-img">
            ${v.image ? `<img src="${v.image}" alt="" loading="lazy">` : ''}
            ${soldOut ? '<span class="playts-config__variant-soldout-badge"></span>' : ''}
          </span>
          <span class="playts-config__variant-meta">
            <span class="playts-config__variant-name">
              ${this.strings.shapeNames[v.shape]} / ${this.colorNames[v.color] || v.color}
            </span>
            <span class="playts-config__variant-price">${formatMoney(v.price)}</span>
            ${subline}
          </span>
        </button>
      `;
    }).join('');

    this.sheetBody.innerHTML = `
      <h3 class="playts-config__sheet-title">${this.strings.pickVariant}</h3>
      <div class="playts-config__variant-grid">${cards}</div>
    `;

    this.sheetBody.querySelectorAll('[data-pc-add-variant]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('is-disabled')) return;
        const [shape, color] = btn.dataset.pcAddVariant.split('|');
        this._addPiece(shape, color);
      });
    });
    this.sheet.hidden = false;
  }

  _openSlotSheet(idx) {
    const p = this.pieces[idx];
    if (!p) return;
    const colors = availableColorsFor(this.variantIndex, p.shape, p.color);
    this.sheetBody.innerHTML = `
      <h3 class="playts-config__sheet-title">${this.strings.shapeNames[p.shape]}</h3>
      <p class="playts-config__sheet-sub">${this.strings.changeColor}</p>
      <div class="playts-config__color-grid">
        ${colors.filter(c => c.exists).map(c => `
          <button type="button"
                  class="playts-config__color-btn ${c.isCurrent ? 'is-current' : ''} ${c.available ? '' : 'is-soldout'}"
                  data-pc-pick-color="${c.color}"
                  aria-label="${this.colorNames[c.color] || c.color}${c.available ? '' : ' — ' + this.strings.soldOut}">
            <span class="playts-config__color-swatch" style="background:${COLOR_HEX[c.color]}"></span>
            <span class="playts-config__color-name">${this.colorNames[c.color] || c.color}</span>
            ${c.available ? '' : `<span class="playts-config__color-badge">${this.strings.soldOut}</span>`}
          </button>
        `).join('')}
      </div>
      <button type="button" class="playts-config__remove-btn" data-pc-remove>${this.strings.remove}</button>
    `;
    this.sheetBody.querySelectorAll('[data-pc-pick-color]').forEach(btn => {
      btn.addEventListener('click', () => {
        const color = btn.dataset.pcPickColor;
        this._changeColor(idx, color);
      });
    });
    this.sheetBody.querySelector('[data-pc-remove]').addEventListener('click', () => this._removePiece(idx));
    this.sheet.hidden = false;
  }

  _closeSheet() {
    this.sheet.hidden = true;
    this.sheetBody.innerHTML = '';
  }

  // ── Floor picker ─────────────────────────────────────────────────────────────
  // Swatches show the ACTUAL floor: proc floors render a thumbnail of their
  // generated texture; oak uses its real wood photo.
  _floorThumb(id) {
    try {
      const mat = this.scene._buildProcFloor(id);
      const img = mat.map.image;
      const sz = Math.min(img.width, img.height) / 2; // crop half a tile — grain visible at swatch size
      const c = document.createElement('canvas'); c.width = c.height = 96;
      c.getContext('2d').drawImage(img, 0, 0, sz, sz, 0, 0, 96, 96);
      return c.toDataURL();
    } catch (e) { return null; }
  }
  _floorSwatchStyle(id) {
    // Tablecloth's colour texture is a flat near-white (weave lives in the normal
    // map), so a photo swatch reads as a blank grey tile — use a CSS linen weave.
    if (id === 'cloth') return "background-color:#efece7;background-image:repeating-linear-gradient(90deg,rgba(120,110,95,.13) 0 1px,transparent 1px 4px),repeating-linear-gradient(0deg,rgba(120,110,95,.11) 0 1px,transparent 1px 4px)";
    if (FLOORS[id] && FLOORS[id].kind === 'tex') {
      const a = this.scene.assets[FLOORS[id].assets];
      if (a && a.color) return `background-image:url('${a.color}');background-size:300%;background-position:center`;
      return id === 'oak'
        ? 'background:linear-gradient(100deg,#d8b892,#c9a279 45%,#dcbf9c 75%,#c9a279)'
        : 'background:linear-gradient(135deg,#d3cec4,#c3bcae 55%,#cdc7bc)';
    }
    const thumb = this._floorThumb(id);
    if (thumb) return `background-image:url('${thumb}');background-size:cover`;
    if (id === 'linoleum') { const c = SCENE_PARAMS.floorLinoColors[SCENE_PARAMS.floorLinoColor] || '#c2cdd8'; return `background:${c}`; }
    return 'background:#eee';
  }
  _openFloorSheet() {
    const names = this.strings.floorNames || {};
    const linoNames = this.strings.floorLinoNames || {};
    const linoCols = SCENE_PARAMS.floorLinoColors;
    this.floorSheetBody.innerHTML = `
      <h3 class="playts-config__sheet-title">${this.strings.floorTitle || 'Hintergrund'}</h3>
      <div class="playts-config__floor-grid">
        ${['lino:grey', 'marblewhite', 'beech', 'cloth'].map(id => id.startsWith('lino:') ? `
          <button type="button" class="playts-config__floor-opt ${(this.floor === 'linoleum' && SCENE_PARAMS.floorLinoColor === id.slice(5)) || (this.floor === 'white' && id.slice(5) === 'grey') ? 'is-current' : ''}" data-pc-lino-color="${id.slice(5)}">
            <span class="playts-config__floor-swatch" style="background:${linoCols[id.slice(5)]}"></span>
            <span class="playts-config__floor-name">${linoNames[id.slice(5)] || id.slice(5)}</span>
          </button>
        ` : `
          <button type="button" class="playts-config__floor-opt ${id === this.floor ? 'is-current' : ''}" data-pc-floor-opt="${id}">
            <span class="playts-config__floor-swatch" style="${this._floorSwatchStyle(id)}"></span>
            <span class="playts-config__floor-name">${names[id] || id}</span>
          </button>
        `).join('')}
      </div>
      <div class="playts-config__floor-group-label">${names.linoleum || 'Linoleum'}</div>
      <div class="playts-config__floor-grid playts-config__floor-grid--lino">
        ${Object.keys(linoCols).filter(id => id !== 'grey').map(id => `
          <button type="button" class="playts-config__floor-opt ${this.floor === 'linoleum' && id === SCENE_PARAMS.floorLinoColor ? 'is-current' : ''}" data-pc-lino-color="${id}" title="${linoNames[id] || id}">
            <span class="playts-config__floor-swatch" style="background:${linoCols[id]}"></span>
            <span class="playts-config__floor-name">${linoNames[id] || id}</span>
          </button>
        `).join('')}
      </div>
    `;
    this.floorSheetBody.querySelectorAll('[data-pc-floor-opt]').forEach(btn => {
      btn.addEventListener('click', () => this._selectFloor(btn.dataset.pcFloorOpt));
    });
    this.floorSheetBody.querySelectorAll('[data-pc-lino-color]').forEach(btn => {
      btn.addEventListener('click', () => this._selectLinoColor(btn.dataset.pcLinoColor));
    });
    this.floorSheet.hidden = false;
  }
  // Pick a linoleum colour: activates linoleum and closes the sheet.
  _selectLinoColor(colorId) {
    if (!SCENE_PARAMS.floorLinoColors[colorId]) return;
    SCENE_PARAMS.floorLinoColor = colorId;
    this.floor = 'linoleum';
    this.scene.setFloor('linoleum');
    writeSetToUrl(this.pieces, this.floor);
    // refresh highlights: texture tiles deselect, the picked colour tile lights up
    this.floorSheetBody.querySelectorAll('[data-pc-floor-opt]').forEach(b =>
      b.classList.remove('is-current'));
    this.floorSheetBody.querySelectorAll('[data-pc-lino-color]').forEach(b =>
      b.classList.toggle('is-current', b.dataset.pcLinoColor === colorId));
    this._closeFloorSheet();
  }
  _closeFloorSheet() {
    this.floorSheet.hidden = true;
    this.floorSheetBody.innerHTML = '';
  }
  _selectFloor(id) {
    if (!FLOORS[id]) return;
    this.floor = id;
    // mark selection immediately; wood may take a moment to load
    this.floorSheetBody.querySelectorAll('[data-pc-floor-opt]').forEach(b =>
      b.classList.toggle('is-current', b.dataset.pcFloorOpt === id));
    this.floorSheetBody.querySelectorAll('[data-pc-lino-color]').forEach(b =>
      b.classList.remove('is-current'));
    const needsLoad = FLOORS[id].kind === 'tex' && !this.scene._floorMatCache[id];
    if (needsLoad) this.loading.hidden = false;
    Promise.resolve(this.scene.setFloor(id)).then(() => { this.loading.hidden = true; });
    writeSetToUrl(this.pieces, this.floor);
    this._closeFloorSheet();
  }

  // ── Mutators ───────────────────────────────────────────────────────────────
  _addPiece(shape, color) {
    // uid = stable identity for the scene's piece lifecycle (drop-in is
    // detected automatically by the uid diff in setComposition).
    // Restock: die Gratis-Round-Markierung übernimmt _syncFreeRound im render()
    // (greift auch, wenn ein hinzugefügtes Nicht-Round-Teil das Set auf ≥ min+1
    // bringt und bereits ein Round enthalten ist).
    this.pieces.push({ shape, color, uid: PC_UID++ });
    this._closeSheet();
    this.render();
  }
  _changeColor(idx, color) {
    if (!this.pieces[idx]) return;
    this.pieces[idx] = { ...this.pieces[idx], color };
    this._closeSheet();
    this.render();
  }
  _removePiece(idx) {
    this.pieces.splice(idx, 1);
    // Restock: qualifiziert das Set nicht mehr, nimmt _syncFreeRound (im render)
    // die Gratis-Markierung zurück (der Round zählt dann als bezahltes Teil).
    this._closeSheet();
    this.render();
  }
  _reset() {
    this.pieces = [];
    this.render();
  }

  _enterFullscreen() {
    // Portal the stage to <body> so it covers the true viewport (above header).
    if (!this._stagePlaceholder) {
      this._stagePlaceholder = document.createComment('pc-stage');
      this.stage.parentNode.insertBefore(this._stagePlaceholder, this.stage);
    }
    document.body.appendChild(this.stage);
    this.stage.classList.add('is-fullscreen');
    document.body.style.overflow = 'hidden';
    this.scene._needsResize = true;
    this.scene.enableOrbit();
    this._maybeShowCoach();
  }
  _exitFullscreen() {
    this._dismissCoach(false);
    this.stage.classList.remove('is-fullscreen');
    document.body.style.overflow = '';
    if (this._stagePlaceholder) {
      this._stagePlaceholder.parentNode.insertBefore(this.stage, this._stagePlaceholder);
    }
    this.scene._needsResize = true;
    this.scene.disableOrbit();
  }

  // ── Fullscreen coach mark (first visit): animated drag gesture + hint. ─────
  // Dismissed on the first interaction (or after 7 s), remembered in
  // localStorage so returning users never see it again.
  _maybeShowCoach() {
    let seen = false;
    try { seen = !!localStorage.getItem('pcCoachDone'); } catch (e) {}
    if (seen || this._coachEl) return;
    const text = this.stage.querySelector('.playts-config__fs-hint')?.textContent?.trim() || '';
    const el = document.createElement('div');
    el.className = 'playts-config__coach';
    el.innerHTML =
      '<div class="playts-config__coach-card">' +
        '<span class="playts-config__coach-gesture" aria-hidden="true">' +
          '<svg width="64" height="40" viewBox="0 0 64 40" fill="none">' +
            '<path class="playts-config__coach-arrow" d="M14 20 C 20 10, 44 10, 50 20" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
            '<path class="playts-config__coach-arrow" d="M18 12 L 14 20 L 22 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<path class="playts-config__coach-arrow" d="M46 12 L 50 20 L 42 21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
            '<circle class="playts-config__coach-dot" cx="32" cy="27" r="7" fill="currentColor" opacity="0.9"/>' +
            '<circle class="playts-config__coach-dot" cx="32" cy="27" r="11" stroke="currentColor" stroke-width="1.5" opacity="0.35"/>' +
          '</svg>' +
        '</span>' +
        (text ? `<span class="playts-config__coach-text">${text}</span>` : '') +
      '</div>';
    this.stage.appendChild(el);
    this._coachEl = el;
    this._coachAbort = new AbortController();
    const dismiss = () => this._dismissCoach(true);
    for (const ev of ['pointerdown', 'wheel', 'touchstart']) {
      this.stage.addEventListener(ev, dismiss, { capture: true, passive: true, signal: this._coachAbort.signal });
    }
    this._coachTimer = setTimeout(() => this._dismissCoach(true), 7000);
  }
  _dismissCoach(remember) {
    if (this._coachTimer) { clearTimeout(this._coachTimer); this._coachTimer = null; }
    if (this._coachAbort) { this._coachAbort.abort(); this._coachAbort = null; }
    const el = this._coachEl;
    if (!el) return;
    this._coachEl = null;
    el.classList.add('is-hiding');
    setTimeout(() => el.remove(), 350);
    if (remember) { try { localStorage.setItem('pcCoachDone', '1'); } catch (e) {} }
  }

  _openShare() {
    if (this.shareInput) this.shareInput.value = window.location.href;
    this.shareModal.hidden = false;
  }
  _closeShare() { this.shareModal.hidden = true; }
  async _copyShare(btn) {
    const url = window.location.href;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else {
        this.shareInput.select(); document.execCommand('copy');
      }
      // Icon-only button: flash a checkmark (don't overwrite with text).
      if (!btn.dataset.icon) btn.dataset.icon = btn.innerHTML;
      btn.classList.add('is-copied');
      btn.innerHTML = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12 l5 5 L20 7"/></svg>';
      setTimeout(() => { btn.innerHTML = btn.dataset.icon; btn.classList.remove('is-copied'); }, 1500);
    } catch (e) { console.error('PLAYTS configurator: copy failed', e); }
  }

  // Current permanent (set-encoded) URL, minus any form-redirect markers.
  _permalink() {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('customer_posted');
      u.searchParams.delete('contact_posted');
      return u.toString();
    } catch (_) { return window.location.href; }
  }
  _openSave() {
    if (!this.saveModal) return;
    const form = this.saveModal.querySelector('[data-pc-save-form]');
    const success = this.saveModal.querySelector('[data-pc-save-success]');
    const err = this.saveModal.querySelector('[data-pc-save-error]');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (err) { err.hidden = true; err.textContent = ''; }
    const noteEl = this.saveModal.querySelector('[data-pc-save-note]');
    if (noteEl) noteEl.value = this._permalink();
    this.saveModal.hidden = false;
    const emailEl = this.saveModal.querySelector('[data-pc-save-email]');
    setTimeout(() => { try { emailEl && emailEl.focus(); } catch (_) {} }, 120);
  }
  _closeSave() { if (this.saveModal) this.saveModal.hidden = true; }
  // Touch gestures on the gallery. The overlay has `touch-action: none`, so the
  // browser does nothing natively and we drive everything: 2-finger pinch zooms
  // the photo, 1-finger pans while zoomed, 1-finger swipe pages the gallery only
  // at zoom 1 (otherwise panning would fight the swipe), double-tap toggles zoom.
  _bindPhotoGestures() {
    const modal = this.photoModal;
    const img = modal.querySelector('[data-pc-photo-img]');
    if (!img) return;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    let sx = 0, sy = 0, swiping = false;
    let pinching = false, startDist = 0, startScale = 1;
    let panning = false, panSX = 0, panSY = 0, startTx = 0, startTy = 0;

    modal.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        pinching = true; swiping = false; panning = false;
        startDist = dist(e.touches) || 1;
        startScale = this._zoom.scale;
        img.style.transition = 'none';
      } else if (e.touches.length === 1) {
        if (this._zoom.scale > 1) {
          panning = true;
          panSX = e.touches[0].clientX; panSY = e.touches[0].clientY;
          startTx = this._zoom.tx; startTy = this._zoom.ty;
          img.style.transition = 'none';
        } else {
          sx = e.touches[0].clientX; sy = e.touches[0].clientY; swiping = true;
        }
      }
    }, { passive: true });

    modal.addEventListener('touchmove', (e) => {
      if (pinching && e.touches.length === 2) {
        e.preventDefault();
        this._zoom.scale = Math.min(4, Math.max(1, startScale * (dist(e.touches) / startDist)));
        this._applyZoom();
      } else if (panning && e.touches.length === 1) {
        e.preventDefault();
        this._zoom.tx = startTx + (e.touches[0].clientX - panSX);
        this._zoom.ty = startTy + (e.touches[0].clientY - panSY);
        this._applyZoom();
      }
    }, { passive: false });

    modal.addEventListener('touchend', (e) => {
      if (pinching && e.touches.length < 2) {
        pinching = false;
        img.style.transition = '';
        if (this._zoom.scale <= 1.05) this._resetZoom(); else this._clampZoom();
        this._guardTap();
      }
      if (panning && e.touches.length === 0) {
        panning = false; img.style.transition = ''; this._clampZoom(); this._guardTap();
      }
      if (swiping && e.touches.length === 0) {
        swiping = false;
        const t = e.changedTouches[0];
        const dx = t.clientX - sx, dy = t.clientY - sy;
        if (this._zoom.scale === 1 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          this._galleryNav(dx < 0 ? 1 : -1);
          this._guardTap(); // suppress the trailing tap-to-close
        }
      }
    }, { passive: true });

    // Double-tap the photo toggles zoom (2.5×) / resets it.
    let lastTap = 0;
    img.addEventListener('touchend', (e) => {
      if (e.touches.length) return;
      const now = Date.now();
      if (now - lastTap < 300) {
        if (this._zoom.scale > 1) this._resetZoom();
        else { this._zoom.scale = 2.5; this._applyZoom(); }
        this._guardTap();
        lastTap = 0;
      } else { lastTap = now; }
    }, { passive: true });
  }
  // Briefly swallow the click that trails a gesture, so it can't close the modal.
  _guardTap() {
    this._photoSwiped = true;
    setTimeout(() => { this._photoSwiped = false; }, 350);
  }
  _applyZoom() {
    const img = this.photoModal && this.photoModal.querySelector('[data-pc-photo-img]');
    if (img) img.style.transform = `translate(${this._zoom.tx}px, ${this._zoom.ty}px) scale(${this._zoom.scale})`;
  }
  // Keep the panned image from being dragged off-screen. Falls back to the
  // (transformed) rect ÷ scale, and if the size is unmeasurable it leaves the
  // user's pan alone rather than snapping it back to centre.
  _clampZoom() {
    const img = this.photoModal && this.photoModal.querySelector('[data-pc-photo-img]');
    if (!img) return;
    const s = this._zoom.scale || 1;
    const r = img.getBoundingClientRect();
    const w = img.offsetWidth || (r.width / s);
    const h = img.offsetHeight || (r.height / s);
    if (!w || !h) { this._applyZoom(); return; }
    const maxX = Math.max(0, (w * s - w) / 2);
    const maxY = Math.max(0, (h * s - h) / 2);
    this._zoom.tx = Math.min(maxX, Math.max(-maxX, this._zoom.tx));
    this._zoom.ty = Math.min(maxY, Math.max(-maxY, this._zoom.ty));
    this._applyZoom();
  }
  _resetZoom() {
    this._zoom = { scale: 1, tx: 0, ty: 0 };
    this._applyZoom();
  }
  // Images shown for a variant: its OWN photo + every image not assigned to any
  // variant (lifestyle/detail shots apply to all). Other variants' photos are
  // hidden. Order follows the product's image order.
  _galleryListFor(variantId) {
    return this.productImages.filter(im => {
      const ids = im.variantIds;
      if (!ids || !ids.length) return true;                     // applies to all variants
      return ids.some(id => String(id) === String(variantId));  // this variant's own photo
    });
  }
  // Full-screen product-image gallery — opened by clicking an item photo.
  _openGallery(variantId, imageId) {
    if (!this.photoModal) return;
    const list = this._galleryListFor(variantId);
    if (!list.length) return;
    this._galleryList = list;
    const i = list.findIndex(im => String(im.id) === String(imageId));
    this._galleryIdx = i < 0 ? 0 : i;
    this.photoModal.hidden = false;
    this._lockScroll(true);
    this._showGalleryImage();
  }
  // Lock the page scroll behind the gallery; pad for the (desktop) scrollbar so
  // the page doesn't shift when it disappears.
  _lockScroll(lock) {
    const b = document.body;
    if (lock) {
      const sw = window.innerWidth - document.documentElement.clientWidth;
      if (sw > 0) b.style.paddingRight = sw + 'px';
      b.classList.add('pc-photo-lock');
    } else {
      b.classList.remove('pc-photo-lock');
      b.style.paddingRight = '';
    }
  }
  _galleryNav(delta) {
    const list = this._galleryList || [];
    if (!this.photoModal || this.photoModal.hidden || !list.length) return;
    this._galleryIdx = (this._galleryIdx + delta + list.length) % list.length; // wrap around
    this._showGalleryImage();
  }
  _showGalleryImage() {
    const list = this._galleryList || [];
    const item = list[this._galleryIdx];
    if (!item) return;
    this._resetZoom(); // each image starts unzoomed
    const img = this.photoModal.querySelector('[data-pc-photo-img]');
    const spinner = this.photoModal.querySelector('[data-pc-photo-spinner]');
    const count = this.photoModal.querySelector('[data-pc-photo-count]');
    if (count) count.textContent = `${this._galleryIdx + 1} / ${list.length}`;
    if (!img) return;
    // Loading state: hide the image + show the spinner until it decodes.
    if (spinner) spinner.hidden = false;
    img.style.visibility = 'hidden';
    const done = () => { if (spinner) spinner.hidden = true; img.style.visibility = 'visible'; };
    img.onload = done;
    img.onerror = () => { if (spinner) spinner.hidden = true; };
    img.src = item.url;
    img.alt = item.alt || '';
    if (img.complete && img.naturalWidth) done(); // already cached → no flash
  }
  _closePhoto() {
    if (!this.photoModal) return;
    this.photoModal.hidden = true;
    this._lockScroll(false);
    this._resetZoom();
    const img = this.photoModal.querySelector('[data-pc-photo-img]');
    if (img) { img.onload = null; img.removeAttribute('src'); }
  }
  // Native top-level submit: Shopify's /contact bot guard blocks AJAX, so we let
  // the browser navigate. We stash the set URL + email so init() can reopen this
  // modal in its success state after Shopify redirects back (?customer_posted).
  _onSaveSubmit(e, form) {
    const emailEl = form.querySelector('[data-pc-save-email]');
    const email = (emailEl && emailEl.value || '').trim();
    if (!email || (emailEl && !emailEl.checkValidity())) {
      e.preventDefault();
      try { emailEl && emailEl.reportValidity(); } catch (_) {}
      return;
    }
    // The Showcase theme attaches a document-level submit handler that AJAX-
    // hijacks every form; stop propagation so the browser does a real navigation.
    e.stopPropagation();
    const link = this._permalink();
    const noteEl = form.querySelector('[data-pc-save-note]');
    if (noteEl) noteEl.value = link; // stored on the customer note for the Flow email
    try {
      sessionStorage.setItem('pc-save-pending', JSON.stringify({ email, url: link, ts: Date.now() }));
    } catch (_) {}
    // Native submit proceeds.
  }
  _showSaveSuccess(email, link) {
    if (!this.saveModal) return;
    const form = this.saveModal.querySelector('[data-pc-save-form]');
    const success = this.saveModal.querySelector('[data-pc-save-success]');
    const body = this.saveModal.querySelector('[data-pc-save-success-body]');
    const linkEl = this.saveModal.querySelector('[data-pc-save-link]');
    if (form) form.hidden = true;
    if (success) success.hidden = false;
    if (body) {
      const tpl = this.strings.saveSuccessBody || 'We’ve emailed the link to your set to {email}. You can also copy it here:';
      body.textContent = tpl.replace('{email}', email || '');
    }
    if (linkEl) linkEl.value = link || this._permalink();
    this.saveModal.hidden = false;
  }
  async _copySaveLink(btn) {
    const linkEl = this.saveModal.querySelector('[data-pc-save-link]');
    const url = (linkEl && linkEl.value) || this._permalink();
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
      else { linkEl.select(); document.execCommand('copy'); }
      if (!btn.dataset.icon) btn.dataset.icon = btn.innerHTML;
      btn.classList.add('is-copied');
      btn.innerHTML = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12 l5 5 L20 7"/></svg>';
      setTimeout(() => { btn.innerHTML = btn.dataset.icon; btn.classList.remove('is-copied'); }, 1500);
    } catch (e) { console.error('PLAYTS configurator: copy failed', e); }
  }

  // Restock-Aktion: Code aus /cart.js lesen (via /discount/CODE-Link aus der
  // Abonnenten-Mail an die Cart-Session geheftet). Präsenz reicht — der Code
  // steht dort auch mit applicable:false, solange ≥3 noch nicht erfüllt ist.
  async _checkRestock() {
    if (!this.restockCode) return;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const want = this.restockCode.toLowerCase();
      this.restockActive = Array.isArray(cart.discount_codes)
        && cart.discount_codes.some(d => (d.code || '').toLowerCase() === want);
    } catch (e) { this.restockActive = false; }
    this.render();
  }
  // Restock: dynamisch EINEN Round gratis setzen, wenn die Aktion aktiv ist, das
  // Set groß genug ist (min + 1 Teile) UND mindestens ein Round enthält — egal
  // wie/wann der Round dazukam (Link ODER normale Hinzufügen-Buttons, und auch
  // wenn das 4. Teil kein Round ist, aber schon ein Round im Set liegt).
  _syncFreeRound() {
    const eligible = this.restockActive
      && this.pieces.length >= this.restockMinPieces + 1
      && this.pieces.some(p => p.shape === 'round');
    const current = this.pieces.find(p => p.free);
    if (eligible && !current) {
      const round = this.pieces.find(p => p.shape === 'round');
      if (round) round.free = true;
    } else if (!eligible && current) {
      current.free = false;
    }
  }
  // Link „+ Gratis Round hinzufügen": legt einen Round ins Set; die Gratis-
  // Markierung übernimmt _syncFreeRound beim render().
  _addFreeRound() {
    this.pieces.push({ shape: 'round', color: this.defaultColor, uid: PC_UID++ });
    this.render();
  }
  // Hinweis über dem Preis (Teaser bzw. Link) + Bestätigung unter dem Preis.
  _renderRestock() {
    const hint = this.giftHintEl, confirm = this.giftConfirmEl;
    if (!hint && !confirm) return;
    const paidCount = this.pieces.filter(p => !p.free).length;
    const hasFree = this.pieces.some(p => p.free);
    if (!this.restockActive) {
      if (hint) hint.hidden = true;
      if (confirm) confirm.hidden = true;
      return;
    }
    if (hasFree) {
      if (hint) hint.hidden = true;
      if (confirm) { confirm.hidden = false; confirm.textContent = this.restockAddedText; }
    } else if (paidCount >= this.restockMinPieces) {
      if (confirm) confirm.hidden = true;
      if (hint) {
        hint.hidden = false;
        hint.classList.add('is-link');
        hint.textContent = this.restockLinkText;
        hint.setAttribute('role', 'button');
        hint.setAttribute('tabindex', '0');
      }
    } else if (paidCount >= 1) {
      // Teaser erst ab dem ersten Teil (nicht im leeren Set). Zählt herunter,
      // wie viele Teile noch bis zum Gratis-Round fehlen.
      if (confirm) confirm.hidden = true;
      if (hint) {
        const remaining = Math.max(1, this.restockMinPieces - paidCount);
        let t = this.restockTeaserTpl.replace('{n}', remaining);
        if (remaining === 1) t = t.replace('Teile', 'Teil');
        hint.hidden = false;
        hint.classList.remove('is-link');
        hint.textContent = t;
        hint.removeAttribute('role');
        hint.removeAttribute('tabindex');
      }
    } else {
      if (hint) hint.hidden = true;
      if (confirm) confirm.hidden = true;
    }
  }

  async _handleAddToCart() {
    if (!this.pieces.length) return;
    if (this._hasSoldOutPieces()) {
      this._toast(this.strings.soldOutBlocked);
      return;
    }
    this.cartBtn.disabled = true;
    this.fsCart.disabled = true;
    const orig = this.cartBtn.textContent;
    this.cartBtn.textContent = '…';
    try {
      await addSetToCart(this.pieces, this.variantIndex);
      if (this.stage.classList.contains('is-fullscreen')) this._exitFullscreen();
      this.successOverlay.hidden = false;
    } catch (e) {
      console.error('PLAYTS configurator: cart add failed', e);
      this._toast(this.strings.cartError);
    } finally {
      const dis = !this.pieces.length || this._hasSoldOutPieces();
      this.cartBtn.disabled = dis;
      this.fsCart.disabled = dis;
      if (this.buyNowBtn) this.buyNowBtn.disabled = dis;
      this.cartBtn.textContent = orig;
    }
  }

  // "Buy now": add the whole set, then land on the cart page — where Shopify's
  // native accelerated checkout buttons (Shop Pay / PayPal / Apple·Google Pay)
  // appear for the full multi-item set. The dynamic-checkout button itself can't
  // wrap a multi-variant set, so this hands off to the cart's native buttons.
  async _handleBuyNow() {
    if (!this.buyNowBtn) return;
    if (!this.pieces.length) return;
    if (this._hasSoldOutPieces()) {
      this._toast(this.strings.soldOutBlocked);
      return;
    }
    this.buyNowBtn.disabled = true;
    this.cartBtn.disabled = true;
    const orig = this.buyNowBtn.textContent;
    this.buyNowBtn.textContent = '…';
    try {
      await addSetToCart(this.pieces, this.variantIndex);
      // The discount permalink from 2 pieces up — plain cart otherwise.
      window.location = (this.pieces.length > 1 && this.root.dataset.pcCartUrl) || '/cart';
    } catch (e) {
      console.error('PLAYTS configurator: buy-now failed', e);
      this._toast(this.strings.cartError);
      const dis = !this.pieces.length || this._hasSoldOutPieces();
      this.buyNowBtn.disabled = dis;
      this.cartBtn.disabled = dis;
      this.buyNowBtn.textContent = orig;
    }
  }

  _toast(msg) {
    const t = document.createElement('div');
    t.className = 'playts-config__toast';
    t.textContent = msg;
    this.root.appendChild(t);
    requestAnimationFrame(() => t.classList.add('is-visible'));
    setTimeout(() => { t.classList.remove('is-visible'); setTimeout(() => t.remove(), 300); }, 1800);
  }
}

// Returning from a Save-form submit: Shopify redirects back with
// ?customer_posted=true and may drop the other query params. Restore the stashed
// set URL BEFORE any ConfigUI reads window.location, and remember the payload so
// init() can reopen the Save modal in its success state. Runs at module-eval,
// i.e. before boot() constructs the UI.
let PC_SAVE_RETURN = null;
try {
  const raw = sessionStorage.getItem('pc-save-pending');
  if (raw) {
    const posted = /[?&](customer|contact)_posted=true(?:&|$)/.test(location.search);
    const p = JSON.parse(raw);
    sessionStorage.removeItem('pc-save-pending');
    if (posted && p && p.ts && (Date.now() - p.ts) < 180000) {
      PC_SAVE_RETURN = p;
      // Restore the clean set URL (also strips the customer_posted marker).
      if (p.url) { try { history.replaceState(null, '', p.url); } catch (_) {} }
    }
  }
} catch (_) {}

// ── Bootstrap ─────────────────────────────────────────────────────────────────
function boot() {
  document.querySelectorAll('[data-pc-root]').forEach(root => {
    if (root.dataset.pcInit) return;
    root.dataset.pcInit = '1';
    let variants = [], strings = {}, productImages = [];
    try {
      variants = JSON.parse(root.querySelector('[data-pc-variants]').textContent);
      strings = JSON.parse(root.querySelector('[data-pc-strings]').textContent);
      const piEl = root.querySelector('[data-pc-product-images]');
      if (piEl) productImages = JSON.parse(piEl.textContent);
    } catch (e) {
      console.error('PLAYTS configurator: failed to parse embedded data', e);
      return;
    }
    const idx = buildVariantIndex(variants);
    new ConfigUI(root, variants, strings, idx, root.dataset.pcDefaultColor, productImages).init();
  });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
